import { findClosestStation, getStationList } from "./data/stations.js";
import { getStationEvents } from "./events/iem.js";
import {
    isL3Product as isL3ProductName,
    listL2Scans,
    loadL2LiveFrames,
    loadL2Polar,
    loadL3Polar,
    loadStormTracks,
} from "./decode/radarClient.js";
import { warmL2Worker, warmGlmWorker } from "./decode/workers.js";
import { decodePolarData } from "./map/polarData.js";
import {
    MOSAIC_CONUS_BOUNDS,
    MOSAIC_LIVE_LAG_MS,
    MOSAIC_SLOTS,
    MOSAIC_STEP_MS,
    createMosaicLayer,
    formatMosaicClock,
    formatMosaicDateCompact,
    formatMosaicTimeCompact,
    formatMosaicTimeParam,
    mosaicArchiveCovers,
    mosaicMsFromSlot,
    mosaicSlotIndex,
    snapMosaicMs,
} from "./map/mosaic.js";
import { formatLoopLabel, loopStartMs, loopTimes, stepLoop } from "./map/timeLoop.js";
import {
    attachVectorBasemap,
    resizeBasemap,
    setBasemapExtraLabels,
    setSatelliteBorders,
} from "./map/basemapLayer.js";
import { createLightningCanvasLayer } from "./map/lightningLayer.js";
import { buildRadarKmz, triggerDownload } from "./share/kmz.js";
import {
    appendRainToken,
    parseRainUrl,
    RAIN_TOKEN_FLAG,
    rainFromToken,
    readSatParams,
    writeRainParams,
    writeSatParams,
} from "./share/params.js";
import {
    filterFlashesForDisplay,
    glmAvailableOn,
    glmInfoLabel,
    GLM_WINDOW_SEC,
    loadGlmFlashes,
    parseUtcScanMs,
} from "./data/glm.js";
import {
    GOES_PRODUCTS,
    GOES_SATELLITES,
    GOES_SECTORS,
    goesAvailableOn,
    goesCadenceMs,
    goesFrameCovers,
    viewMaxSize,
    listGoesScans,
    loadGoesFrame,
    normalizeSatParam,
    normalizeSatChoice,
    normalizeSatSector,
} from "./data/goes.js";
import { formatGoesClock } from "./data/goesColor.js";
import { createSatelliteCanvasLayer } from "./map/satelliteLayer.js";
import {
    formatSatInfo,
    formatSatOffset,
    nearestByTime,
    SAT_PRODUCT_GROUPS,
    SAT_QUICK_PRODUCTS,
    satLegendConfig,
    satProductLabel,
    satSectorLabel,
} from "./ui/satellite.js";
import { alertLabel, alertStyle, alertTitle, geometryLabelPoint, loadAlerts } from "./data/alerts.js";
import { initRainPanel } from "./rain/rainPanel.js";

/* ═══════════════════════════════════════════════════════════════
   RadarRewind — Frontend Application
   Polar-coordinate radar rendering via HTML5 Canvas
   ═══════════════════════════════════════════════════════════════ */

    // ── Color Tables (match backend REF_COLORS / VEL_COLORS) ──
    // Index 0 = no data (always skipped during render)
    // Index 1 = below first threshold
    // Indices 2..N = color levels matching backend color arrays

    const DEFAULT_STATION_ID = "KMUX";
    const RADAR_PALETTE_BINS = 128;
    const RADAR_PALETTE_SIZE = RADAR_PALETTE_BINS + 1; // +1 for index 0 (no data)

    function rgbaString(r, g, b, a) {
        return `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${Math.max(0, Math.min(1, a)).toFixed(3)})`;
    }

    function lerp(a, b, t) {
        return a + (b - a) * t;
    }

    function buildInterpolatedPalette(anchorStops, bins) {
        const out = [null]; // index 0 = no data
        for (let i = 0; i < bins; i++) {
            const x = bins === 1 ? 0 : (i / (bins - 1));
            let left = anchorStops[0];
            let right = anchorStops[anchorStops.length - 1];
            for (let j = 0; j < anchorStops.length - 1; j++) {
                const a = anchorStops[j];
                const b = anchorStops[j + 1];
                if (x >= a.pos && x <= b.pos) {
                    left = a;
                    right = b;
                    break;
                }
            }
            const span = Math.max(1e-9, right.pos - left.pos);
            const t = Math.max(0, Math.min(1, (x - left.pos) / span));
            out.push(rgbaString(
                lerp(left.r, right.r, t),
                lerp(left.g, right.g, t),
                lerp(left.b, right.b, t),
                lerp(left.a, right.a, t)
            ));
        }
        return out;
    }

    const POLAR_REF_COLORS = buildInterpolatedPalette([
        { pos: 0.00, r: 0, g: 0, b: 0, a: 0.00 },      // below 5 dBZ
        { pos: 0.08, r: 74, g: 255, b: 255, a: 0.04 }, // 5-10
        { pos: 0.16, r: 0, g: 191, b: 255, a: 0.23 },  // 10-15
        { pos: 0.24, r: 0, g: 119, b: 255, a: 0.40 },  // 15-20
        { pos: 0.36, r: 0, g: 200, b: 75, a: 0.78 },   // 20-25
        { pos: 0.46, r: 130, g: 220, b: 0, a: 0.88 },  // 30-35
        { pos: 0.56, r: 255, g: 230, b: 0, a: 0.90 },  // 35-40
        { pos: 0.66, r: 255, g: 140, b: 0, a: 0.94 },  // 45-50
        { pos: 0.78, r: 235, g: 0, b: 0, a: 0.97 },    // 55-60
        { pos: 0.88, r: 210, g: 0, b: 160, a: 0.99 },  // 65-70
        { pos: 1.00, r: 255, g: 255, b: 255, a: 1.00 },// 75+
    ], RADAR_PALETTE_BINS);

    const POLAR_VEL_COLORS = buildInterpolatedPalette([
        { pos: 0.00, r: 0, g: 255, b: 0, a: 0.98 },      // strongest inbound
        { pos: 0.10, r: 0, g: 225, b: 0, a: 0.96 },
        { pos: 0.20, r: 0, g: 190, b: 0, a: 0.93 },
        { pos: 0.30, r: 0, g: 145, b: 0, a: 0.89 },
        { pos: 0.40, r: 32, g: 108, b: 32, a: 0.84 },
        { pos: 0.50, r: 96, g: 96, b: 96, a: 0.38 },     // near-zero
        { pos: 0.60, r: 128, g: 48, b: 48, a: 0.84 },
        { pos: 0.70, r: 168, g: 24, b: 24, a: 0.89 },
        { pos: 0.80, r: 210, g: 0, b: 0, a: 0.93 },
        { pos: 0.90, r: 240, g: 0, b: 0, a: 0.96 },
        { pos: 1.00, r: 255, g: 100, b: 100, a: 1.00 },  // strongest outbound
    ], RADAR_PALETTE_BINS);

    const POLAR_SW_COLORS = buildInterpolatedPalette([
        { pos: 0.00, r: 10, g: 20, b: 45, a: 0.10 },
        { pos: 0.20, r: 0, g: 90, b: 180, a: 0.60 },
        { pos: 0.45, r: 0, g: 180, b: 220, a: 0.80 },
        { pos: 0.65, r: 245, g: 220, b: 80, a: 0.92 },
        { pos: 0.85, r: 255, g: 140, b: 20, a: 0.96 },
        { pos: 1.00, r: 220, g: 30, b: 30, a: 1.00 },
    ], RADAR_PALETTE_BINS);

    const POLAR_CC_COLORS = buildInterpolatedPalette([
        { pos: 0.00, r: 255, g: 40, b: 130, a: 0.95 },
        { pos: 0.25, r: 190, g: 70, b: 220, a: 0.94 },
        { pos: 0.50, r: 110, g: 120, b: 245, a: 0.90 },
        { pos: 0.70, r: 60, g: 180, b: 180, a: 0.82 },
        { pos: 0.85, r: 90, g: 210, b: 90, a: 0.72 },
        { pos: 1.00, r: 170, g: 255, b: 170, a: 0.55 },
    ], RADAR_PALETTE_BINS);

    const POLAR_ZDR_COLORS = buildInterpolatedPalette([
        { pos: 0.00, r: 25, g: 85, b: 220, a: 0.95 },
        { pos: 0.20, r: 60, g: 145, b: 255, a: 0.92 },
        { pos: 0.45, r: 210, g: 230, b: 255, a: 0.82 },
        { pos: 0.50, r: 240, g: 240, b: 240, a: 0.70 },
        { pos: 0.70, r: 255, g: 185, b: 120, a: 0.86 },
        { pos: 0.85, r: 255, g: 120, b: 65, a: 0.93 },
        { pos: 1.00, r: 220, g: 25, b: 25, a: 1.00 },
    ], RADAR_PALETTE_BINS);

    // ── Level 3 palettes ─────────────────────────────────────────

    const POLAR_ET_COLORS = buildInterpolatedPalette([
        { pos: 0.00, r: 20,  g: 60,  b: 180, a: 0.60 },
        { pos: 0.20, r: 0,   g: 160, b: 220, a: 0.78 },
        { pos: 0.40, r: 0,   g: 210, b: 90,  a: 0.86 },
        { pos: 0.60, r: 240, g: 220, b: 0,   a: 0.92 },
        { pos: 0.80, r: 255, g: 120, b: 0,   a: 0.96 },
        { pos: 1.00, r: 220, g: 0,   b: 30,  a: 1.00 },
    ], RADAR_PALETTE_BINS);

    const POLAR_VIL_COLORS = buildInterpolatedPalette([
        { pos: 0.00, r: 20,  g: 50,  b: 150, a: 0.50 },
        { pos: 0.15, r: 0,   g: 130, b: 220, a: 0.72 },
        { pos: 0.30, r: 0,   g: 200, b: 100, a: 0.82 },
        { pos: 0.50, r: 200, g: 220, b: 0,   a: 0.90 },
        { pos: 0.70, r: 255, g: 140, b: 0,   a: 0.95 },
        { pos: 0.85, r: 230, g: 0,   b: 0,   a: 0.98 },
        { pos: 1.00, r: 200, g: 0,   b: 180, a: 1.00 },
    ], RADAR_PALETTE_BINS);

    const POLAR_KDP_COLORS = buildInterpolatedPalette([
        { pos: 0.00, r: 100, g: 0,   b: 180, a: 0.90 },
        { pos: 0.15, r: 40,  g: 80,  b: 220, a: 0.88 },
        { pos: 0.35, r: 180, g: 210, b: 255, a: 0.70 },
        { pos: 0.50, r: 230, g: 230, b: 230, a: 0.50 },
        { pos: 0.65, r: 255, g: 220, b: 140, a: 0.78 },
        { pos: 0.80, r: 255, g: 140, b: 40,  a: 0.92 },
        { pos: 1.00, r: 220, g: 20,  b: 20,  a: 1.00 },
    ], RADAR_PALETTE_BINS);

    const POLAR_PRECIP_COLORS = buildInterpolatedPalette([
        { pos: 0.00, r: 0,   g: 140, b: 60,  a: 0.55 },
        { pos: 0.15, r: 0,   g: 200, b: 80,  a: 0.72 },
        { pos: 0.30, r: 160, g: 220, b: 0,   a: 0.82 },
        { pos: 0.50, r: 255, g: 220, b: 0,   a: 0.90 },
        { pos: 0.65, r: 255, g: 140, b: 0,   a: 0.94 },
        { pos: 0.80, r: 220, g: 30,  b: 30,  a: 0.97 },
        { pos: 1.00, r: 190, g: 0,   b: 180, a: 1.00 },
    ], RADAR_PALETTE_BINS);

    const POLAR_HCA_COLORS = (function () {
        var palette = [null];
        var classColors = [
            { r: 128, g: 128, b: 128, a: 0.55 },
            { r: 128, g: 0,   b: 192, a: 0.75 },
            { r: 180, g: 220, b: 255, a: 0.78 },
            { r: 0,   g: 180, b: 255, a: 0.82 },
            { r: 0,   g: 80,  b: 220, a: 0.88 },
            { r: 0,   g: 200, b: 75,  a: 0.85 },
            { r: 255, g: 230, b: 0,   a: 0.92 },
            { r: 255, g: 140, b: 0,   a: 0.95 },
            { r: 235, g: 0,   b: 0,   a: 0.97 },
            { r: 255, g: 0,   b: 200, a: 0.98 },
            { r: 180, g: 180, b: 180, a: 0.45 },
        ];
        for (var i = 0; i < classColors.length; i++) {
            var c = classColors[i];
            palette.push(rgbaString(c.r, c.g, c.b, c.a));
        }
        while (palette.length <= RADAR_PALETTE_BINS) {
            palette.push(rgbaString(0, 0, 0, 0));
        }
        return palette;
    })();

    const SUPPORTED_PRODUCTS = [
        "reflectivity",
        "velocity",
        "spectrum_width",
        "correlation_coefficient",
        "differential_reflectivity",
        "l3_reflectivity",
        "l3_velocity",
        "l3_echo_tops",
        "l3_vil",
        "l3_hydrometeor",
        "l3_hybrid_hca",
        "l3_zdr",
        "l3_cc",
        "l3_kdp",
    ];

    const SHORT_PARAM_TO_PRODUCT = {
        r: "reflectivity",
        v: "velocity",
        sw: "spectrum_width",
        cc: "correlation_coefficient",
        zdr: "differential_reflectivity",
        l3r: "l3_reflectivity",
        l3v: "l3_velocity",
        et: "l3_echo_tops",
        vil: "l3_vil",
        hca: "l3_hydrometeor",
        hhc: "l3_hybrid_hca",
        l3zdr: "l3_zdr",
        l3cc: "l3_cc",
        kdp: "l3_kdp",
    };

    const PRODUCT_TO_SHORT_PARAM = {
        reflectivity: "r",
        velocity: "v",
        spectrum_width: "sw",
        correlation_coefficient: "cc",
        differential_reflectivity: "zdr",
        l3_reflectivity: "l3r",
        l3_velocity: "l3v",
        l3_echo_tops: "et",
        l3_vil: "vil",
        l3_hydrometeor: "hca",
        l3_hybrid_hca: "hhc",
        l3_zdr: "l3zdr",
        l3_cc: "l3cc",
        l3_kdp: "kdp",
    };

    const PRODUCT_TO_SHORT_TOKEN_CODE = {
        reflectivity: 0,
        velocity: 1,
        spectrum_width: 2,
        correlation_coefficient: 3,
        differential_reflectivity: 4,
        l3_reflectivity: 5,
        l3_velocity: 6,
        l3_echo_tops: 7,
        l3_vil: 8,
        l3_hydrometeor: 9,
        l3_hybrid_hca: 10,
        l3_zdr: 11,
        l3_cc: 12,
        l3_kdp: 13,
    };

    const SHORT_TOKEN_CODE_TO_PRODUCT = {
        0: "reflectivity",
        1: "velocity",
        2: "spectrum_width",
        3: "correlation_coefficient",
        4: "differential_reflectivity",
        5: "l3_reflectivity",
        6: "l3_velocity",
        7: "l3_echo_tops",
        8: "l3_vil",
        9: "l3_hydrometeor",
        10: "l3_hybrid_hca",
        11: "l3_zdr",
        12: "l3_cc",
        13: "l3_kdp",
    };

    function isL3Product(product) {
        return isL3ProductName(product);
    }

    function getPaletteForProduct(product) {
        if (product === "velocity" || product === "l3_velocity") return POLAR_VEL_COLORS;
        if (product === "spectrum_width") return POLAR_SW_COLORS;
        if (product === "correlation_coefficient" || product === "l3_cc") return POLAR_CC_COLORS;
        if (product === "differential_reflectivity" || product === "l3_zdr") return POLAR_ZDR_COLORS;
        if (product === "l3_echo_tops") return POLAR_ET_COLORS;
        if (product === "l3_vil") return POLAR_VIL_COLORS;
        if (product === "l3_kdp") return POLAR_KDP_COLORS;
        if (product === "l3_hydrometeor" || product === "l3_hybrid_hca") return POLAR_HCA_COLORS;
        if (product === "l3_reflectivity") return POLAR_REF_COLORS;
        return POLAR_REF_COLORS;
    }

    // ══════════════════════════════════════════════════════════════
    // ── Polar Rendering Engine (WebGL) ────────────────────────────
    // ══════════════════════════════════════════════════════════════

    function parseColorToRgba(color) {
        if (!color) return [0, 0, 0, 0];
        var m = color.match(/rgba?\(([^)]+)\)/);
        if (!m) return [0, 0, 0, 0];
        var parts = m[1].split(",").map((p) => p.trim());
        var r = Math.max(0, Math.min(255, parseFloat(parts[0]) || 0));
        var g = Math.max(0, Math.min(255, parseFloat(parts[1]) || 0));
        var b = Math.max(0, Math.min(255, parseFloat(parts[2]) || 0));
        var a = parts.length > 3 ? Math.max(0, Math.min(1, parseFloat(parts[3]) || 0)) : 1;
        return [r, g, b, a * 255];
    }

    function createShader(gl, type, source) {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            const err = gl.getShaderInfoLog(shader);
            gl.deleteShader(shader);
            throw new Error(err || "Shader compile failed");
        }
        return shader;
    }

    function createProgram(gl, vsSource, fsSource) {
        const vs = createShader(gl, gl.VERTEX_SHADER, vsSource);
        const fs = createShader(gl, gl.FRAGMENT_SHADER, fsSource);
        const program = gl.createProgram();
        gl.attachShader(program, vs);
        gl.attachShader(program, fs);
        gl.linkProgram(program);
        gl.deleteShader(vs);
        gl.deleteShader(fs);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            const err = gl.getProgramInfoLog(program);
            gl.deleteProgram(program);
            throw new Error(err || "Program link failed");
        }
        return program;
    }

    const RadarGLLayer = L.Layer.extend({
        initialize: function (polarData, decoded, options) {
            this._polarData = polarData;
            this._decoded = decoded;
            this._opacity = (options && options.opacity != null) ? options.opacity : 0.85;
            this._raf = null;
            this._canvas = document.createElement("canvas");
            this._canvas.className = "leaflet-zoom-animated";
            this._canvas.style.position = "absolute";
            this._canvas.style.pointerEvents = "none";
            this._canvas.style.opacity = "1";
            this._canvas.style.zIndex = "220";
            this._gl = this._canvas.getContext("webgl", {
                alpha: true,
                antialias: false,
                depth: false,
                stencil: false,
                premultipliedAlpha: false,
                preserveDrawingBuffer: false,
            });
            if (!this._gl) {
                throw new Error("WebGL not supported in this browser");
            }
            this._initGlObjects();
            this._uploadDataTextures();
        },

        _initGlObjects: function () {
            const gl = this._gl;
            const fragPrecision = (
                gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT)?.precision || 0
            ) > 0 ? "highp" : "mediump";
            const vsSource = `
                attribute vec2 a_pos;
                void main() {
                    gl_Position = vec4(a_pos, 0.0, 1.0);
                }
            `;
            const fsSource = `
                precision ${fragPrecision} float;
                uniform vec2 u_resolution;
                uniform vec2 u_centerPx;
                uniform float u_maxRangePx;
                uniform float u_maxRangeM;
                uniform float u_rangeStartM;
                uniform float u_rangeStepM;
                uniform float u_numGates;
                uniform float u_numAz;
                uniform float u_azStartDeg;
                uniform float u_layerOpacity;
                uniform sampler2D u_gateTex;
                uniform sampler2D u_paletteTex;

                void main() {
                    vec2 fragTopLeft = vec2(gl_FragCoord.x, u_resolution.y - gl_FragCoord.y);
                    vec2 d = fragTopLeft - u_centerPx;
                    float rPx = length(d);
                    if (rPx > u_maxRangePx || u_maxRangePx <= 0.0) discard;

                    float rangeM = (rPx / u_maxRangePx) * u_maxRangeM;
                    float gateF = (rangeM - u_rangeStartM) / u_rangeStepM;
                    if (gateF < 0.0 || gateF >= u_numGates) discard;
                    float gateIdx = clamp(floor(gateF), 0.0, u_numGates - 1.0);

                    float theta = degrees(atan(d.x, -d.y));
                    if (theta < 0.0) theta += 360.0;
                    float azNorm = mod(theta - u_azStartDeg + 360.0, 360.0);
                    float azIdx = clamp(floor((azNorm / 360.0) * u_numAz), 0.0, u_numAz - 1.0);

                    float u = (gateIdx + 0.5) / u_numGates;
                    float v = (azIdx + 0.5) / u_numAz;
                    float cNorm = texture2D(u_gateTex, vec2(u, v)).r;
                    float cIdx = clamp(
                        floor(cNorm * 255.0 + 0.5),
                        0.0,
                        ${(RADAR_PALETTE_SIZE - 1).toFixed(1)}
                    );
                    if (cIdx < 0.5) discard;

                    float pu = (cIdx + 0.5) / ${RADAR_PALETTE_SIZE.toFixed(1)};
                    vec4 color = texture2D(u_paletteTex, vec2(pu, 0.5));
                    color.a *= u_layerOpacity;
                    if (color.a <= 0.0) discard;
                    gl_FragColor = color;
                }
            `;

            this._program = createProgram(gl, vsSource, fsSource);
            gl.useProgram(this._program);

            const quad = new Float32Array([
                -1, -1,
                 1, -1,
                -1,  1,
                 1,  1,
            ]);
            this._quadBuffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, this._quadBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW);

            this._aPos = gl.getAttribLocation(this._program, "a_pos");
            gl.enableVertexAttribArray(this._aPos);
            gl.vertexAttribPointer(this._aPos, 2, gl.FLOAT, false, 0, 0);

            this._uResolution = gl.getUniformLocation(this._program, "u_resolution");
            this._uCenterPx = gl.getUniformLocation(this._program, "u_centerPx");
            this._uMaxRangePx = gl.getUniformLocation(this._program, "u_maxRangePx");
            this._uMaxRangeM = gl.getUniformLocation(this._program, "u_maxRangeM");
            this._uRangeStartM = gl.getUniformLocation(this._program, "u_rangeStartM");
            this._uRangeStepM = gl.getUniformLocation(this._program, "u_rangeStepM");
            this._uNumGates = gl.getUniformLocation(this._program, "u_numGates");
            this._uNumAz = gl.getUniformLocation(this._program, "u_numAz");
            this._uAzStartDeg = gl.getUniformLocation(this._program, "u_azStartDeg");
            this._uLayerOpacity = gl.getUniformLocation(this._program, "u_layerOpacity");

            this._gateTex = gl.createTexture();
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, this._gateTex);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            gl.uniform1i(gl.getUniformLocation(this._program, "u_gateTex"), 0);

            this._paletteTex = gl.createTexture();
            gl.activeTexture(gl.TEXTURE1);
            gl.bindTexture(gl.TEXTURE_2D, this._paletteTex);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            gl.uniform1i(gl.getUniformLocation(this._program, "u_paletteTex"), 1);
        },

        _uploadDataTextures: function () {
            const gl = this._gl;
            const d = this._polarData;
            const dec = this._decoded;

            gl.useProgram(this._program);
            gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, this._gateTex);
            gl.texImage2D(
                gl.TEXTURE_2D,
                0,
                gl.LUMINANCE,
                d.num_gates,
                d.num_azimuths,
                0,
                gl.LUMINANCE,
                gl.UNSIGNED_BYTE,
                dec.values
            );

            const palette = new Uint8Array(RADAR_PALETTE_SIZE * 4);
            const colors = getPaletteForProduct(d.product);
            for (let i = 0; i < RADAR_PALETTE_SIZE; i++) {
                const rgba = parseColorToRgba(colors[i] || null);
                const off = i * 4;
                palette[off] = rgba[0];
                palette[off + 1] = rgba[1];
                palette[off + 2] = rgba[2];
                palette[off + 3] = rgba[3];
            }
            gl.activeTexture(gl.TEXTURE1);
            gl.bindTexture(gl.TEXTURE_2D, this._paletteTex);
            gl.texImage2D(
                gl.TEXTURE_2D,
                0,
                gl.RGBA,
                RADAR_PALETTE_SIZE,
                1,
                0,
                gl.RGBA,
                gl.UNSIGNED_BYTE,
                palette
            );
        },

        onAdd: function (map) {
            this._map = map;
            map.getPane("overlayPane").appendChild(this._canvas);
            map.on("move zoom resize viewreset", this._scheduleRender, this);
            this._scheduleRender();
            return this;
        },

        onRemove: function (map) {
            if (this._raf) {
                cancelAnimationFrame(this._raf);
                this._raf = null;
            }
            map.off("move zoom resize viewreset", this._scheduleRender, this);
            if (this._canvas.parentNode) {
                this._canvas.parentNode.removeChild(this._canvas);
            }
            this._map = null;
            return this;
        },

        setOpacity: function (opacity) {
            this._opacity = opacity;
            this._scheduleRender();
            return this;
        },

        setData: function (polarData, decoded) {
            this._polarData = polarData;
            this._decoded = decoded;
            this._uploadDataTextures();
            this._scheduleRender();
            return this;
        },

        _scheduleRender: function () {
            if (!this._map) return;
            const topLeft = this._map.containerPointToLayerPoint([0, 0]);
            L.DomUtil.setPosition(this._canvas, topLeft);

            const mapSize = this._map.getSize();
            const dpr = window.devicePixelRatio || 1;
            const w = Math.max(1, Math.round(mapSize.x * dpr));
            const h = Math.max(1, Math.round(mapSize.y * dpr));
            if (this._canvas.width !== w || this._canvas.height !== h) {
                this._canvas.width = w;
                this._canvas.height = h;
                this._canvas.style.width = mapSize.x + "px";
                this._canvas.style.height = mapSize.y + "px";
            }

            if (this._raf) return;
            this._raf = requestAnimationFrame(() => {
                this._raf = null;
                this._render();
            });
        },

        _render: function () {
            if (!this._map) return;
            const gl = this._gl;
            const d = this._polarData;
            const dec = this._decoded;
            if (!d || !dec) return;

            const dpr = window.devicePixelRatio || 1;
            const mapSize = this._map.getSize();
            const width = Math.max(1, Math.round(mapSize.x * dpr));
            const height = Math.max(1, Math.round(mapSize.y * dpr));
            gl.viewport(0, 0, width, height);
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.useProgram(this._program);

            const center = this._map.latLngToContainerPoint([d.lat, d.lon]);
            const centerPxX = center.x * dpr;
            const centerPxY = center.y * dpr;
            const maxRangeM = d.range_start_m + d.num_gates * d.range_step_m;

            const dlat = maxRangeM / 111000;
            const edge = this._map.latLngToContainerPoint([d.lat + dlat, d.lon]);
            const maxRangePx = Math.max(1, Math.hypot(edge.x - center.x, edge.y - center.y) * dpr);

            gl.uniform2f(this._uResolution, width, height);
            gl.uniform2f(this._uCenterPx, centerPxX, centerPxY);
            gl.uniform1f(this._uMaxRangePx, maxRangePx);
            gl.uniform1f(this._uMaxRangeM, maxRangeM);
            gl.uniform1f(this._uRangeStartM, d.range_start_m);
            gl.uniform1f(this._uRangeStepM, d.range_step_m);
            gl.uniform1f(this._uNumGates, d.num_gates);
            gl.uniform1f(this._uNumAz, d.num_azimuths);
            gl.uniform1f(this._uAzStartDeg, dec.azimuthStartDeg || 0);
            gl.uniform1f(this._uLayerOpacity, this._opacity);

            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        },
    });

    const END_PAUSE_MULTIPLIER = 4;
    const ADVANCED_PANEL_STORAGE_KEY = "radarrewind.advancedPanelOpen";
    const EVENTS_PANEL_STORAGE_KEY = "radarrewind.eventsPanelOpen";
    const LIVE_DEFAULT_POLL_INTERVAL_MS = 60 * 1000;
    const LIVE_MIN_POLL_DELAY_MS = 15 * 1000;
    const LIVE_MAX_POLL_DELAY_MS = 3 * 60 * 1000;
    const LIVE_POLL_LEAD_MS = 15 * 1000;
    // Level 2 live mode also polls the real-time chunk bucket, where base and
    // SAILS sweeps land every ~2-3 minutes, well before the volume is archived.
    const LIVE_CHUNK_POLL_MS = 30 * 1000;
    const LIVE_CHUNK_WAIT_MS = 8 * 1000;
    const SCAN_DATE_FETCH_CONCURRENCY = 3;
    const PRELOAD_FETCH_CONCURRENCY = 2;
    const PRELOAD_MAX_RETRIES = 2;
    const PRELOAD_RETRY_BASE_MS = 10000;
    const MIN_FRAMES_TO_START_ANIM = 2;
    const RADAR_MAX_FRAMES = 20;
    const SAT_MAX_FRAMES = 30;

    function sliderValueToIntervalMs(value) {
        const min = parseInt(dom.speedSlider.min || "100", 10);
        const max = parseInt(dom.speedSlider.max || "600", 10);
        // Invert slider semantics so lower slider value = slower playback.
        return (min + max) - value;
    }

    function intervalMsToSliderValue(intervalMs) {
        const min = parseInt(dom.speedSlider.min || "100", 10);
        const max = parseInt(dom.speedSlider.max || "600", 10);
        return (min + max) - intervalMs;
    }

    // ── State ─────────────────────────────────────────────────
    const state = {
        stations: [],
        selectedStation: null,
        date: "",
        scans: [],
        selectedScanIndex: -1,
        product: "reflectivity",
        selectedElevationByProduct: {
            reflectivity: null,
            velocity: null,
            spectrum_width: null,
            correlation_coefficient: null,
            differential_reflectivity: null,
        },
        availableElevations: [],
        frameLoadStatusText: "",

        // Animation
        playing: false,
        animInterval: null,
        animSpeed: 200,
        animFrameIndex: 0,
        animEndPauseTicksRemaining: 0,
        radarOpacity: 0.85,
        showReferenceOverlay: false,
        liveMode: false,
        mosaicMode: false,
        mosaicTimeMs: null,
        mosaicLoopAnchorMs: null,
        mosaicLiveTimer: null,
        livePollTimer: null,
        hiddenSinceMs: null,
        liveChunkShownKey: "",  // newest real-time chunk frame on screen ("" = none)
        lastRadarMeta: null,

        // Frame preloading (polar data)
        frameCount: 12,
        frameWindow: [],       // scan indices in current animation window
        polarFrames: [],       // [{scanIdx, polarData, decoded, loaded}, ...]
        pendingPolarFrames: null, // frames loaded mid-loop; applied at wrap
        framesReady: false,
        preloading: false,
        preloadController: null,
        radarLoopProgress: null, // { loaded, total } while a radar loop preloads

        // Initial URL time (saved before updateURL can strip it)
        initialUrlTime: null,
        initialUrlMap: null,
        initialUrlAutoplay: false,
        initialRain: null,     // { startMs, endMs, point } from ?rain=&rpt= or a short token
        pendingRain: null,     // kept until shareState() returns the restored total

        // Interesting events
        eventsLoading: false,
        interestingEvents: [],
        eventsExpanded: true,
        pendingInitialStationId: null,
        initialStationSelected: false,
        geoLocateInFlight: false,

        // Request sequencing: prevents stale async responses from rendering.
        radarLoadToken: 0,
        productSwitchToken: 0,

        // Storm tracks
        showStormTracks: false,
        stormTrackCache: {},
        stormTrackFetchToken: 0,

        // GLM lightning
        showLightning: false,
        glmCache: {},
        glmInflight: {},
        glmAbort: null,
        glmActiveKey: "",
        glmFetchToken: 0,
        glmRenderedKey: "",
        lastGlmCount: null,
        glmLoading: false,
        glmToastKey: "",

        // GOES satellite imagery
        showSatellite: false,
        satProduct: "geo",
        satMode: "overlay",      // "overlay" under the radar; "only" hides the radar and plays the satellite clock
        satSector: "auto",       // viewer's sector choice: auto | m | c | f
        satGoes: "auto",         // viewer's satellite choice: auto | east | west
        satHold: false,          // Overlay: keep one satellite frame while the radar loops
        satHoldMs: null,         // time of the held frame
        satOpacity: 0.8,
        satStatus: "none",       // info-bar dot: ready | loading | none
        satFrame: null,          // frame on screen
        satBounds: null,         // viewport that national and satellite-only requests were made for
        satLoop: null,           // Overlay loop: { times, targets: [{ ms, status, frame }] }, one target per GOES file
        satClock: [],            // Satellite only: listed scans ending at the anchor, oldest first
        satClockIndex: -1,
        satClockKey: "",
        satClockStepMs: 5 * 60 * 1000,
        satClockToken: 0,
        satPreloading: false,
        satPreloadToken: 0,
        satLiveTimer: null,
        satDebounceTimer: null, // map-move reload
        satFramesTimer: null,   // satellite-only re-list after the Frames slider settles
        satFetchToken: 0,
        satAbort: null,          // AbortController for all satellite work on this station/date/view
        satCurrentAbort: null,   // AbortController for the frame on screen
        satToastKey: "",

        // Watches / warnings / MDs
        showAlerts: false,
        alertsCache: {},
        alertsFetchToken: 0,
        alertsRenderedKey: "",
        lastAlertCount: null,
        alertsToastKey: "",

        // Prevents setFrameLoadStatus from clearing loading during loadRadarData
        radarDataLoadInProgress: false,
    };

    // ── DOM References ────────────────────────────────────────
    const dom = {
        map: null,
        sidebar: document.getElementById("sidebar"),
        sidebarToggle: document.getElementById("sidebarToggle"),
        mobileSidebarBtn: document.getElementById("mobileSidebarBtn"),
        viewSite: document.getElementById("viewSite"),
        viewMosaic: document.getElementById("viewMosaic"),
        mosaicHint: document.getElementById("mosaicHint"),
        stationSearch: document.getElementById("stationSearch"),
        stationDropdown: document.getElementById("stationDropdown"),
        stationInfo: document.getElementById("stationInfo"),
        timeGroup: document.getElementById("timeGroup"),
        datePicker: document.getElementById("datePicker"),
        dateLiveHint: document.getElementById("dateLiveHint"),
        timeControls: document.getElementById("timeControls"),
        timeSelect: document.getElementById("timeSelect"),
        elevationSelect: document.getElementById("elevationSelect"),
        btnLive: document.getElementById("btnLive"),
        scanCount: document.getElementById("scanCount"),
        btnPrev: document.getElementById("btnPrev"),
        btnPlay: document.getElementById("btnPlay"),
        btnNext: document.getElementById("btnNext"),
        frameCycle: document.getElementById("frameCycle"),
        frameProgress: document.getElementById("frameProgress"),
        frameProgressLabel: document.getElementById("frameProgressLabel"),
        eventsToggle: document.getElementById("eventsToggle"),
        eventsContainer: document.getElementById("eventsContainer"),
        advancedToggle: document.getElementById("advancedToggle"),
        advancedPanel: document.getElementById("advancedPanel"),
        overlayToggle: document.getElementById("overlayToggle"),
        speedSlider: document.getElementById("speedSlider"),
        opacitySlider: document.getElementById("opacitySlider"),
        opacityValue: document.getElementById("opacityValue"),
        framesSlider: document.getElementById("framesSlider"),
        framesValue: document.getElementById("framesValue"),
        btnShare: document.getElementById("btnShare"),
        btnShareShort: document.getElementById("btnShareShort"),
        btnExportKmz: document.getElementById("btnExportKmz"),
        btnAbout: document.getElementById("btnAbout"),
        shareToast: document.getElementById("shareToast"),
        aboutModal: document.getElementById("aboutModal"),
        aboutBackdrop: document.getElementById("aboutBackdrop"),
        aboutClose: document.getElementById("aboutClose"),
        eventsStatus: document.getElementById("eventsStatus"),
        eventsList: document.getElementById("eventsList"),
        mapInfoBar: document.getElementById("mapInfoBar"),
        colorScaleOverlay: document.getElementById("colorScaleOverlay"),
        colorScaleBar: document.getElementById("colorScaleBar"),
        colorScaleTicks: document.getElementById("colorScaleTicks"),
        stormTracksToggle: document.getElementById("stormTracksToggle"),
        lightningToggle: document.getElementById("lightningToggle"),
        satelliteToggle: document.getElementById("satelliteToggle"),
        satelliteHint: document.getElementById("satelliteHint"),
        satelliteBody: document.getElementById("satelliteBody"),
        satModeToggle: document.getElementById("satModeToggle"),
        satelliteChips: document.getElementById("satelliteChips"),
        satelliteProduct: document.getElementById("satelliteProduct"),
        satGoesToggle: document.getElementById("satGoesToggle"),
        satSectorToggle: document.getElementById("satSectorToggle"),
        satSectorReadout: document.getElementById("satSectorReadout"),
        satHoldRow: document.getElementById("satHoldRow"),
        satHoldToggle: document.getElementById("satHoldToggle"),
        satelliteOpacity: document.getElementById("satelliteOpacity"),
        satelliteOpacityValue: document.getElementById("satelliteOpacityValue"),
        satelliteOpacityRow: document.getElementById("satelliteOpacityRow"),
        alertsToggle: document.getElementById("alertsToggle"),
    };

    // Compatibility guard: if an older cached HTML shell is served without
    // the elevation select element, create a hidden fallback so app init
    // does not crash before stations/data can load.
    if (!dom.elevationSelect) {
        const fallbackElevationSelect = document.createElement("select");
        fallbackElevationSelect.id = "elevationSelect-fallback";
        fallbackElevationSelect.hidden = true;
        fallbackElevationSelect.disabled = true;
        if (dom.timeControls) {
            dom.timeControls.appendChild(fallbackElevationSelect);
        } else {
            document.body.appendChild(fallbackElevationSelect);
        }
        dom.elevationSelect = fallbackElevationSelect;
        console.warn("Missing #elevationSelect in DOM; using hidden fallback.");
    }

    // ── Map Layers ────────────────────────────────────────────
    let stationMarkers = L.layerGroup();
    let radarOverlay = null;       // RadarGLLayer
    let mosaicLayer = null;        // IEM national composite WMS
    let rainPanel = null;          // rain-total mode; hides the radar overlay while active
    let rangeRings = null;
    let stationDot = null;
    let stormTrackLayer = null;   // L.layerGroup for storm tracks
    let lightningLayer = null;    // LightningCanvasLayer for GLM flashes
    let satelliteLayer = null;    // GOES imagery under the radar
    let alertsLayer = null;       // L.layerGroup for watches/warnings/MDs
    let dockSyncRaf = null;
    let infoBarResizeObserver = null;

    // ── Reflectivity color legend data ────────────────────────
    const REF_LEGEND = [
        { label: "75+ dBZ", color: "rgb(255,255,255)" },
        { label: "70–75",   color: "rgb(165,95,235)" },
        { label: "65–70",   color: "rgb(210,0,160)" },
        { label: "60–65",   color: "rgb(200,0,90)" },
        { label: "55–60",   color: "rgb(235,0,0)" },
        { label: "50–55",   color: "rgb(255,80,0)" },
        { label: "45–50",   color: "rgb(255,140,0)" },
        { label: "40–45",   color: "rgb(255,190,0)" },
        { label: "35–40",   color: "rgb(255,230,0)" },
        { label: "30–35",   color: "rgb(130,220,0)" },
        { label: "25–30",   color: "rgb(0,170,45)" },
        { label: "20–25",   color: "rgb(0,200,75)" },
        { label: "15–20",   color: "rgba(0,120,255,0.50)" },
        { label: "10–15",   color: "rgba(0,190,255,0.35)" },
        { label: "5–10",    color: "rgba(74,255,255,0.20)" },
    ];

    // Legend: subset of bins (scale +/-130 kt); labels in kt.
    const VEL_LEGEND = [
        { label: "> +130 kt", color: "rgb(255,100,100)" },
        { label: "+90–110",  color: "rgb(240,0,0)" },
        { label: "+50–70",   color: "rgb(205,0,0)" },
        { label: "+20–30",   color: "rgb(150,36,36)" },
        { label: "±5",       color: "rgb(80,80,80)" },
        { label: "-20–-30",  color: "rgb(20,132,20)" },
        { label: "-50–-70",  color: "rgb(0,182,0)" },
        { label: "-90–-110", color: "rgb(0,226,0)" },
        { label: "< -130 kt", color: "rgb(0,255,0)" },
    ];

    const SW_LEGEND = [
        { label: "12-15 m/s", color: "rgb(220,30,30)" },
        { label: "9-12", color: "rgb(255,140,20)" },
        { label: "6-9", color: "rgb(245,220,80)" },
        { label: "3-6", color: "rgb(0,180,220)" },
        { label: "0-3", color: "rgb(0,90,180)" },
    ];

    const CC_LEGEND = [
        { label: "0.98-1.00", color: "rgb(170,255,170)" },
        { label: "0.95-0.98", color: "rgb(90,210,90)" },
        { label: "0.90-0.95", color: "rgb(60,180,180)" },
        { label: "0.85-0.90", color: "rgb(110,120,245)" },
        { label: "0.70-0.85", color: "rgb(255,40,130)" },
    ];

    const ZDR_LEGEND = [
        { label: "+4 to +6 dB", color: "rgb(220,25,25)" },
        { label: "+2 to +4", color: "rgb(255,120,65)" },
        { label: "0 to +2", color: "rgb(255,185,120)" },
        { label: "-2 to 0", color: "rgb(60,145,255)" },
    ];

    const ET_LEGEND = [
        { label: "60-70 kft", color: "rgb(220,0,30)" },
        { label: "45-55", color: "rgb(255,120,0)" },
        { label: "30-40", color: "rgb(240,220,0)" },
        { label: "15-25", color: "rgb(0,210,90)" },
        { label: "5-15", color: "rgb(0,160,220)" },
    ];

    const VIL_LEGEND = [
        { label: "> 60 kg/m\u00b2", color: "rgb(200,0,180)" },
        { label: "40-60", color: "rgb(230,0,0)" },
        { label: "25-40", color: "rgb(255,140,0)" },
        { label: "10-25", color: "rgb(200,220,0)" },
        { label: "0-10", color: "rgb(0,130,220)" },
    ];

    const KDP_LEGEND = [
        { label: "+6 to +10 \u00b0/km", color: "rgb(220,20,20)" },
        { label: "+2 to +6", color: "rgb(255,140,40)" },
        { label: "0 to +2", color: "rgb(255,220,140)" },
        { label: "-2 to 0", color: "rgb(40,80,220)" },
    ];

    const HCA_LEGEND = [
        { label: "Hail", color: "rgb(255,0,200)" },
        { label: "Graupel", color: "rgb(235,0,0)" },
        { label: "Big Drops", color: "rgb(255,140,0)" },
        { label: "Heavy Rain", color: "rgb(255,230,0)" },
        { label: "Rain", color: "rgb(0,200,75)" },
        { label: "Wet Snow", color: "rgb(0,80,220)" },
        { label: "Dry Snow", color: "rgb(0,180,255)" },
        { label: "Ice Crystals", color: "rgb(180,220,255)" },
        { label: "AP/Clutter", color: "rgb(128,0,192)" },
        { label: "Biological", color: "rgb(128,128,128)" },
    ];

    const PRECIP_LEGEND = [
        { label: "> 4 in", color: "rgb(190,0,180)" },
        { label: "2-4", color: "rgb(220,30,30)" },
        { label: "1-2", color: "rgb(255,140,0)" },
        { label: "0.5-1", color: "rgb(255,220,0)" },
        { label: "0.1-0.5", color: "rgb(0,200,80)" },
        { label: "< 0.1 in", color: "rgb(0,140,60)" },
    ];

    const STORM_TOTAL_LEGEND = [
        { label: "> 10 in", color: "rgb(190,0,180)" },
        { label: "5-10", color: "rgb(220,30,30)" },
        { label: "2-5", color: "rgb(255,140,0)" },
        { label: "1-2", color: "rgb(255,220,0)" },
        { label: "0.5-1", color: "rgb(0,200,80)" },
        { label: "< 0.5 in", color: "rgb(0,140,60)" },
    ];

    const PRODUCT_LEGEND_CONFIG = {
        reflectivity: {
            label: "Reflectivity",
            items: REF_LEGEND,
            ticks: ["75", "55", "35", "15", "5 dBZ"],
        },
        velocity: {
            label: "Velocity",
            items: VEL_LEGEND,
            ticks: ["+130", "+65", "0", "−65", "−130 kt"],
        },
        spectrum_width: {
            label: "Spectrum Width",
            items: SW_LEGEND,
            ticks: ["15", "10", "5", "0 m/s"],
        },
        correlation_coefficient: {
            label: "Correlation",
            items: CC_LEGEND,
            ticks: ["1.00", "0.90", "0.80", "0.70"],
        },
        differential_reflectivity: {
            label: "Differential Refl.",
            items: ZDR_LEGEND,
            ticks: ["+6", "+2", "\u22122 dB"],
        },
        l3_reflectivity: {
            label: "L3 Reflectivity",
            items: REF_LEGEND,
            ticks: ["75", "55", "35", "15", "\u221220 dBZ"],
        },
        l3_velocity: {
            label: "L3 Velocity",
            items: VEL_LEGEND,
            ticks: ["+64", "+32", "0", "\u221232", "\u221264 kts"],
        },
        l3_echo_tops: {
            label: "Echo Tops",
            items: ET_LEGEND,
            ticks: ["70", "50", "30", "10", "5 kft"],
        },
        l3_vil: {
            label: "VIL",
            items: VIL_LEGEND,
            ticks: ["80", "50", "25", "0 kg/m\u00b2"],
        },
        l3_hydrometeor: {
            label: "Precip Type",
            items: HCA_LEGEND,
            ticks: [],
        },
        l3_hybrid_hca: {
            label: "Hybrid HCA",
            items: HCA_LEGEND,
            ticks: [],
        },
        l3_zdr: {
            label: "L3 Diff. Refl.",
            items: ZDR_LEGEND,
            ticks: ["+6", "+2", "\u22122 dB"],
        },
        l3_cc: {
            label: "L3 Corr. Coef.",
            items: CC_LEGEND,
            ticks: ["1.05", "0.90", "0.70", "0.20"],
        },
        l3_kdp: {
            label: "KDP",
            items: KDP_LEGEND,
            ticks: ["+10", "+4", "0", "\u22122 \u00b0/km"],
        },
    };

    // Satellite-only mode shows a brightness-temperature scale for the IR and water vapour bands.
    for (const item of GOES_PRODUCTS) {
        const legend = satLegendConfig(item.id);
        if (legend) PRODUCT_LEGEND_CONFIG[`sat_${item.id}`] = legend;
    }

    // ── Initialize ────────────────────────────────────────────

    function init() {
        warmL2Worker();
        warmGlmWorker();
        initMap();
        initSidebar();
        initControls();
        initLegend();
        initRainTotal();
        loadFromURL();
        loadStations();
        initKeyboard();
        window.addEventListener("resize", () => {
            syncMapDockOffsets();
        });
        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", syncMapDockOffsets);
            window.visualViewport.addEventListener("scroll", syncMapDockOffsets);
        }
        if ("ResizeObserver" in window && dom.mapInfoBar) {
            infoBarResizeObserver = new ResizeObserver(() => syncMapDockOffsets());
            infoBarResizeObserver.observe(dom.mapInfoBar);
        }
    }

    function initMap() {
        dom.map = L.map("map", {
            center: [39.0, -98.0],
            zoom: 5,
            minZoom: 1,
            maxZoom: 19,
            zoomSnap: 0.05,
            zoomDelta: 1.0,
            wheelPxPerZoomLevel: 100,
            wheelDebounceTime: 10,
            zoomAnimation: false,
            fadeAnimation: false,
            markerZoomAnimation: false,
            zoomControl: false,
            attributionControl: true,
        });

        // MapLibre GL vector basemap as a Leaflet layer (tile pane, under RadarGLLayer).
        attachVectorBasemap(dom.map, {
            extraLabels: state.showReferenceOverlay,
            L,
        }).catch((err) => {
            console.warn("MapLibre basemap failed; radar overlay still works.", err);
        });

        initReferenceOverlay();
        stationMarkers.addTo(dom.map);

        // Keep URL map state in sync for shareable view position.
        dom.map.on("moveend zoomend", () => {
            updateURL();
        });
        dom.map.on("moveend", onSatelliteMapMove);
    }

    function initReferenceOverlay() {
        setReferenceOverlayVisible(state.showReferenceOverlay);
    }

    function setReferenceOverlayVisible(show) {
        state.showReferenceOverlay = !!show;
        if (dom.overlayToggle) {
            dom.overlayToggle.checked = state.showReferenceOverlay;
        }
        setBasemapExtraLabels(state.showReferenceOverlay);
    }

    // ── Storm Tracks ─────────────────────────────────────────

    function setStormTracksVisible(show) {
        state.showStormTracks = !!show;
        if (dom.stormTracksToggle) {
            dom.stormTracksToggle.checked = state.showStormTracks;
        }
        if (state.showStormTracks) {
            loadStormTracksForCurrentFrame();
        } else {
            clearStormTracks();
        }
    }

    function setLightningVisible(show) {
        state.showLightning = !!show;
        if (dom.lightningToggle) {
            dom.lightningToggle.checked = state.showLightning;
        }
        if (state.showLightning) {
            loadLightningForCurrentFrame();
        } else {
            abortLightningFetch();
            state.glmLoading = false;
            clearLightning();
            state.lastGlmCount = null;
            state.glmRenderedKey = "";
            if (state.lastRadarMeta) updateInfoBar(state.lastRadarMeta);
        }
    }

    function setAlertsVisible(show) {
        state.showAlerts = !!show;
        if (dom.alertsToggle) {
            dom.alertsToggle.checked = state.showAlerts;
        }
        if (state.showAlerts) {
            loadAlertsForCurrentFrame();
        } else {
            clearAlerts();
            state.lastAlertCount = null;
            state.alertsRenderedKey = "";
        }
    }

    const LightningCanvasLayer = createLightningCanvasLayer(L);
    const SatelliteCanvasLayer = createSatelliteCanvasLayer(L);

    function clearLightning() {
        if (lightningLayer) {
            lightningLayer.clearFlashes();
            if (dom.map && dom.map.hasLayer(lightningLayer)) {
                dom.map.removeLayer(lightningLayer);
            }
        }
    }

    function abortLightningFetch() {
        for (const entry of Object.values(state.glmInflight)) {
            entry.abort?.abort();
        }
        if (state.glmAbort) {
            state.glmAbort.abort();
            state.glmAbort = null;
        }
        state.glmActiveKey = "";
        state.glmFetchToken += 1;
    }

    function _glmCacheKey(stationId, date, time) {
        return `${stationId || ""}_${(date || "").replace(/-/g, "")}_${(time || "").replace(/:/g, "")}`;
    }

    function glmMetaFromScan(scan) {
        if (!scan || !state.selectedStation) return null;
        const polar = state.lastRadarMeta;
        return {
            lat: polar?.lat || state.selectedStation.lat,
            lon: polar?.lon || state.selectedStation.lon,
            max_range_km: polar?.max_range_km || 300,
            date: scan.scanDate || state.date,
            time: scan.time,
        };
    }

    function currentRadarMeta() {
        let meta = null;
        const total = getTotalFrameCount();
        if (state.framesReady && total > 0) {
            const frame = getFrameAt(state.animFrameIndex);
            if (frame && frame.polarData) meta = frame.polarData;
        }
        return meta || state.lastRadarMeta;
    }

    function prefetchLightning(scan) {
        if (!state.showLightning || !state.selectedStation || !scan) return;
        const meta = glmMetaFromScan(scan);
        if (!meta?.date || !meta?.time) return;
        if (!glmAvailableOn(meta.date)) return;
        fetchLightning(meta).catch(() => {});
    }

    function prefetchNeighborLightning() {
        if (!state.showLightning) return;
        if (state.framesReady) {
            const next = getFrameAt(state.animFrameIndex + 1);
            if (next && state.scans[next.scanIdx]) {
                prefetchLightning(state.scans[next.scanIdx]);
                return;
            }
        }
        const nextScan = state.scans[state.selectedScanIndex + 1];
        if (nextScan) prefetchLightning(nextScan);
    }

    async function fetchLightning(meta, { onPartial } = {}) {
        if (!meta || !state.selectedStation) return null;
        const cacheKey = _glmCacheKey(state.selectedStation.id, meta.date, meta.time);
        if (state.glmCache[cacheKey]) return state.glmCache[cacheKey];
        if (state.glmInflight[cacheKey]) {
            const entry = state.glmInflight[cacheKey];
            if (onPartial) entry.partials.add(onPartial);
            return entry.promise;
        }
        const ctrl = new AbortController();
        const partials = new Set(onPartial ? [onPartial] : []);
        const promise = (async () => {
            try {
                const data = await loadGlmFlashes({
                    lat: meta.lat,
                    lon: meta.lon,
                    maxRangeKm: meta.max_range_km,
                    date: meta.date,
                    time: meta.time,
                    signal: ctrl.signal,
                    onPartial: (flashes) => {
                        for (const fn of partials) fn(flashes);
                    },
                });
                if (ctrl.signal.aborted) throw new DOMException("Aborted", "AbortError");
                state.glmCache[cacheKey] = data;
                return data;
            } catch (err) {
                if (err?.name === "AbortError") throw err;
                return { flashes: [], reason: "error" };
            } finally {
                delete state.glmInflight[cacheKey];
            }
        })();
        state.glmInflight[cacheKey] = { promise, partials, abort: ctrl };
        return promise;
    }

    function lightningHideFuture() {
        return !!(state.playing || state.framesReady);
    }

    function lightningRenderKey(cacheKey, frameMs, hideFuture) {
        return `${cacheKey}|${Number.isFinite(frameMs) ? frameMs : ""}|${hideFuture ? 1 : 0}`;
    }

    function ensureLightningLayer() {
        if (!lightningLayer) lightningLayer = new LightningCanvasLayer();
        return lightningLayer;
    }

    function renderLightning(data, { renderKey, frameMs, hideFuture } = {}) {
        if (!dom.map) return;
        const layer = ensureLightningLayer();
        const flashes = data?.flashes || [];
        const visible = filterFlashesForDisplay(flashes, frameMs, {
            hideFuture: !!hideFuture,
            windowSec: GLM_WINDOW_SEC,
        });
        state.lastGlmCount = visible.length;
        state.glmRenderedKey = renderKey || "";

        if (!visible.length) {
            layer.clearFlashes();
            if (dom.map.hasLayer(layer)) dom.map.removeLayer(layer);
            return;
        }

        layer.setFlashes(flashes, { frameMs, hideFuture: !!hideFuture });
        if (!dom.map.hasLayer(layer)) layer.addTo(dom.map);
    }

    function maybeToastGlm(data, date) {
        const key = `${data?.reason || ""}:${String(date || "").replace(/-/g, "")}`;
        if (key === state.glmToastKey) return;
        if (data?.reason === "unavailable") {
            state.glmToastKey = key;
            showShareToast("GLM lightning starts in 2018");
        } else if (data?.reason === "error") {
            state.glmToastKey = key;
            showShareToast("Could not load lightning");
        }
    }

    function refreshInfoBar(meta) {
        // The frame on screen, not the selected scan, so playback keeps its own time.
        const barMeta = meta || currentRadarMeta();
        if (barMeta || satUsesViewport()) updateInfoBar(barMeta);
    }

    async function loadLightningForCurrentFrame() {
        if (!state.showLightning) return;
        if (!state.selectedStation) return;
        const meta = currentRadarMeta() || glmMetaFromScan(state.scans[state.selectedScanIndex]);
        if (!meta || !meta.date || !meta.time) return;
        const cacheKey = _glmCacheKey(state.selectedStation.id, meta.date, meta.time);
        const frameMs = parseUtcScanMs(meta.date, meta.time);
        const hideFuture = lightningHideFuture();
        const renderKey = lightningRenderKey(cacheKey, frameMs, hideFuture);
        if (!glmAvailableOn(meta.date)) {
            abortLightningFetch();
            state.glmLoading = false;
            clearLightning();
            state.lastGlmCount = 0;
            state.glmRenderedKey = renderKey;
            maybeToastGlm({ reason: "unavailable" }, meta.date);
            refreshInfoBar(meta);
            return;
        }
        if (state.glmCache[cacheKey]) {
            state.glmFetchToken += 1;
            if (state.glmRenderedKey === renderKey && !state.glmLoading) {
                prefetchNeighborLightning();
                return;
            }
            state.glmLoading = false;
            maybeToastGlm(state.glmCache[cacheKey], meta.date);
            renderLightning(state.glmCache[cacheKey], { renderKey, frameMs, hideFuture });
            refreshInfoBar(meta);
            prefetchNeighborLightning();
            return;
        }
        const token = ++state.glmFetchToken;
        state.glmLoading = true;
        refreshInfoBar(meta);
        try {
            const dataPromise = fetchLightning(meta, {
                onPartial: (partialFlashes) => {
                    if (token !== state.glmFetchToken) return;
                    renderLightning({ flashes: partialFlashes, reason: "ok" }, {
                        renderKey,
                        frameMs,
                        hideFuture,
                    });
                    refreshInfoBar(meta);
                },
            });
            prefetchNeighborLightning();
            const data = await dataPromise;
            if (token !== state.glmFetchToken) return;
            state.glmLoading = false;
            maybeToastGlm(data, meta.date);
            renderLightning(data, { renderKey, frameMs, hideFuture });
            refreshInfoBar(meta);
        } catch (err) {
            if (err?.name === "AbortError" || token !== state.glmFetchToken) return;
            state.glmLoading = false;
            maybeToastGlm({ reason: "error" }, meta.date);
            refreshInfoBar(meta);
        }
    }

    // ── GOES satellite ────────────────────────────────────────

    // One crop radius for every radar product, so switching products or the
    // first decode never changes the window (and cache key) for a scan.
    const SAT_CROP_KM = 460;
    const SAT_LOOP_MARGIN_MS = 10 * 60 * 1000;
    const SAT_MESO_LOOP_PAD_MS = 2 * 60 * 1000;
    // Loop times are volume starts; a volume's SAILS passes run a few minutes on.
    const SAT_MESO_LOOP_AFTER_MS = 7 * 60 * 1000;
    const SAT_MESO_LOOP_MAX = 60;
    const SAT_LIVE_POLL_MS = 60 * 1000;
    const SAT_MOVE_DEBOUNCE_MS = 400;

    function satOnlyActive() {
        return state.showSatellite && state.satMode === "only";
    }

    // National view and satellite-only crop to the viewport; the site overlay to the radar.
    function satUsesViewport() {
        return state.mosaicMode || satOnlyActive();
    }

    function fillSatelliteProducts() {
        if (!dom.satelliteProduct || dom.satelliteProduct.options.length) return;
        const byId = new Map(GOES_PRODUCTS.map((item) => [item.id, item]));
        const option = (item) => {
            const el = document.createElement("option");
            el.value = item.id;
            el.textContent = item.kind === "geo" ? item.label : `${item.id} · ${item.label}`;
            return el;
        };
        for (const item of GOES_PRODUCTS) {
            if (item.kind === "geo") dom.satelliteProduct.appendChild(option(item));
        }
        for (const group of SAT_PRODUCT_GROUPS) {
            const el = document.createElement("optgroup");
            el.label = group.label;
            for (const id of group.ids) {
                if (byId.has(id)) el.appendChild(option(byId.get(id)));
            }
            dom.satelliteProduct.appendChild(el);
        }
        for (const item of SAT_QUICK_PRODUCTS) {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "sat-chip";
            btn.dataset.satProduct = item.id;
            btn.textContent = item.label;
            dom.satelliteChips?.appendChild(btn);
        }
        for (const item of GOES_SATELLITES) {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "level-btn";
            btn.dataset.satGoes = item.id;
            btn.textContent = item.label;
            dom.satGoesToggle?.appendChild(btn);
        }
        for (const item of GOES_SECTORS) {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "level-btn";
            btn.dataset.satSector = item.id;
            btn.textContent = item.label;
            dom.satSectorToggle?.appendChild(btn);
        }
    }

    function updateSatReadout() {
        if (!dom.satSectorReadout) return;
        const frame = state.satFrame;
        dom.satSectorReadout.textContent = frame?.sat
            ? `Showing ${frame.sat} ${satSectorLabel(frame.sector)}`
            : "";
    }

    function syncSatelliteControls() {
        fillSatelliteProducts();
        const available = goesAvailableOn(state.date);
        const only = state.satMode === "only";
        if (dom.satelliteToggle) {
            dom.satelliteToggle.checked = state.showSatellite;
            dom.satelliteToggle.disabled = !available && !state.showSatellite;
        }
        if (dom.satelliteHint) dom.satelliteHint.hidden = available;
        if (dom.satelliteBody) dom.satelliteBody.hidden = !(state.showSatellite && available);
        if (dom.sidebar) dom.sidebar.classList.toggle("sat-only", satOnlyActive());
        dom.satModeToggle?.querySelectorAll("[data-sat-mode]").forEach((btn) => {
            btn.classList.toggle("active", btn.dataset.satMode === state.satMode);
        });
        dom.satelliteChips?.querySelectorAll("[data-sat-product]").forEach((btn) => {
            btn.classList.toggle("active", btn.dataset.satProduct === state.satProduct);
        });
        dom.satGoesToggle?.querySelectorAll("[data-sat-goes]").forEach((btn) => {
            btn.classList.toggle("active", btn.dataset.satGoes === state.satGoes);
        });
        dom.satSectorToggle?.querySelectorAll("[data-sat-sector]").forEach((btn) => {
            btn.classList.toggle("active", btn.dataset.satSector === state.satSector);
        });
        if (dom.satelliteProduct) dom.satelliteProduct.value = state.satProduct || "geo";
        if (dom.satHoldRow) dom.satHoldRow.hidden = only;
        if (dom.satHoldToggle) dom.satHoldToggle.checked = state.satHold;
        if (dom.satelliteOpacityRow) dom.satelliteOpacityRow.hidden = only;
        if (dom.satelliteOpacity) {
            const pct = Math.round(state.satOpacity * 100);
            dom.satelliteOpacity.value = String(pct);
            if (dom.satelliteOpacityValue) dom.satelliteOpacityValue.textContent = `${pct}%`;
        }
        updateSatReadout();
    }

    function ensureSatelliteLayer() {
        if (!satelliteLayer) {
            satelliteLayer = new SatelliteCanvasLayer();
            satelliteLayer.setOpacity(state.satOpacity);
        }
        return satelliteLayer;
    }

    function clearSatellite() {
        state.satFrame = null;
        updateSatReadout();
        if (!satelliteLayer || !dom.map) return;
        satelliteLayer.setFrame(null);
        if (dom.map.hasLayer(satelliteLayer)) dom.map.removeLayer(satelliteLayer);
        setSatelliteBorders(dom.map, false);
    }

    function renderSatellite(frame) {
        if (!dom.map || !frame || frame.reason !== "ok") return;
        const layer = ensureSatelliteLayer();
        layer.setOpacity(satOnlyActive() ? 1 : state.satOpacity);
        if (state.satFrame !== frame || !dom.map.hasLayer(layer)) layer.setFrame(frame);
        if (!dom.map.hasLayer(layer)) {
            layer.addTo(dom.map);
            // The imagery hides the basemap's borders, so draw faint ones on top.
            setSatelliteBorders(dom.map, true);
        }
        state.satFrame = frame;
        updateSatReadout();
    }

    function maybeToastSatellite(data, date) {
        const key = `${data?.reason || ""}:${state.satGoes}:${String(date || "").replace(/-/g, "")}`;
        if (key === state.satToastKey) return;
        if (data?.reason === "outside" && state.satGoes !== "auto") {
            // A forced satellite can't see past its own horizon (e.g. Hawaii from East).
            state.satToastKey = key;
            showShareToast(`Out of view for GOES-${state.satGoes === "west" ? "West" : "East"}. Try Auto.`);
            return;
        }
        if (data?.reason === "unavailable") {
            state.satToastKey = key;
            showShareToast("GOES imagery starts in December 2017");
        } else if (data?.reason === "error") {
            state.satToastKey = key;
            showShareToast("Could not load satellite");
        } else if (data?.reason === "empty" || data?.reason === "outside") {
            state.satToastKey = key;
            showShareToast("No GOES image for this time");
        }
    }

    function goesDateTime(ms) {
        const iso = new Date(ms).toISOString();
        return { date: iso.slice(0, 10), time: iso.slice(11, 19) };
    }

    function scanMs(scan) {
        return scan ? parseUtcScanMs(scan.scanDate || state.date, scan.time) : null;
    }

    function frameMidMs(frame) {
        return Number.isFinite(frame?.endMs) ? (frame.startMs + frame.endMs) / 2 : frame?.startMs;
    }

    function mapBounds() {
        const b = dom.map.getBounds();
        return { north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() };
    }

    function boundsInside(inner, outer) {
        return !!inner && !!outer
            && inner.north <= outer.north && inner.south >= outer.south
            && inner.east <= outer.east && inner.west >= outer.west;
    }

    // Inside `outer` and not so much smaller that its crop would be decimated
    // less. Stands in for goesFrameCovers until a frame has loaded.
    function boundsFit(inner, outer) {
        if (!boundsInside(inner, outer)) return false;
        const span = (b) => Math.max(b.north - b.south, b.east - b.west);
        return span(inner) * 2 > span(outer);
    }

    function satelliteArea() {
        if (satUsesViewport()) {
            if (!state.satBounds && dom.map) state.satBounds = mapBounds();
            return state.satBounds ? { bounds: state.satBounds } : null;
        }
        if (!state.selectedStation) return null;
        return { lat: state.selectedStation.lat, lon: state.selectedStation.lon, rangeKm: SAT_CROP_KM };
    }

    function satAreaKey(area) {
        if (!area) return "";
        if (area.bounds) return ["north", "south", "east", "west"].map((k) => area.bounds[k].toFixed(2)).join(",");
        return `${area.lat.toFixed(2)},${area.lon.toFixed(2)},${area.rangeKm}`;
    }

    function satelliteMeta() {
        const scan = state.scans[state.selectedScanIndex];
        return currentRadarMeta() || glmMetaFromScan(scan);
    }

    // The radar time the overlay matches: the frame on screen, or the national composite.
    function satRadarMs() {
        if (state.mosaicMode) return Number.isFinite(state.mosaicTimeMs) ? state.mosaicTimeMs : null;
        const meta = satelliteMeta();
        return meta?.date && meta?.time ? parseUtcScanMs(meta.date, meta.time) : null;
    }

    // Aborted whenever the station, date or view changes, so stale prefetches
    // never sit in the queue ahead of the frame on screen.
    function satelliteSignal() {
        if (!state.satAbort) state.satAbort = new AbortController();
        return state.satAbort.signal;
    }

    function resetSatelliteRequests() {
        state.satAbort?.abort();
        state.satAbort = null;
        state.satCurrentAbort?.abort();
        state.satCurrentAbort = null;
        state.satLoop = null;
        state.satBounds = null;
        state.satHoldMs = null;
        state.satClockKey = "";
    }

    // Only the newest on-screen request matters. A superseded one is dropped if
    // it is still queued; if it is already running it finishes and is cached.
    function currentSatSignal() {
        state.satCurrentAbort?.abort();
        const current = new AbortController();
        state.satCurrentAbort = current;
        satelliteSignal().addEventListener("abort", () => current.abort(), { once: true });
        return current.signal;
    }

    // Cells a viewport crop may have a side, sized to this screen.
    function satMaxSize() {
        const size = dom.map?.getSize();
        return viewMaxSize({
            width: size?.x,
            height: size?.y,
            dpr: window.devicePixelRatio || 1,
            textureMax: ensureSatelliteLayer().maxTextureSize(),
        });
    }

    function requestSatFrame(ms, area, priority, signal) {
        const { date, time } = goesDateTime(ms);
        return loadGoesFrame({
            ...area,
            maxSize: satMaxSize(),
            date,
            time,
            product: state.satProduct,
            sector: state.satSector,
            satellite: state.satGoes,
            priority,
            signal,
        });
    }

    // A loop or clock entry keeps its frame and settles to "ok" or "none".
    // An aborted request leaves it pending.
    function loadSatTarget(target, priority, signal) {
        return requestSatFrame(target.ms, target.area, priority, signal).then((frame) => {
            target.frame = frame?.reason === "ok" ? frame : null;
            target.status = target.frame ? "ok" : "none";
            return frame;
        });
    }

    function prefetchSatellite(scan) {
        if (!state.showSatellite || satOnlyActive() || state.satHold) return;
        const area = satelliteArea();
        const ms = scanMs(scan);
        if (!area || !Number.isFinite(ms) || !goesAvailableOn(goesDateTime(ms).date)) return;
        requestSatFrame(ms, area, "prefetch", satelliteSignal()).catch(() => {});
    }

    function prefetchNextSatellite() {
        if (state.playing || state.satHold) return;
        if (!state.mosaicMode) {
            prefetchSatellite(state.scans[state.selectedScanIndex + 1]);
            return;
        }
        const area = satelliteArea();
        if (!area || !Number.isFinite(state.mosaicTimeMs)) return;
        requestSatFrame(state.mosaicTimeMs + MOSAIC_STEP_MS, area, "prefetch", satelliteSignal()).catch(() => {});
    }

    // The times of the loop on screen: the radar frames, or the national composites.
    function currentLoopTimes() {
        if (state.mosaicMode) {
            return Number.isFinite(state.mosaicLoopAnchorMs)
                ? loopTimes(state.mosaicLoopAnchorMs, state.frameCount, MOSAIC_STEP_MS)
                : [];
        }
        return state.frameWindow.map((idx) => scanMs(state.scans[idx]));
    }

    // Queue a loop's satellite frames at loop priority, one request per GOES
    // file, newest first like the radar. Playback shows the nearest ready one.
    async function queueSatelliteLoop(timesMs) {
        state.satLoop = null;
        if (!state.showSatellite || satOnlyActive() || state.satHold) return;
        const area = satelliteArea();
        const times = timesMs.filter(Number.isFinite).sort((a, b) => a - b);
        if (!area || !times.length) return;
        const { date } = goesDateTime(times[times.length - 1]);
        if (!goesAvailableOn(date)) return;
        const loop = { times, targets: [] };
        state.satLoop = loop;
        const signal = satelliteSignal();
        let scans = [];
        try {
            scans = await listGoesScans({
                ...area,
                date,
                startMs: times[0] - SAT_LOOP_MARGIN_MS,
                endMs: times[times.length - 1] + SAT_LOOP_MARGIN_MS,
                // Pick the sector where the frames themselves will, not past the margin.
                sectorAtMs: times[times.length - 1],
                sector: state.satSector,
                satellite: state.satGoes,
                signal,
            });
        } catch {
            scans = [];
        }
        if (state.satLoop !== loop || signal.aborted) return;
        const listed = scans.map((scan) => ({ ...scan, ms: (scan.startMs + scan.endMs) / 2 }));
        const byFile = new Map();
        const meso = listed.length && goesCadenceMs(listed[listed.length - 1].sector) < goesCadenceMs("C");
        if (meso) {
            // Meso scans every minute, finer than the radar volumes and their SAILS
            // passes. Queue every scan for the newest stretch, which is what plays.
            const first = times[0] - SAT_MESO_LOOP_PAD_MS;
            const last = times[times.length - 1] + SAT_MESO_LOOP_AFTER_MS;
            const recent = listed.filter((item) => item.ms >= first && item.ms <= last)
                .slice(-Math.min(SAT_MESO_LOOP_MAX, state.frameCount * 2));
            for (const scan of recent) byFile.set(scan.name, { ms: scan.ms, area, status: "pending", frame: null });
        }
        // One file per radar volume across the whole window.
        for (const ms of times) {
            const scan = nearestByTime(listed, ms);
            const match = scan && Math.abs(scan.ms - ms) <= goesCadenceMs(scan.sector) ? scan : null;
            const key = match ? match.name : `t${ms}`;
            if (!byFile.has(key)) byFile.set(key, { ms: match ? match.ms : ms, area, status: "pending", frame: null });
        }
        loop.targets = [...byFile.values()].sort((a, b) => a.ms - b.ms);
        updateFrameProgressUI();
        for (const target of loop.targets.slice().reverse()) {
            loadSatTarget(target, "loop", signal).then(() => {
                if (state.satLoop === loop) updateFrameProgressUI();
            }, () => {});
        }
    }

    // During playback: the nearest decoded loop frame, so the radar never waits.
    function showNearestLoopFrame(ms) {
        const targets = state.satLoop?.targets;
        if (!targets?.length) return false;
        const wanted = nearestByTime(targets, ms);
        const ready = nearestByTime(targets, ms, (t) => t.status === "ok");
        if (ready) renderSatellite(ready.frame);
        state.satStatus = wanted.status === "ok" ? "ready" : (wanted.status === "pending" ? "loading" : "none");
        return true;
    }

    // "Sat 5/8": GOES files settled out of those the loop needs.
    function satLoopProgressText(always = false) {
        const targets = satOnlyActive() ? state.satClock : state.satLoop?.targets;
        if (!state.showSatellite || !targets?.length) return "";
        const settled = targets.filter((t) => t.status !== "pending").length;
        return always || settled < targets.length ? `Sat ${settled}/${targets.length}` : "";
    }

    async function loadSatelliteForCurrentFrame() {
        if (!state.showSatellite) return;
        syncSatelliteControls();
        if (satOnlyActive()) {
            syncSatClock();
            return;
        }
        if (state.satHold && !Number.isFinite(state.satHoldMs)) state.satHoldMs = satRadarMs();
        const ms = state.satHold ? state.satHoldMs : satRadarMs();
        const area = satelliteArea();
        if (!area || !Number.isFinite(ms)) return;
        const { date } = goesDateTime(ms);
        if (!goesAvailableOn(date)) {
            state.satStatus = "none";
            clearSatellite();
            if (!state.playing) maybeToastSatellite({ reason: "unavailable" }, date);
            refreshInfoBar();
            return;
        }
        if (state.playing && !state.satHold && showNearestLoopFrame(ms)) {
            refreshInfoBar();
            return;
        }
        const token = ++state.satFetchToken;
        const signal = currentSatSignal();
        // Cached frames come back within a tick; only show "loading" for real fetches.
        const loadingTimer = setTimeout(() => {
            if (token !== state.satFetchToken) return;
            state.satStatus = "loading";
            refreshInfoBar();
        }, 120);
        try {
            const data = await requestSatFrame(ms, area, "current", signal);
            clearTimeout(loadingTimer);
            if (token !== state.satFetchToken) return;
            if (data?.reason === "ok") {
                renderSatellite(data);
                state.satStatus = "ready";
            } else {
                state.satStatus = "none";
                if (!state.playing) {
                    clearSatellite();
                    maybeToastSatellite(data, date);
                }
            }
            refreshInfoBar();
            prefetchNextSatellite();
        } catch (err) {
            clearTimeout(loadingTimer);
            if (err?.name === "AbortError" || token !== state.satFetchToken) return;
            state.satStatus = "none";
            if (!state.playing) maybeToastSatellite({ reason: "error" }, date);
            refreshInfoBar();
        }
    }

    // ── Satellite-only clock ──────────────────────────────────

    // The loop ends at the selected scan (site) or the national composite time.
    function satAnchorMs() {
        if (state.liveMode) return Date.now();
        if (state.mosaicMode) return Number.isFinite(state.mosaicTimeMs) ? state.mosaicTimeMs : null;
        const scan = state.scans[state.selectedScanIndex];
        if (scan) return scanMs(scan);
        return state.initialUrlTime && state.date ? parseUtcScanMs(state.date, state.initialUrlTime) : null;
    }

    function satClockGuessStepMs() {
        if (state.satSector !== "auto") return goesCadenceMs(state.satSector);
        const last = state.satClock[state.satClock.length - 1];
        return goesCadenceMs(last?.sector || state.satFrame?.sector || "C");
    }

    async function listSatClock(anchorMs, stepMs, area) {
        try {
            const scans = await listGoesScans({
                ...area,
                date: goesDateTime(anchorMs).date,
                startMs: loopStartMs(anchorMs, state.frameCount, stepMs) - stepMs / 2,
                endMs: anchorMs + stepMs / 2,
                sectorAtMs: anchorMs,
                sector: state.satSector,
                satellite: state.satGoes,
                signal: satelliteSignal(),
            });
            return scans.slice(-state.frameCount);
        } catch {
            return [];
        }
    }

    function resetSatClock() {
        state.satClockToken += 1;
        state.satPreloadToken += 1;
        state.satPreloading = false;
        state.satClock = [];
        state.satClockIndex = -1;
        state.satClockKey = "";
    }

    // Lists the scans of the loop ending at the anchor, then shows the newest.
    // `force` re-lists the same loop (live), keeping loaded frames.
    async function syncSatClock(options = {}) {
        const anchorMs = satAnchorMs();
        const area = satelliteArea();
        ensureSatLiveTimer();
        if (!Number.isFinite(anchorMs) || !area) {
            renderSatelliteChrome();
            return;
        }
        const { date } = goesDateTime(anchorMs);
        if (!goesAvailableOn(date)) {
            resetSatClock();
            clearSatellite();
            state.satStatus = "none";
            maybeToastSatellite({ reason: "unavailable" }, date);
            renderSatelliteChrome();
            return;
        }
        const key = [
            state.liveMode ? "live" : anchorMs,
            state.satSector,
            state.satGoes,
            state.frameCount,
            satAreaKey(area),
        ].join("|");
        if (!options.force && key === state.satClockKey && state.satClock.length) {
            showSatClockFrame(state.satClockIndex);
            return;
        }
        const token = ++state.satClockToken;
        const followNewest = state.satClockIndex >= state.satClock.length - 1;
        const keepName = options.force && !followNewest ? state.satClock[state.satClockIndex]?.name : null;
        state.satClockKey = key;
        if (!options.force) {
            state.satStatus = "loading";
            renderSatelliteChrome();
        }
        let stepMs = satClockGuessStepMs();
        let scans = await listSatClock(anchorMs, stepMs, area);
        if (token !== state.satClockToken) return;
        // Auto can land on Meso, CONUS or Full Disk; size the span by the sector it found.
        const listedStep = scans.length ? goesCadenceMs(scans[scans.length - 1].sector) : stepMs;
        if (listedStep !== stepMs) {
            stepMs = listedStep;
            scans = await listSatClock(anchorMs, stepMs, area);
            if (token !== state.satClockToken) return;
        }
        const previous = options.force ? new Map(state.satClock.map((entry) => [entry.name, entry])) : new Map();
        state.satClock = scans.map((scan) => previous.get(scan.name) || {
            ...scan,
            ms: (scan.startMs + scan.endMs) / 2,
            area,
            status: "pending",
            frame: null,
        });
        state.satClockStepMs = stepMs;
        const kept = keepName ? state.satClock.findIndex((entry) => entry.name === keepName) : -1;
        state.satClockIndex = kept >= 0 ? kept : state.satClock.length - 1;
        updateFramesLabel();
        if (!state.satClock.length) {
            clearSatellite();
            state.satStatus = "none";
            if (!state.playing) maybeToastSatellite({ reason: "empty" }, date);
            renderSatelliteChrome();
            return;
        }
        showSatClockFrame(state.satClockIndex);
    }

    function prefetchSatClock(index) {
        const entry = state.satClock[index];
        if (entry?.status === "pending") loadSatTarget(entry, "prefetch", satelliteSignal()).catch(() => {});
    }

    async function showSatClockFrame(index) {
        const entry = state.satClock[index];
        if (!entry) {
            renderSatelliteChrome();
            return;
        }
        state.satClockIndex = index;
        const token = ++state.satFetchToken;
        if (entry.status === "ok") {
            renderSatellite(entry.frame);
            state.satStatus = "ready";
            renderSatelliteChrome();
            if (!state.playing) {
                prefetchSatClock(index - 1);
                prefetchSatClock(index + 1);
            }
            return;
        }
        // Playback never waits: keep the last frame and mark this one.
        if (state.playing || entry.status === "none") {
            state.satStatus = entry.status === "pending" ? "loading" : "none";
            if (!state.playing) clearSatellite();
            renderSatelliteChrome();
            return;
        }
        state.satStatus = "loading";
        renderSatelliteChrome();
        try {
            const data = await loadSatTarget(entry, "current", currentSatSignal());
            if (token !== state.satFetchToken || state.satClock[index] !== entry) return;
            if (entry.status === "ok") {
                renderSatellite(entry.frame);
                state.satStatus = "ready";
                prefetchSatClock(index - 1);
                prefetchSatClock(index + 1);
            } else {
                state.satStatus = "none";
                clearSatellite();
                if (!state.playing) maybeToastSatellite(data, goesDateTime(entry.ms).date);
            }
            renderSatelliteChrome();
        } catch (err) {
            if (err?.name === "AbortError" || token !== state.satFetchToken) return;
            state.satStatus = "none";
            renderSatelliteChrome();
        }
    }

    function satClockLabel(index) {
        const entry = state.satClock[index];
        if (!entry) return state.satStatus === "loading" ? "Listing satellite scans…" : "No satellite scans";
        const base = `${formatGoesClock(entry.startMs)} · ${satSectorLabel(entry.sector)} · ${index + 1} / ${state.satClock.length}`;
        const progress = state.satPreloading ? satLoopProgressText(true) : "";
        return progress ? `${base} · ${progress}` : base;
    }

    function renderSatelliteChrome() {
        if (!satOnlyActive()) return;
        renderSatelliteInfoBar();
        updateFrameProgressUI();
        const ready = state.satClock.length > 0;
        // The scan list may have left the play button spinning; the clock owns it now.
        if (!state.playing && !state.satPreloading) setPlayButtonState("stopped");
        dom.btnPrev.disabled = !ready;
        dom.btnPlay.disabled = !ready;
        dom.btnNext.disabled = !ready;
    }

    // Preloads every scan of the clock at loop priority, then plays it.
    async function toggleSatellitePlay() {
        if (state.playing) {
            stopAnim();
            ensureSatLiveTimer();
            updateURL();
            return;
        }
        if (state.satPreloading) {
            stopAnim();
            ensureSatLiveTimer();
            return;
        }
        const clock = state.satClock;
        if (!clock.length) return;
        const token = ++state.satPreloadToken;
        state.satPreloading = true;
        stopSatLiveTimer();
        setPlayButtonState("loading");
        updateFrameProgressUI();
        const signal = satelliteSignal();
        await Promise.all(clock.slice().reverse().map((entry) => (entry.status !== "pending"
            ? null
            : loadSatTarget(entry, "loop", signal).then(() => {
                if (token === state.satPreloadToken) updateFrameProgressUI();
            }, () => {}))));
        if (token !== state.satPreloadToken || clock !== state.satClock) return;
        state.satPreloading = false;
        const first = clock.findIndex((entry) => entry.status === "ok");
        if (first < 0) {
            setPlayButtonState("stopped");
            renderSatelliteChrome();
            ensureSatLiveTimer();
            return;
        }
        state.playing = true;
        setPlayButtonState("playing");
        state.animEndPauseTicksRemaining = 0;
        showSatClockFrame(first);
        state.animInterval = setInterval(satAnimTick, state.animSpeed);
        updateURL();
    }

    function satAnimTick() {
        const clock = state.satClock;
        if (!clock.length) return;
        // Hold on the newest scan before looping back, as the radar loop does.
        if (state.animEndPauseTicksRemaining > 0) {
            state.animEndPauseTicksRemaining -= 1;
            if (state.animEndPauseTicksRemaining > 0) return;
        } else if (state.satClockIndex === lastReadySatIndex(clock)) {
            state.animEndPauseTicksRemaining = END_PAUSE_MULTIPLIER - 1;
            if (state.animEndPauseTicksRemaining > 0) return;
        }
        let next = state.satClockIndex;
        for (let i = 0; i < clock.length; i++) {
            next = (next + 1) % clock.length;
            if (clock[next].status === "ok") break;
        }
        showSatClockFrame(next);
    }

    function lastReadySatIndex(clock) {
        for (let i = clock.length - 1; i >= 0; i--) {
            if (clock[i].status === "ok") return i;
        }
        return -1;
    }

    function stepSatClock(delta) {
        if (state.playing || state.satPreloading) stopAnim();
        const total = state.satClock.length;
        if (total) showSatClockFrame((state.satClockIndex + delta + total) % total);
        ensureSatLiveTimer();
    }

    function stopSatLiveTimer() {
        if (state.satLiveTimer) {
            clearInterval(state.satLiveTimer);
            state.satLiveTimer = null;
        }
    }

    // Live satellite-only follows the newest scan, re-listing once a minute.
    function ensureSatLiveTimer() {
        const want = satOnlyActive() && state.liveMode && !state.playing && !state.satPreloading;
        if (!want) {
            stopSatLiveTimer();
        } else if (!state.satLiveTimer) {
            state.satLiveTimer = setInterval(() => syncSatClock({ force: true }), SAT_LIVE_POLL_MS);
        }
    }

    function setSatelliteLive(enabled) {
        if (state.playing || state.satPreloading) stopAnim();
        if (state.mosaicMode) {
            const entry = state.satClock[state.satClockIndex];
            if (!enabled && entry) state.mosaicTimeMs = snapMosaicMs(entry.startMs);
            state.liveMode = !!enabled;
            dom.datePicker.hidden = state.liveMode;
            dom.dateLiveHint.hidden = !state.liveMode;
            updateLiveButtonState();
            if (!state.liveMode) syncMosaicDateControl();
        } else {
            setLiveModeEnabled(enabled, { refreshNow: true });
        }
        state.satClockKey = "";
        syncSatClock();
        updateURL();
    }

    function hideRadarForSatellite() {
        if (radarOverlay && dom.map.hasLayer(radarOverlay)) dom.map.removeLayer(radarOverlay);
        if (rangeRings && dom.map.hasLayer(rangeRings)) dom.map.removeLayer(rangeRings);
        if (mosaicLayer && dom.map.hasLayer(mosaicLayer)) dom.map.removeLayer(mosaicLayer);
    }

    // Enters or leaves satellite-only after showSatellite or satMode changed.
    function applySatelliteMode(wasOnly) {
        const only = satOnlyActive();
        syncSatelliteControls();
        if (only === wasOnly) return false;
        stopAnim();
        stopSatLiveTimer();
        state.satFetchToken += 1;
        state.satLoop = null;
        state.satBounds = null;
        if (only) {
            resetSatClock();
            if (rainPanel?.active) rainPanel.exit();
            if (state.mosaicMode) {
                stopMosaicLiveTimer();
            } else {
                stopLivePolling();
                invalidatePreload();
            }
            hideRadarForSatellite();
            updateLegend();
            setColorScaleVisible(true);
            updateFramesLabel();
            loadSatelliteForCurrentFrame();
        } else {
            const entry = state.satClock[state.satClockIndex];
            resetSatClock();
            updateFramesLabel();
            if (state.mosaicMode) {
                if (state.liveMode) {
                    jumpMosaicToLatest();
                    startMosaicLiveTimer();
                } else {
                    // Pick the national composite up where the satellite loop stopped.
                    if (entry) state.mosaicTimeMs = snapMosaicMs(entry.startMs);
                    refreshMosaic();
                }
            } else {
                restoreSiteRadar();
                if (state.liveMode && state.selectedStation) startLivePolling();
                if (state.showSatellite) loadSatelliteForCurrentFrame();
            }
        }
        updateURL();
        return true;
    }

    function setSatelliteVisible(show) {
        const wasOnly = satOnlyActive();
        state.showSatellite = !!show;
        if (!state.showSatellite) {
            state.satFetchToken += 1;
            resetSatelliteRequests();
            state.satStatus = "none";
            clearSatellite();
        }
        if (applySatelliteMode(wasOnly)) return;
        if (state.showSatellite) {
            if (state.playing) queueSatelliteLoop(currentLoopTimes());
            loadSatelliteForCurrentFrame();
        } else {
            refreshInfoBar();
        }
    }

    function setSatelliteMode(mode) {
        const next = mode === "only" ? "only" : "overlay";
        if (next === state.satMode) return;
        const wasOnly = satOnlyActive();
        state.satMode = next;
        applySatelliteMode(wasOnly);
    }

    // Re-requests what is on screen after the product or sector changed.
    function reloadSatellite() {
        if (!state.showSatellite) return;
        state.satFetchToken += 1;
        if (satOnlyActive()) {
            const resume = state.playing;
            if (resume || state.satPreloading) stopAnim();
            // Fresh entries, so requests for the old choice cannot land on them.
            state.satClock = state.satClock.map((entry) => ({ ...entry, status: "pending", frame: null }));
            updateLegend();
            setColorScaleVisible(true);
            syncSatClock().then(() => {
                if (resume && satOnlyActive() && !state.playing) toggleSatellitePlay();
            });
            return;
        }
        if (state.playing) queueSatelliteLoop(currentLoopTimes());
        loadSatelliteForCurrentFrame();
    }

    function setSatelliteProduct(product) {
        const next = normalizeSatParam(product) || "geo";
        if (next === state.satProduct) return;
        state.satProduct = next;
        syncSatelliteControls();
        reloadSatellite();
    }

    function setSatelliteSector(sector) {
        const next = normalizeSatSector(sector);
        if (next === state.satSector) return;
        state.satSector = next;
        syncSatelliteControls();
        reloadSatellite();
    }

    function setSatelliteGoes(choice) {
        const next = normalizeSatChoice(choice);
        if (next === state.satGoes) return;
        state.satGoes = next;
        syncSatelliteControls();
        reloadSatellite();
    }

    function setSatelliteHold(on) {
        state.satHold = !!on;
        state.satHoldMs = state.satHold && state.satFrame ? frameMidMs(state.satFrame) : null;
        syncSatelliteControls();
        if (!state.showSatellite || satOnlyActive()) return;
        if (!state.satHold && state.playing) queueSatelliteLoop(currentLoopTimes());
        loadSatelliteForCurrentFrame();
    }

    // National and satellite-only crops follow the viewport, but only reload once
    // the loaded crop no longer covers it.
    function onSatelliteMapMove() {
        if (!state.showSatellite || !satUsesViewport() || !state.satBounds) return;
        clearTimeout(state.satDebounceTimer);
        state.satDebounceTimer = setTimeout(() => {
            state.satDebounceTimer = null;
            if (!state.showSatellite || !satUsesViewport() || !state.satBounds) return;
            const bounds = mapBounds();
            const shown = state.satFrame;
            if (shown ? goesFrameCovers(shown, bounds, satMaxSize()) : boundsFit(bounds, state.satBounds)) return;
            state.satBounds = bounds;
            if (satOnlyActive()) {
                const resume = state.playing;
                if (resume || state.satPreloading) stopAnim();
                syncSatClock().then(() => {
                    if (resume && satOnlyActive() && !state.playing) toggleSatellitePlay();
                });
                return;
            }
            if (state.playing) queueSatelliteLoop(currentLoopTimes());
            loadSatelliteForCurrentFrame();
        }, SAT_MOVE_DEBOUNCE_MS);
    }

    function satInfoHtml() {
        if (!state.showSatellite) return "";
        const frame = state.satFrame;
        const status = state.satStatus;
        let text;
        if (frame) {
            text = formatSatInfo(frame);
            const offset = satOnlyActive() ? "" : formatSatOffset(frame.startMs, satRadarMs());
            if (offset) text += ` (${offset})`;
        } else {
            text = `${satProductLabel(state.satProduct)}${status === "loading" ? "…" : " · no image"}`;
        }
        const title = status === "ready"
            ? "Satellite image ready"
            : (status === "loading" ? "Loading satellite image" : "No satellite image for this time");
        return `<span class="info-item sat-info"><span class="sat-dot is-${status}" role="img" title="${title}" aria-label="${title}"></span><span class="info-label">Sat:</span> <span class="info-value">${text}</span></span>`;
    }

    function renderSatelliteInfoBar() {
        const entry = state.satClock[state.satClockIndex];
        const where = state.mosaicMode ? "National" : (state.selectedStation?.id || "");
        const date = entry ? goesDateTime(entry.startMs).date : state.date;
        dom.mapInfoBar.innerHTML = `
            <span class="info-item"><span class="info-label">View:</span> <span class="info-value">Satellite${where ? ` · ${where}` : ""}</span></span>
            ${satInfoHtml()}
            ${date ? `<span class="info-item"><span class="info-label">Date:</span> <span class="info-value">${formatDate(date)}</span></span>` : ""}
        `;
        dom.mapInfoBar.classList.add("visible");
        syncMapDockOffsets();
    }

    function clearAlerts() {
        if (alertsLayer && dom.map) {
            alertsLayer.clearLayers();
            if (dom.map.hasLayer(alertsLayer)) dom.map.removeLayer(alertsLayer);
        }
    }

    function _alertsCacheKey(stationId, date, time) {
        return `${stationId || ""}_${(date || "").replace(/-/g, "")}_${(time || "").replace(/:/g, "")}`;
    }

    async function fetchAlerts(meta) {
        if (!meta || !state.selectedStation) return null;
        const cacheKey = _alertsCacheKey(state.selectedStation.id, meta.date, meta.time);
        if (state.alertsCache[cacheKey]) return state.alertsCache[cacheKey];
        try {
            const data = await loadAlerts({
                lat: meta.lat,
                lon: meta.lon,
                maxRangeKm: meta.max_range_km,
                date: meta.date,
                time: meta.time,
            });
            state.alertsCache[cacheKey] = data;
            return data;
        } catch {
            return { features: [], reason: "error" };
        }
    }

    function escapeAlertHtml(value) {
        return String(value ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
    }

    function formatAlertClock(value) {
        if (!value) return null;
        const text = String(value);
        if (text.length >= 20 && text.includes("T")) {
            return `${text.slice(11, 16)} UTC`;
        }
        return text;
    }

    function buildAlertPopup(feature) {
        const rows = [];
        if (feature.kind === "md" && feature.concerning) {
            rows.push(`<div>${escapeAlertHtml(feature.concerning)}</div>`);
        }
        if (feature.wfo) {
            rows.push(`<div>Office: ${escapeAlertHtml(feature.wfo)}</div>`);
        }
        if (feature.phenomena && feature.kind !== "md") {
            const vtec = [
                feature.year,
                feature.wfo,
                feature.phenomena,
                feature.significance,
                feature.eventid != null ? String(feature.eventid).padStart(4, "0") : null,
            ].filter((part) => part != null && part !== "").join("-");
            if (vtec) rows.push(`<div>VTEC: ${escapeAlertHtml(vtec)}</div>`);
        }
        const issue = formatAlertClock(feature.issue);
        const expire = formatAlertClock(feature.expire);
        if (issue || expire) {
            rows.push(`<div>${issue ? `Issued ${escapeAlertHtml(issue)}` : ""}${issue && expire ? " · " : ""}${expire ? `Until ${escapeAlertHtml(expire)}` : ""}</div>`);
        }
        if (feature.isPds) rows.push("<div>PDS</div>");
        if (feature.isEmergency) rows.push("<div>Emergency</div>");
        if (feature.tornadotag) rows.push(`<div>Tornado: ${escapeAlertHtml(feature.tornadotag)}</div>`);
        if (feature.hailtag) rows.push(`<div>Hail: ${escapeAlertHtml(feature.hailtag)}</div>`);
        if (feature.windtag) rows.push(`<div>Wind: ${escapeAlertHtml(feature.windtag)}</div>`);
        if (feature.watchConfidence != null) {
            rows.push(`<div>Watch confidence: ${escapeAlertHtml(feature.watchConfidence)}%</div>`);
        }
        if (feature.href) {
            const linkText = feature.kind === "md" ? "SPC discussion" : (feature.kind === "watch" ? "SPC watch" : "VTEC details");
            rows.push(`<div><a class="alert-popup-link" href="${escapeAlertHtml(feature.href)}" target="_blank" rel="noopener">${linkText}</a></div>`);
        }
        return [
            '<div class="alert-popup-content">',
            `<div class="alert-popup-title">${escapeAlertHtml(alertTitle(feature))}</div>`,
            ...rows,
            "</div>",
        ].join("");
    }

    function renderAlerts(data, cacheKey) {
        if (!dom.map) return;
        if (!alertsLayer) alertsLayer = L.layerGroup();
        alertsLayer.clearLayers();

        const features = data?.features || [];
        state.lastAlertCount = features.length;
        state.alertsRenderedKey = cacheKey || "";

        if (!features.length) {
            if (dom.map.hasLayer(alertsLayer)) dom.map.removeLayer(alertsLayer);
            return;
        }

        const paneName = "alertsPane";
        if (!dom.map.getPane(paneName)) {
            const pane = dom.map.createPane(paneName);
            pane.style.zIndex = "452";
        }

        const order = { md: 0, watch: 1, warning: 2 };
        const sorted = features.slice().sort((a, b) => (order[a.kind] || 0) - (order[b.kind] || 0));
        sorted.forEach((feature) => {
            if (!feature.geometry) return;
            const style = alertStyle(feature);
            L.geoJSON(feature.geometry, {
                pane: paneName,
                style: {
                    color: style.stroke,
                    fillColor: style.fill,
                    fillOpacity: style.fillOpacity,
                    weight: style.weight,
                    dashArray: style.dashArray || null,
                    className: `alert-poly alert-${feature.kind}`,
                },
            }).bindPopup(() => buildAlertPopup(feature), {
                className: "alert-popup",
            }).addTo(alertsLayer);

            const labelPt = geometryLabelPoint(feature.geometry);
            if (labelPt) {
                L.marker(labelPt, {
                    icon: L.divIcon({
                        className: `alert-label alert-label-${feature.kind}`,
                        html: `<span class="alert-label-text">${escapeAlertHtml(alertLabel(feature))}</span>`,
                        iconSize: [72, 16],
                        iconAnchor: [36, 8],
                    }),
                    interactive: false,
                    pane: paneName,
                }).addTo(alertsLayer);
            }
        });

        if (!dom.map.hasLayer(alertsLayer)) {
            alertsLayer.addTo(dom.map);
        }
    }

    function maybeToastAlerts(data) {
        const key = data?.reason || "";
        if (key === state.alertsToastKey) return;
        if (data?.reason === "error") {
            state.alertsToastKey = key;
            showShareToast("Could not load watches & warnings");
        }
    }

    async function loadAlertsForCurrentFrame() {
        if (!state.showAlerts) return;
        if (!state.selectedStation) return;
        const meta = currentRadarMeta();
        if (!meta || !meta.date || !meta.time) return;
        const cacheKey = _alertsCacheKey(state.selectedStation.id, meta.date, meta.time);
        if (state.alertsRenderedKey === cacheKey) return;
        const token = ++state.alertsFetchToken;
        const data = await fetchAlerts(meta);
        if (token !== state.alertsFetchToken) return;
        maybeToastAlerts(data);
        renderAlerts(data, cacheKey);
        if (state.lastRadarMeta) updateInfoBar(state.lastRadarMeta);
    }

    function clearStormTracks() {
        if (stormTrackLayer && dom.map) {
            stormTrackLayer.clearLayers();
        }
    }

    function _stormCacheKey(date, time) {
        return (date || "").replace(/-/g, "") + "_" + (time || "").replace(/:/g, "");
    }

    async function fetchStormTracks(station, date, time) {
        if (!station || !date || !time) return null;
        const cacheKey = _stormCacheKey(date, time);
        if (state.stormTrackCache[cacheKey]) return state.stormTrackCache[cacheKey];

        const dateClean = date.replace(/-/g, "");
        const timeClean = time.replace(/:/g, "");
        try {
            const data = await loadStormTracks({
                station: state.selectedStation,
                dateClean,
                timeClean,
            });
            state.stormTrackCache[cacheKey] = data;
            return data;
        } catch {
            return null;
        }
    }

    function renderStormTracks(data) {
        if (!dom.map) return;
        if (!stormTrackLayer) {
            stormTrackLayer = L.layerGroup();
        }
        stormTrackLayer.clearLayers();

        if (!data || !data.storms || data.storms.length === 0) {
            if (dom.map.hasLayer(stormTrackLayer)) dom.map.removeLayer(stormTrackLayer);
            return;
        }

        // Create pane for storm tracks so they render on top of radar
        const stormTrackPaneName = "stormTrackPane";
        if (!dom.map.getPane(stormTrackPaneName)) {
            const pane = dom.map.createPane(stormTrackPaneName);
            pane.style.zIndex = "460"; // Above radar (220) and reference overlay (450)
        }
        const stormTrackPane = stormTrackPaneName;

        data.storms.forEach(function (storm) {
            const pos = [storm.lat, storm.lon];

            // Forecast track polyline (solid, white)
            if (storm.forecast && storm.forecast.length > 0) {
                const fcPts = [pos].concat(storm.forecast);
                L.polyline(fcPts, {
                    color: "#ffffff",
                    weight: 2.5,
                    opacity: 0.85,
                    dashArray: null,
                    className: "storm-track-forecast",
                    pane: stormTrackPane,
                }).addTo(stormTrackLayer);

                // Forecast position dots
                storm.forecast.forEach(function (fp) {
                    L.circleMarker(fp, {
                        radius: 3,
                        color: "#ffffff",
                        fillColor: "#ffffff",
                        fillOpacity: 0.7,
                        weight: 1,
                        pane: stormTrackPane,
                    }).addTo(stormTrackLayer);
                });
            }

            // Past track polyline (dashed, muted)
            if (storm.past && storm.past.length > 0) {
                var pastPts = storm.past.slice().reverse().concat([pos]);
                L.polyline(pastPts, {
                    color: "#ffaa44",
                    weight: 2,
                    opacity: 0.5,
                    dashArray: "6,4",
                    className: "storm-track-past",
                    pane: stormTrackPane,
                }).addTo(stormTrackLayer);
            }

            // Current position marker (triangle if TVS detected, else circle)
            var markerLayer;
            if (storm.tvs) {
                markerLayer = L.marker(pos, {
                    icon: L.divIcon({
                        className: "storm-marker storm-marker-tvs",
                        html: '<svg viewBox="0 0 16 14" width="16" height="14"><path d="M0 0 L16 0 L8 14 Z" fill="#ff8800" stroke="#ff6600" stroke-width="2"/></svg>',
                        iconSize: [16, 14],
                        iconAnchor: [8, 14],
                    }),
                    pane: stormTrackPane,
                });
            } else {
                markerLayer = L.circleMarker(pos, {
                    radius: 7,
                    color: "#ff6600",
                    fillColor: "#ff8800",
                    fillOpacity: 0.85,
                    weight: 2,
                    className: "storm-marker",
                    pane: stormTrackPane,
                });
            }
            markerLayer.addTo(stormTrackLayer).bindPopup(function () {
                return buildStormPopup(storm);
            }, { className: "storm-popup" });

            // Storm ID label
            L.marker(pos, {
                icon: L.divIcon({
                    className: "storm-label",
                    html: '<span class="storm-label-text">' + storm.id + "</span>",
                    iconSize: [28, 16],
                    iconAnchor: [-6, 8],
                }),
                interactive: false,
                pane: stormTrackPane,
            }).addTo(stormTrackLayer);
        });

        if (!dom.map.hasLayer(stormTrackLayer)) {
            stormTrackLayer.addTo(dom.map);
        }
    }

    function buildStormPopup(storm) {
        var lines = ['<div class="storm-popup-content">'];
        lines.push('<div class="storm-popup-id">Storm ' + storm.id + "</div>");
        if (storm.motion_speed_kts != null && storm.motion_dir_deg != null) {
            lines.push(
                "<div>Motion: " + storm.motion_dir_deg + "&deg; at " +
                storm.motion_speed_kts + " kts</div>"
            );
        }
        if (storm.lat != null && storm.lon != null) {
            lines.push(
                "<div>Position: " + storm.lat.toFixed(2) + "&deg;N, " +
                Math.abs(storm.lon).toFixed(2) + "&deg;" + (storm.lon < 0 ? "W" : "E") +
                "</div>"
            );
        }
        if (storm.azimuth_deg != null && storm.range_nm != null) {
            lines.push(
                "<div>From radar: " + storm.azimuth_deg + "&deg;, " +
                storm.range_nm + " nm</div>"
            );
        }
        lines.push("<div>TVS: " + (storm.tvs ? "detected" : "—") + "</div>");
        lines.push("</div>");
        return lines.join("");
    }

    async function loadStormTracksForCurrentFrame() {
        if (!state.showStormTracks) return;
        if (!state.selectedStation) return;

        var meta = null;
        var total = getTotalFrameCount();
        if (state.framesReady && total > 0) {
            var frame = getFrameAt(state.animFrameIndex);
            if (frame && frame.polarData) meta = frame.polarData;
        }
        if (!meta) meta = state.lastRadarMeta;
        if (!meta || !meta.date || !meta.time) return;

        var token = ++state.stormTrackFetchToken;
        var data = await fetchStormTracks(
            state.selectedStation.id,
            meta.date,
            meta.time
        );
        if (token !== state.stormTrackFetchToken) return;
        renderStormTracks(data);
    }

    function initSidebar() {
        dom.sidebarToggle.addEventListener("click", toggleSidebar);
        dom.mobileSidebarBtn.addEventListener("click", () => {
            dom.sidebar.classList.remove("collapsed");
        });

        if (window.innerWidth < 800) {
            dom.sidebar.classList.add("collapsed");
            setTimeout(invalidateMapSize, 350);
        }

        // Close sidebar on mobile when clicking map
        document.getElementById("mapContainer").addEventListener("click", (e) => {
            if (window.innerWidth <= 768 && !dom.sidebar.classList.contains("collapsed")) {
                if (!dom.sidebar.contains(e.target)) {
                    dom.sidebar.classList.add("collapsed");
                }
            }
        });
    }

    function toggleSidebar() {
        dom.sidebar.classList.toggle("collapsed");
        setTimeout(invalidateMapSize, 350);
    }

    function invalidateMapSize() {
        if (dom.map) {
            dom.map.invalidateSize();
        }
        resizeBasemap();
    }

    function initControls() {
        // Station search
        dom.stationSearch.addEventListener("input", onStationSearchInput);
        dom.stationSearch.addEventListener("focus", onStationSearchFocus);
        dom.stationSearch.addEventListener("keydown", onStationSearchKeydown);
        document.addEventListener("click", (e) => {
            if (!dom.stationSearch.contains(e.target) && !dom.stationDropdown.contains(e.target)) {
                dom.stationDropdown.classList.remove("visible");
            }
        });

        // Date picker
        dom.datePicker.addEventListener("change", onDateChange);

        // Time select
        dom.timeSelect.addEventListener("change", onTimeChange);
        dom.elevationSelect.addEventListener("change", async () => {
            const nextElevation = parseFloat(dom.elevationSelect.value);
            if (!Number.isFinite(nextElevation)) return;
            const prevElevation = getSelectedElevation();
            if (
                Number.isFinite(prevElevation) &&
                Math.abs(prevElevation - nextElevation) < 0.001
            ) {
                return;
            }
            state.selectedElevationByProduct[state.product] = nextElevation;
            const resumeAnimation = state.playing;
            if (state.liveMode) {
                await refreshLiveLatest({ force: true });
            } else {
                if (resumeAnimation) {
                    stopAnim();
                }
                invalidatePreload();
                if (state.selectedScanIndex >= 0) {
                    await loadRadarData();
                    if (resumeAnimation) {
                        await togglePlay();
                    }
                }
            }
        });
        dom.btnLive.addEventListener("click", () => {
            if (satOnlyActive()) {
                setSatelliteLive(!state.liveMode);
                return;
            }
            if (state.mosaicMode) {
                setMosaicLive(!state.liveMode);
                return;
            }
            setLiveModeEnabled(!state.liveMode, { refreshNow: true });
        });
        if (dom.viewSite) {
            dom.viewSite.addEventListener("click", () => {
                if (!state.mosaicMode) return;
                openSiteFromMosaic();
            });
        }
        if (dom.viewMosaic) {
            dom.viewMosaic.addEventListener("click", () => {
                if (state.mosaicMode) return;
                setMosaicMode(true, { fit: true, animateFit: true });
            });
        }

        // Level 2 / Level 3 toggle — show only the selected level's product list
        var l2Panel = document.getElementById("l2Products");
        var l3Panel = document.getElementById("l3Products");
        function setLevelPanelsVisibility(level) {
            if (level === "3") {
                l2Panel.hidden = true;
                l3Panel.hidden = false;
            } else {
                l2Panel.hidden = false;
                l3Panel.hidden = true;
            }
        }
        document.querySelectorAll(".level-btn[data-level]").forEach((btn) => {
            btn.addEventListener("click", () => {
                const level = btn.dataset.level;
                document.querySelectorAll(".level-btn[data-level]").forEach((b) => {
                    b.classList.toggle("active", b.dataset.level === level);
                });
                setLevelPanelsVisibility(level);
                if (level === "3") {
                    var firstL3 = l3Panel.querySelector(".product-btn");
                    if (firstL3) firstL3.click();
                } else {
                    var firstL2 = l2Panel.querySelector(".product-btn");
                    if (firstL2) firstL2.click();
                }
            });
        });
        // Initial sync so only one panel is visible (matches current product / Level 2 default)
        setLevelPanelsVisibility(state.product && isL3Product(state.product) ? "3" : "2");

        // Product buttons
        document.querySelectorAll(".product-btn").forEach((btn) => {
            btn.addEventListener("click", async () => {
                if (state.product === btn.dataset.product) return;
                setActiveProductButton(btn.dataset.product);
                const resumeAnimation = state.playing;
                state.product = btn.dataset.product;
                const switchToken = ++state.productSwitchToken;
                updateLegend();
                if (state.liveMode && isL3Product(state.product)) {
                    setLiveModeEnabled(false, { refreshNow: false });
                }
                if (state.liveMode) {
                    await refreshLiveLatest({ force: true });
                } else {
                    if (resumeAnimation) {
                        stopAnim();
                    }
                    invalidatePreload();
                    if (state.selectedScanIndex >= 0) {
                        await loadRadarData();
                        if (switchToken !== state.productSwitchToken) return;
                        if (resumeAnimation) {
                            await togglePlay();
                        }
                    }
                }
            });
        });

        // Animation
        dom.btnPrev.addEventListener("click", prevScan);
        dom.btnNext.addEventListener("click", nextScan);
        dom.btnPlay.addEventListener("click", togglePlay);
        dom.frameProgress.addEventListener("input", onFrameProgressInput);
        dom.frameProgress.addEventListener("change", onFrameProgressChange);

        // Advanced panel visibility
        const showAdvanced = (() => {
            try {
                return window.localStorage.getItem(ADVANCED_PANEL_STORAGE_KEY) === "1";
            } catch {
                return false;
            }
        })();
        setAdvancedPanelVisible(showAdvanced);
        dom.advancedToggle.addEventListener("click", () => {
            setAdvancedPanelVisible(dom.advancedPanel.hidden);
        });
        const showEvents = (() => {
            try {
                const saved = window.localStorage.getItem(EVENTS_PANEL_STORAGE_KEY);
                return saved == null ? false : saved === "1";
            } catch {
                return false;
            }
        })();
        setEventsPanelVisible(showEvents);
        dom.eventsToggle.addEventListener("click", () => {
            setEventsPanelVisible(!state.eventsExpanded);
        });
        dom.overlayToggle.checked = state.showReferenceOverlay;
        dom.overlayToggle.addEventListener("change", (e) => {
            setReferenceOverlayVisible(e.target.checked);
            updateURL();
        });
        if (dom.stormTracksToggle) {
            dom.stormTracksToggle.checked = state.showStormTracks;
            dom.stormTracksToggle.addEventListener("change", (e) => {
                setStormTracksVisible(e.target.checked);
                updateURL();
            });
        }
        if (dom.lightningToggle) {
            dom.lightningToggle.checked = state.showLightning;
            dom.lightningToggle.addEventListener("change", (e) => {
                setLightningVisible(e.target.checked);
                updateURL();
            });
        }
        syncSatelliteControls();
        dom.satelliteToggle?.addEventListener("change", (e) => {
            setSatelliteVisible(e.target.checked);
            updateURL();
        });
        dom.satModeToggle?.addEventListener("click", (e) => {
            const btn = e.target.closest("[data-sat-mode]");
            if (!btn) return;
            setSatelliteMode(btn.dataset.satMode);
            updateURL();
        });
        dom.satelliteChips?.addEventListener("click", (e) => {
            const btn = e.target.closest("[data-sat-product]");
            if (!btn) return;
            setSatelliteProduct(btn.dataset.satProduct);
            updateURL();
        });
        dom.satelliteProduct?.addEventListener("change", (e) => {
            setSatelliteProduct(e.target.value);
            updateURL();
        });
        dom.satSectorToggle?.addEventListener("click", (e) => {
            const btn = e.target.closest("[data-sat-sector]");
            if (!btn) return;
            setSatelliteSector(btn.dataset.satSector);
            updateURL();
        });
        dom.satGoesToggle?.addEventListener("click", (e) => {
            const btn = e.target.closest("[data-sat-goes]");
            if (!btn) return;
            setSatelliteGoes(btn.dataset.satGoes);
            updateURL();
        });
        dom.satHoldToggle?.addEventListener("change", (e) => {
            setSatelliteHold(e.target.checked);
            updateURL();
        });
        dom.satelliteOpacity?.addEventListener("input", (e) => {
            const pct = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
            state.satOpacity = pct / 100;
            if (dom.satelliteOpacityValue) dom.satelliteOpacityValue.textContent = `${pct}%`;
            if (satelliteLayer && !satOnlyActive()) satelliteLayer.setOpacity(state.satOpacity);
        });
        if (dom.alertsToggle) {
            dom.alertsToggle.checked = state.showAlerts;
            dom.alertsToggle.addEventListener("change", (e) => {
                setAlertsVisible(e.target.checked);
                updateURL();
            });
        }
        document.addEventListener("visibilitychange", onVisibilityChange);

        // Speed slider
        state.animSpeed = sliderValueToIntervalMs(parseInt(dom.speedSlider.value, 10));

        dom.speedSlider.addEventListener("input", (e) => {
            state.animSpeed = sliderValueToIntervalMs(parseInt(e.target.value, 10));
            state.animEndPauseTicksRemaining = 0;
            if (state.playing) {
                clearInterval(state.animInterval);
                state.animInterval = setInterval(currentAnimTick(), state.animSpeed);
            }
            updateURL();
        });

        // Opacity slider
        state.radarOpacity = parseInt(dom.opacitySlider.value, 10) / 100;
        dom.opacityValue.textContent = `${Math.round(state.radarOpacity * 100)}%`;
        dom.opacitySlider.addEventListener("input", (e) => {
            const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10)));
            state.radarOpacity = v / 100;
            dom.opacityValue.textContent = `${v}%`;
            if (radarOverlay && radarOverlay.setOpacity) {
                radarOverlay.setOpacity(effectiveRadarOpacity());
            }
            if (mosaicLayer && mosaicLayer.setOpacity) {
                mosaicLayer.setOpacity(state.radarOpacity);
            }
            updateURL();
        });

        // Frames slider. In national mode this is the length of the 5-minute loop.
        dom.framesSlider.addEventListener("input", (e) => {
            state.frameCount = parseInt(e.target.value, 10);
            updateFramesLabel();
            if (satOnlyActive()) {
                // Satellite only: the loop is re-listed once the slider settles.
                if (state.playing || state.satPreloading) stopAnim();
                clearTimeout(state.satFramesTimer);
                state.satFramesTimer = setTimeout(() => {
                    state.satFramesTimer = null;
                    if (satOnlyActive()) syncSatClock();
                }, 250);
                return;
            }
            if (state.mosaicMode) {
                if (state.playing && Number.isFinite(state.mosaicLoopAnchorMs)) {
                    const start = loopStartMs(state.mosaicLoopAnchorMs, state.frameCount, MOSAIC_STEP_MS);
                    if (state.mosaicTimeMs < start) state.mosaicTimeMs = start;
                    if (state.mosaicTimeMs > state.mosaicLoopAnchorMs) {
                        state.mosaicTimeMs = state.mosaicLoopAnchorMs;
                    }
                    if (state.showSatellite) queueSatelliteLoop(currentLoopTimes());
                    refreshMosaic();
                }
                return;
            }
            if (state.playing || state.framesReady) {
                stopAnim();
                invalidatePreload();
            }
        });

        // Share
        dom.btnShare.addEventListener("click", shareView);
        dom.btnShareShort.addEventListener("click", shareShortView);
        if (dom.btnExportKmz) dom.btnExportKmz.addEventListener("click", exportKmz);
        dom.btnAbout.addEventListener("click", openAboutModal);
        dom.aboutBackdrop.addEventListener("click", closeAboutModal);
        dom.aboutClose.addEventListener("click", closeAboutModal);
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && !dom.aboutModal.hidden) {
                closeAboutModal();
            }
        });
        dom.timeSelect.disabled = state.liveMode || dom.timeSelect.disabled;
        updateLiveButtonState();
        updateFrameCycleVisibility();
        updateFrameProgressUI();
    }

    function currentAnimTick() {
        if (satOnlyActive()) return satAnimTick;
        return state.mosaicMode ? mosaicAnimTick : animTick;
    }

    function initLegend() {
        updateLegend();
    }

    function setActiveProductButton(product) {
        document.querySelectorAll(".product-btn").forEach((b) => {
            b.classList.toggle("active", b.dataset.product === product);
        });
        // Sync level toggle to match the product's level
        var isL3 = isL3Product(product);
        document.querySelectorAll(".level-btn[data-level]").forEach((b) => {
            b.classList.toggle("active", b.dataset.level === (isL3 ? "3" : "2"));
        });
        var l2Panel = document.getElementById("l2Products");
        var l3Panel = document.getElementById("l3Products");
        if (l2Panel && l3Panel) {
            l2Panel.hidden = isL3;
            l3Panel.hidden = !isL3;
        }
    }

    function formatProductLabel(product) {
        if (product === "reflectivity") return "Reflectivity";
        if (product === "velocity") return "Velocity";
        if (product === "spectrum_width") return "Spectrum Width";
        if (product === "correlation_coefficient") return "Correlation Coefficient";
        if (product === "differential_reflectivity") return "Differential Reflectivity";
        if (product === "l3_reflectivity") return "L3 Reflectivity";
        if (product === "l3_velocity") return "L3 Velocity";
        if (product === "l3_echo_tops") return "Echo Tops";
        if (product === "l3_vil") return "VIL";
        if (product === "l3_hydrometeor") return "Precip Type";
        if (product === "l3_hybrid_hca") return "Hybrid HCA";
        if (product === "l3_zdr") return "L3 Diff. Refl.";
        if (product === "l3_cc") return "L3 Corr. Coef.";
        if (product === "l3_kdp") return "KDP";
        return product || "";
    }

    function updateLegend(productName) {
        const product = satOnlyActive() ? `sat_${state.satProduct}` : (productName || state.product);
        const config = PRODUCT_LEGEND_CONFIG[product] || (satOnlyActive() ? null : PRODUCT_LEGEND_CONFIG.reflectivity);
        if (!config) {
            setColorScaleVisible(false);
            return;
        }
        const items = config.items || [];
        const colors = items.map((i) => i.color).slice().reverse();

        let gradientCss = "none";
        if (colors.length > 0) {
            const stops = colors.length === 1
                ? `${colors[0]} 0%, ${colors[0]} 100%`
                : colors
                    .map((c, i) => `${c} ${((i / (colors.length - 1)) * 100).toFixed(1)}%`)
                    .join(", ");
            gradientCss = `linear-gradient(to top, ${stops})`;
        }

        if (dom.colorScaleBar) {
            dom.colorScaleBar.style.background = gradientCss;
            dom.colorScaleBar.setAttribute("aria-label", `${config.label} color scale`);
        }

        if (dom.colorScaleTicks) {
            const ticks = config.ticks || [];
            dom.colorScaleTicks.innerHTML = ticks
                .map((t) => `<span class="color-scale-tick">${t}</span>`)
                .join("");
        }

        if (dom.colorScaleOverlay) {
            // Lets alternative legend renderers (src/themes/legend.js) redraw from the same data.
            const detail = { product, label: config.label, code: config.code, items, ticks: config.ticks || [] };
            dom.colorScaleOverlay.legend = detail;
            dom.colorScaleOverlay.dispatchEvent(new CustomEvent("legendchange", { detail }));
        }
    }

    function setColorScaleVisible(visible) {
        // Satellite only shows a scale for the IR and water vapour bands, and nothing else.
        const show = satOnlyActive() ? !!PRODUCT_LEGEND_CONFIG[`sat_${state.satProduct}`] : !!visible;
        if (dom.colorScaleOverlay) {
            dom.colorScaleOverlay.classList.toggle("visible", show);
        }
    }

    function setAdvancedPanelVisible(show) {
        dom.advancedPanel.hidden = !show;
        dom.advancedToggle.setAttribute("aria-expanded", show ? "true" : "false");
        dom.advancedToggle.textContent = show
            ? "Hide Advanced Controls"
            : "Show Advanced Controls";
        try {
            window.localStorage.setItem(ADVANCED_PANEL_STORAGE_KEY, show ? "1" : "0");
        } catch {
            // Ignore storage failures (private mode, disabled storage, etc).
        }
    }

    function setEventsPanelVisible(show) {
        state.eventsExpanded = !!show;
        if (dom.eventsContainer) {
            dom.eventsContainer.hidden = !state.eventsExpanded;
        }
        if (dom.eventsToggle) {
            dom.eventsToggle.setAttribute("aria-expanded", state.eventsExpanded ? "true" : "false");
            dom.eventsToggle.textContent = state.eventsExpanded
                ? "Hide Interesting Events"
                : "Show Interesting Events";
        }
        try {
            window.localStorage.setItem(EVENTS_PANEL_STORAGE_KEY, state.eventsExpanded ? "1" : "0");
        } catch {
            // Ignore storage failures (private mode, disabled storage, etc).
        }
    }

    function getTotalFrameCount(list) {
        const frames = list || state.polarFrames;
        if (!frames || frames.length === 0) return 0;
        return frames.reduce(function (n, slot) {
            return n + (Array.isArray(slot) ? slot.length : slot ? 1 : 0);
        }, 0);
    }

    function getFrameAt(globalIdx, list) {
        const frames = list || state.polarFrames;
        if (!frames || globalIdx < 0) return null;
        let idx = 0;
        for (let s = 0; s < frames.length; s++) {
            const slot = frames[s];
            const len = Array.isArray(slot) ? slot.length : slot ? 1 : 0;
            if (globalIdx < idx + len) {
                return Array.isArray(slot) ? slot[globalIdx - idx] : slot;
            }
            idx += len;
        }
        return null;
    }

    /**
     * Swap in a rebuilt frame list. While playing, callers defer this until
     * the loop wraps so newly loaded (typically older) frames are not
     * prepended under the current index.
     */
    function commitPolarFrames(nextFrames, options = {}) {
        const current = options.resetToStart ? null : getFrameAt(state.animFrameIndex);
        state.polarFrames = nextFrames || [];
        state.pendingPolarFrames = null;
        if (options.resetToStart) {
            state.animFrameIndex = 0;
        } else if (current) {
            const total = getTotalFrameCount();
            let found = -1;
            for (let i = 0; i < total; i++) {
                if (getFrameAt(i) === current) {
                    found = i;
                    break;
                }
            }
            state.animFrameIndex = found >= 0
                ? found
                : Math.max(0, Math.min(state.animFrameIndex, Math.max(total - 1, 0)));
        }
        updateFrameProgressUI();
    }

    function getFrameProgressBounds() {
        const total = getTotalFrameCount();
        if (state.framesReady && total > 0) {
            return {
                total,
                index: Math.max(0, Math.min(state.animFrameIndex, total - 1)),
            };
        }
        if (state.scans.length > 0) {
            const idx = state.selectedScanIndex >= 0 ? state.selectedScanIndex : 0;
            return {
                total: state.scans.length,
                index: Math.max(0, Math.min(idx, state.scans.length - 1)),
            };
        }
        return { total: 0, index: 0 };
    }

    function updateFrameProgressUI() {
        if (satOnlyActive()) {
            const count = state.satClock.length;
            const current = Math.max(0, Math.min(state.satClockIndex, count - 1));
            dom.frameProgress.min = "0";
            dom.frameProgress.max = String(Math.max(count - 1, 0));
            dom.frameProgress.value = String(current);
            dom.frameProgress.disabled = count === 0;
            dom.frameProgressLabel.textContent = satClockLabel(current);
            return;
        }
        const satProgress = satLoopProgressText(state.preloading);
        if (state.mosaicMode && Number.isFinite(state.mosaicTimeMs)) {
            const slot = mosaicSlotIndex(state.mosaicTimeMs);
            dom.frameProgress.min = "0";
            dom.frameProgress.max = String(MOSAIC_SLOTS - 1);
            dom.frameProgress.value = String(slot);
            dom.frameProgress.disabled = false;
            const mosaicLabel = `${formatMosaicClock(state.mosaicTimeMs)} · national composite`;
            dom.frameProgressLabel.textContent = satProgress ? `${mosaicLabel} · ${satProgress}` : mosaicLabel;
            return;
        }
        const { total, index } = getFrameProgressBounds();
        dom.frameProgress.min = "0";
        dom.frameProgress.max = String(Math.max(total - 1, 0));
        dom.frameProgress.value = String(index);
        dom.frameProgress.disabled = total === 0;
        const loop = state.radarLoopProgress;
        let label;
        if (loop) label = `Radar ${loop.loaded}/${loop.total}`;
        else label = total > 0 ? `Frame ${index + 1} / ${total}` : "Frame 0 / 0";
        dom.frameProgressLabel.textContent = satProgress ? `${label} · ${satProgress}` : label;
    }

    function onFrameProgressInput() {
        if (satOnlyActive()) {
            const idx = parseInt(dom.frameProgress.value, 10);
            if (Number.isFinite(idx)) dom.frameProgressLabel.textContent = satClockLabel(idx);
            return;
        }
        if (state.mosaicMode && Number.isFinite(state.mosaicTimeMs)) {
            const slot = parseInt(dom.frameProgress.value, 10);
            if (!Number.isFinite(slot)) return;
            const date = formatMosaicTimeParam(state.mosaicTimeMs).slice(0, 10);
            dom.frameProgressLabel.textContent = `${formatMosaicClock(mosaicMsFromSlot(date, slot))} · national composite`;
            return;
        }
        updateFrameProgressUI();
    }

    function setFrameLoadStatus(text) {
        state.frameLoadStatusText = text || "";
        if (!state.radarDataLoadInProgress) {
            if (state.frameLoadStatusText || !state.preloading) {
                dom.btnPlay.classList.toggle("loading", !!state.frameLoadStatusText);
            }
        }
        updateFrameProgressUI();
    }

    function onFrameProgressChange() {
        const idx = parseInt(dom.frameProgress.value, 10);
        if (!Number.isFinite(idx)) return;
        if (satOnlyActive()) {
            if (state.playing || state.satPreloading) stopAnim();
            showSatClockFrame(Math.max(0, Math.min(idx, state.satClock.length - 1)));
            ensureSatLiveTimer();
            return;
        }
        if (state.mosaicMode) {
            if (state.playing) stopAnim();
            pinMosaicTime();
            setMosaicSlot(idx);
            return;
        }

        const total = getTotalFrameCount();
        if (state.framesReady && total > 0) {
            state.animEndPauseTicksRemaining = 0;
            const bounded = Math.max(0, Math.min(idx, total - 1));
            showFrame(bounded);
            return;
        }

        if (state.scans.length > 0) {
            const bounded = Math.max(0, Math.min(idx, state.scans.length - 1));
            if (bounded !== state.selectedScanIndex) {
                stopAnim();
                invalidatePreload();
                state.selectedScanIndex = bounded;
                dom.timeSelect.value = String(bounded);
                loadRadarData();
            } else {
                updateFrameProgressUI();
            }
        }
    }

    function initKeyboard() {
        document.addEventListener("keydown", (e) => {
            if (e.target.tagName === "INPUT" || e.target.tagName === "SELECT") return;

            switch (e.key) {
                case " ":
                    e.preventDefault();
                    togglePlay();
                    break;
                case "ArrowLeft":
                    e.preventDefault();
                    prevScan();
                    break;
                case "ArrowRight":
                    e.preventDefault();
                    nextScan();
                    break;
            }
        });
    }

    // ── Station Management ────────────────────────────────────

    async function readResponseJsonSafe(resp) {
        const text = await resp.text();
        if (!text) return null;
        try {
            return JSON.parse(text);
        } catch {
            return null;
        }
    }

    function fallbackStationId() {
        if (state.stations.some((s) => s.id === DEFAULT_STATION_ID)) return DEFAULT_STATION_ID;
        return state.stations[0]?.id || null;
    }

    function requestUserPosition({ timeoutMs = 8000 } = {}) {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error("Geolocation unavailable"));
                return;
            }
            navigator.geolocation.getCurrentPosition(
                (pos) => resolve({
                    lat: pos.coords.latitude,
                    lon: pos.coords.longitude,
                }),
                (err) => reject(err),
                {
                    enableHighAccuracy: false,
                    timeout: timeoutMs,
                    maximumAge: 15 * 60 * 1000,
                },
            );
        });
    }

    function trySelectInitialStation() {
        if (state.mosaicMode) {
            state.initialStationSelected = true;
            return;
        }
        if (state.initialStationSelected || state.stations.length === 0) return;
        const requestedId = state.pendingInitialStationId
            ? String(state.pendingInitialStationId).toUpperCase()
            : null;
        if (requestedId && state.stations.some((s) => s.id === requestedId)) {
            selectStation(requestedId);
            return;
        }
        resolveStationFromLocation();
    }

    async function resolveStationFromLocation() {
        if (state.initialStationSelected || state.geoLocateInFlight) return;
        state.geoLocateInFlight = true;
        if (dom.stationInfo) {
            dom.stationInfo.textContent = "Finding nearest radar…";
        }
        try {
            const pos = await requestUserPosition();
            if (state.initialStationSelected) return;
            const closest = findClosestStation(state.stations, pos.lat, pos.lon);
            if (closest?.id) {
                selectStation(closest.id);
                return;
            }
        } catch {
            // Permission denied, timeout, or unsupported — use the fallback site.
        } finally {
            state.geoLocateInFlight = false;
        }
        if (state.initialStationSelected) return;
        const fallback = fallbackStationId();
        if (fallback) selectStation(fallback);
    }

    async function loadStations() {
        try {
            const data = getStationList();
            if (!Array.isArray(data) || data.length === 0) {
                throw new Error("Station list was empty");
            }
            state.stations = data;
            plotStationMarkers();
            trySelectInitialStation();
        } catch (err) {
            console.error("Failed to load stations:", err);
            state.stations = [];
        }
    }

    function plotStationMarkers() {
        stationMarkers.clearLayers();

        state.stations.forEach((station) => {
            const marker = L.circleMarker([station.lat, station.lon], {
                radius: 4,
                color: "rgba(90, 169, 255, 0.6)",
                fillColor: "rgba(90, 169, 255, 0.3)",
                fillOpacity: 1,
                weight: 1,
            });
            // Keep visual marker size the same, but use a larger invisible hit area.
            const hitMarker = L.circleMarker([station.lat, station.lon], {
                radius: 12, // 3x clickable radius
                stroke: false,
                fill: true,
                fillColor: "rgba(0,0,0,0)",
                fillOpacity: 0,
                interactive: true,
            });

            const popupHtml = `<div>
                    <div class="station-popup-title">${station.id}</div>
                    <div class="station-popup-name">${station.name}, ${station.state}</div>
                    <button class="station-popup-btn" onclick="window._selectStation('${station.id}')">
                        Select Station
                    </button>
                </div>`;

            marker.bindPopup(popupHtml, { className: "dark-popup" });
            hitMarker.bindPopup(popupHtml, { className: "dark-popup" });

            function applyHoverStyle() {
                marker.setStyle({ radius: 7, color: "#5aa9ff", fillOpacity: 0.8 });
            }
            function applyBaseStyle() {
                marker.setStyle({ radius: 4, color: "rgba(90, 169, 255, 0.6)", fillOpacity: 1 });
            }

            marker.on("mouseover", applyHoverStyle);
            marker.on("mouseout", applyBaseStyle);
            hitMarker.on("mouseover", applyHoverStyle);
            hitMarker.on("mouseout", applyBaseStyle);

            stationMarkers.addLayer(hitMarker);
            stationMarkers.addLayer(marker);
        });
    }

    // Expose to popup onclick
    window._selectStation = function (stationId) {
        selectStation(stationId);
        dom.map.closePopup();
    };

    function selectStation(stationId) {
        const station = state.stations.find((s) => s.id === stationId);
        if (!station) return;
        if (state.mosaicMode) setMosaicMode(false, { restoreRadar: false });
        state.initialStationSelected = true;

        stopAnim();
        abortLightningFetch();
        resetSatelliteRequests();
        invalidatePreload();
        state.stormTrackCache = {};
        clearStormTracks();
        if (state.liveMode) {
            startLivePolling();
        }

        state.selectedStation = station;
        if (state.initialRain) {
            const initialRain = state.initialRain;
            state.pendingRain = initialRain;
            state.initialRain = null;
            setTimeout(() => rainPanel?.restore(initialRain), 0);
        } else if (rainPanel?.active) {
            rainPanel.exit();
        }
        dom.stationSearch.value = `${station.id} - ${station.name}, ${station.state}`;
        dom.stationDropdown.classList.remove("visible");
        dom.stationInfo.innerHTML = `<span class="active">${station.id}</span> &middot; ${station.lat.toFixed(3)}°N, ${Math.abs(station.lon).toFixed(3)}°W`;

        if (state.initialUrlMap) {
            // On initial page load, preserve URL map view instead of forcing station zoom.
            dom.map.setView(
                [state.initialUrlMap.lat, state.initialUrlMap.lon],
                state.initialUrlMap.zoom,
                { animate: false }
            );
            state.initialUrlMap = null;
        } else {
            dom.map.setView([station.lat, station.lon], 7, { animate: true });
        }
        updateURL();
        loadInterestingEvents(station.id);

        if (state.date) {
            loadScans();
        }
    }

    async function loadInterestingEvents(stationId) {
        state.eventsLoading = true;
        state.interestingEvents = [];
        renderInterestingEvents();

        try {
            const station = state.stations.find((s) => s.id === stationId) || state.selectedStation;
            if (!station) throw new Error("Station not found");
            const data = await getStationEvents(station, { limit: 12, years: 20 });
            state.interestingEvents = data.events || [];
            if (state.interestingEvents.length === 0) {
                dom.eventsStatus.textContent = data.message || "No interesting events available.";
            }
        } catch (err) {
            console.error("Failed to load interesting events:", err);
            dom.eventsStatus.textContent = `Events unavailable: ${err.message}`;
            state.interestingEvents = [];
        } finally {
            state.eventsLoading = false;
            renderInterestingEvents();
        }
    }

    function renderInterestingEvents() {
        if (!dom.eventsList || !dom.eventsStatus) return;

        if (!state.selectedStation) {
            dom.eventsStatus.textContent = "Select a station to load event highlights.";
            dom.eventsList.innerHTML = "";
            return;
        }

        if (state.eventsLoading) {
            dom.eventsStatus.textContent = "Loading interesting events...";
            dom.eventsList.innerHTML = "";
            return;
        }

        if (!state.interestingEvents.length) {
            if (!dom.eventsStatus.textContent) {
                dom.eventsStatus.textContent = "No interesting events found.";
            }
            dom.eventsList.innerHTML = "";
            return;
        }

        dom.eventsStatus.textContent = `Top ${state.interestingEvents.length} events in last 20 years`;
        dom.eventsList.innerHTML = state.interestingEvents.map((event, idx) => {
            const date = event.date || "";
            const y = date.slice(0, 4);
            const m = date.slice(4, 6);
            const d = date.slice(6, 8);
            const t = event.time || "";
            const timeFmt = `${t.slice(0, 2)}:${t.slice(2, 4)}:${t.slice(4, 6)} UTC`;
            return `
                <button class="event-item" data-event-index="${idx}">
                    <div class="event-item-title">${event.title || event.event_type || "Event"}</div>
                    <div class="event-item-meta">${y}-${m}-${d} ${timeFmt} • Score ${Math.round(event.score || 0)}</div>
                </button>
            `;
        }).join("");

        dom.eventsList.querySelectorAll(".event-item").forEach((btn) => {
            btn.addEventListener("click", () => {
                const idx = parseInt(btn.dataset.eventIndex, 10);
                const event = state.interestingEvents[idx];
                if (event) {
                    jumpToInterestingEvent(event);
                }
            });
        });
    }

    function jumpToInterestingEvent(event) {
        if (!state.selectedStation) return;
        const date = event.date || "";
        const time = event.time || "";
        if (date.length !== 8 || time.length < 4) return;

        stopAnim();
        invalidatePreload();
        setLiveModeEnabled(false, { refreshNow: false });
        dom.timeSelect.disabled = false;

        const formattedDate = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`;
        state.date = formattedDate;
        dom.datePicker.value = formattedDate;
        state.initialUrlTime = time;
        updateURL();
        loadScans();
    }

    // ── Station Search / Dropdown ─────────────────────────────

    let dropdownHighlight = -1;

    function onStationSearchInput() {
        const query = dom.stationSearch.value.toLowerCase().trim();
        if (!query) {
            dom.stationDropdown.classList.remove("visible");
            return;
        }

        const matches = state.stations
            .filter((s) => {
                return (
                    s.id.toLowerCase().includes(query) ||
                    s.name.toLowerCase().includes(query) ||
                    s.state.toLowerCase().includes(query) ||
                    s.label.toLowerCase().includes(query)
                );
            })
            .slice(0, 20);

        if (matches.length === 0) {
            dom.stationDropdown.classList.remove("visible");
            return;
        }

        dropdownHighlight = -1;
        dom.stationDropdown.innerHTML = matches
            .map(
                (s, i) => `
            <div class="station-option" data-index="${i}" data-id="${s.id}">
                <span class="station-id">${s.id}</span>
                <span class="station-name">${s.name}, ${s.state}</span>
            </div>`
            )
            .join("");

        dom.stationDropdown.querySelectorAll(".station-option").forEach((opt) => {
            opt.addEventListener("click", () => selectStation(opt.dataset.id));
        });

        dom.stationDropdown.classList.add("visible");
    }

    function onStationSearchFocus() {
        if (dom.stationSearch.value.trim()) {
            onStationSearchInput();
        }
    }

    function onStationSearchKeydown(e) {
        const options = dom.stationDropdown.querySelectorAll(".station-option");
        if (!options.length) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            dropdownHighlight = Math.min(dropdownHighlight + 1, options.length - 1);
            updateDropdownHighlight(options);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            dropdownHighlight = Math.max(dropdownHighlight - 1, 0);
            updateDropdownHighlight(options);
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (dropdownHighlight >= 0 && options[dropdownHighlight]) {
                selectStation(options[dropdownHighlight].dataset.id);
            }
        } else if (e.key === "Escape") {
            dom.stationDropdown.classList.remove("visible");
        }
    }

    function updateDropdownHighlight(options) {
        options.forEach((o, i) => {
            o.classList.toggle("highlighted", i === dropdownHighlight);
        });
        if (dropdownHighlight >= 0 && options[dropdownHighlight]) {
            options[dropdownHighlight].scrollIntoView({ block: "nearest" });
        }
    }

    function setLiveModeEnabled(enabled, options = {}) {
        const refreshNow = options.refreshNow !== false;
        state.liveMode = !!enabled;
        dom.timeSelect.disabled = state.liveMode || state.scans.length === 0;
        dom.timeControls.classList.toggle("live-mode", state.liveMode);
        dom.datePicker.hidden = state.liveMode;
        dom.dateLiveHint.hidden = !state.liveMode;
        dom.timeGroup.hidden = state.liveMode;
        updateLiveButtonState();

        if (state.liveMode) {
            startLivePolling();
            if (refreshNow && state.selectedStation) {
                refreshLiveLatest();
            }
        } else {
            stopLivePolling();
            if (refreshNow && state.selectedStation) {
                loadScans();
            }
        }
        updateURL();
    }

    function startLivePolling() {
        scheduleNextLivePoll();
    }

    function stopLivePolling() {
        if (state.livePollTimer) {
            clearTimeout(state.livePollTimer);
            state.livePollTimer = null;
        }
    }

    function scheduleNextLivePoll(delayMs) {
        // Satellite only runs its own clock; the radar list is not polled underneath it.
        if (!state.liveMode || satOnlyActive()) return;
        stopLivePolling();
        const predictedDelay = Number.isFinite(delayMs)
            ? delayMs
            : estimateNextLivePollDelayMs();
        const waitMs = Math.max(
            LIVE_MIN_POLL_DELAY_MS,
            Math.min(LIVE_MAX_POLL_DELAY_MS, predictedDelay)
        );
        state.livePollTimer = setTimeout(async () => {
            state.livePollTimer = null;
            await refreshLiveLatest();
        }, waitMs);
    }

    async function refreshLiveLatest(options = {}) {
        const force = options.force === true;
        if (!state.liveMode || !state.selectedStation) return;
        const switchToken = state.productSwitchToken;
        const liveLatest = await fetchLatestScanMetaForLive();
        if (!liveLatest) {
            startLivePolling();
            return;
        }

        const latestExistingIdx = state.scans.length
            ? getLatestScanIndexAcrossDates(state.scans)
            : -1;
        const currentScan = latestExistingIdx >= 0 ? state.scans[latestExistingIdx] : null;
        const currentTime = currentScan ? currentScan.time : null;
        const currentDate = currentScan && currentScan.scanDate ? currentScan.scanDate : state.date;
        const unchanged = (
            currentDate === liveLatest.date &&
            currentTime === liveLatest.time
        );
        let liveSweepsChanged = false;
        if (unchanged && !force && !isL3Product(state.product)) {
            const live = await fetchLiveChunkFrames();
            if (switchToken !== state.productSwitchToken || !state.liveMode) return;
            liveSweepsChanged = liveChunkFramesKey(live) !== state.liveChunkShownKey;
        }
        if (unchanged && !force && !liveSweepsChanged) {
            if (state.lastRadarMeta) {
                updateInfoBar(state.lastRadarMeta);
            }
            startLivePolling();
            return;
        }

        const resumePlayback = state.playing;
        if (resumePlayback) {
            stopAnim();
            invalidatePreload();
        }
        await loadScans();
        if (switchToken !== state.productSwitchToken) return;
        if (resumePlayback && state.scans.length > 0) {
            await togglePlay();
        }
        startLivePolling();
    }

    function updateLiveButtonState() {
        dom.btnLive.classList.toggle("active", state.liveMode);
    }

    function updateFrameCycleVisibility() {
        dom.frameCycle.hidden = false;
    }

    function onVisibilityChange() {
        if (!state.liveMode) return;
        if (document.hidden) {
            state.hiddenSinceMs = Date.now();
            stopLivePolling();
            return;
        }

        const hiddenForMs = state.hiddenSinceMs ? Date.now() - state.hiddenSinceMs : 0;
        state.hiddenSinceMs = null;
        startLivePolling();
        if (hiddenForMs >= LIVE_DEFAULT_POLL_INTERVAL_MS) {
            refreshLiveLatest();
        }
    }

    async function fetchScansForDate(candidateDate) {
        if (!state.selectedStation) return null;
        const dateClean = candidateDate.replace(/-/g, "");
        try {
            const data = await listL2Scans(state.selectedStation.id, dateClean);
            const scans = Array.isArray(data.scans) ? data.scans : [];
            return { date: candidateDate, scans };
        } catch (err) {
            console.warn(`Scan fetch failed for ${candidateDate}:`, err);
            return null;
        }
    }

    async function fetchLatestScanMetaForLive() {
        if (!state.selectedStation) return null;
        const dateCandidates = getRecentDateCandidatesUTC();
        const fetchResults = await Promise.all(
            dateCandidates.map((candidateDate) => fetchScansForDate(candidateDate))
        );
        for (let i = 0; i < dateCandidates.length; i++) {
            const result = fetchResults[i];
            if (!result || !result.scans.length) continue;
            const latestIdx = getLatestScanIndex(result.scans);
            return {
                date: result.date,
                time: result.scans[latestIdx].time,
            };
        }
        return null;
    }

    async function fetchScansForDatesInBatches(dateCandidates) {
        const results = new Array(dateCandidates.length).fill(null);
        for (let start = 0; start < dateCandidates.length; start += SCAN_DATE_FETCH_CONCURRENCY) {
            const batchDates = dateCandidates.slice(start, start + SCAN_DATE_FETCH_CONCURRENCY);
            const batchResults = await Promise.all(
                batchDates.map((candidateDate) => fetchScansForDate(candidateDate))
            );
            for (let i = 0; i < batchResults.length; i++) {
                results[start + i] = batchResults[i];
            }
        }
        return results;
    }

    function estimateNextLivePollDelayMs() {
        const latestIdx = getLatestScanIndexAcrossDates(state.scans || []);
        const latest = state.scans[latestIdx];
        if (!latest) return LIVE_DEFAULT_POLL_INTERVAL_MS;

        const latestDt = parseScanUtcDate(latest.scanDate || state.date, latest.time);
        if (!latestDt) return LIVE_DEFAULT_POLL_INTERVAL_MS;

        // Estimate cadence from the previous scan when available.
        let cadenceMs = LIVE_DEFAULT_POLL_INTERVAL_MS;
        if (latestIdx > 0) {
            const prev = state.scans[latestIdx - 1];
            const prevDt = parseScanUtcDate(prev.scanDate || state.date, prev.time);
            if (prevDt) {
                const diff = latestDt.getTime() - prevDt.getTime();
                if (diff > 0) cadenceMs = diff;
            }
        }

        // Check slightly before the predicted next frame time, bounded by min/max.
        const nextExpectedTs = latestDt.getTime() + cadenceMs;
        const remaining = nextExpectedTs - Date.now() - LIVE_POLL_LEAD_MS;
        const delay = Math.max(LIVE_MIN_POLL_DELAY_MS, remaining);
        return isL3Product(state.product) ? delay : Math.min(delay, LIVE_CHUNK_POLL_MS);
    }

    function latestArchivedScanStartMs() {
        const idx = getLatestScanIndexAcrossDates(state.scans || []);
        const scan = idx >= 0 ? state.scans[idx] : null;
        const dt = scan ? parseScanUtcDate(scan.scanDate || state.date, scan.time) : null;
        return dt ? dt.getTime() : 0;
    }

    function liveChunkFramesKey(live) {
        const newest = live?.frames?.[live.frames.length - 1];
        return newest ? `${newest.date}_${newest.time}_${live.frames.length}` : "";
    }

    /**
     * Complete sweeps (base tilt + SAILS) from the volume being scanned now.
     * Null outside Level 2 live mode, before the newest archived volume, or on error.
     */
    async function fetchLiveChunkFrames(signal) {
        if (!state.liveMode || !state.selectedStation || isL3Product(state.product)) return null;
        const afterMs = latestArchivedScanStartMs();
        if (!afterMs) return null;
        try {
            return await loadL2LiveFrames({
                station: state.selectedStation,
                product: state.product,
                elevation: getSelectedElevation(),
                afterMs,
                signal,
            });
        } catch (err) {
            if (err?.name !== "AbortError") console.warn("Real-time Level 2 chunks unavailable:", err);
            return null;
        }
    }

    function withTimeout(promise, ms) {
        return Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);
    }

    function getSelectedElevation() {
        return state.selectedElevationByProduct[state.product];
    }

    function buildElevationQueryParam() {
        const selected = getSelectedElevation();
        return Number.isFinite(selected) ? `&elevation=${selected}` : "";
    }

    function updateElevationControlsFromPayload(payload) {
        if (isL3Product(state.product)) {
            dom.elevationSelect.innerHTML = '<option value="">N/A (Level 3)</option>';
            dom.elevationSelect.disabled = true;
            state.availableElevations = [];
            return;
        }

        const availableRaw = payload && Array.isArray(payload.available_elevations)
            ? payload.available_elevations
            : [];
        state.availableElevations = availableRaw
            .map((e) => parseFloat(e))
            .filter((e) => Number.isFinite(e))
            .sort((a, b) => a - b);

        let selected = getSelectedElevation();
        if (!Number.isFinite(selected) && state.availableElevations.length > 0) {
            selected = state.availableElevations[0];
            state.selectedElevationByProduct[state.product] = selected;
        } else if (
            Number.isFinite(selected) &&
            state.availableElevations.length > 0 &&
            !state.availableElevations.some((e) => Math.abs(e - selected) < 0.051)
        ) {
            selected = state.availableElevations[0];
            state.selectedElevationByProduct[state.product] = selected;
        }

        const options = [];
        state.availableElevations.forEach((elev) => {
            const value = elev.toFixed(1);
            options.push(`<option value="${value}">${value}\u00b0</option>`);
        });
        dom.elevationSelect.innerHTML = options.join("") || '<option value="">No elevations available</option>';

        if (Number.isFinite(selected) && state.availableElevations.length > 0) {
            const selectedValue = selected.toFixed(1);
            const hasOption = state.availableElevations.some((elev) => elev.toFixed(1) === selectedValue);
            dom.elevationSelect.value = hasOption
                ? selectedValue
                : state.availableElevations[0].toFixed(1);
        }
        dom.elevationSelect.disabled = !state.availableElevations.length;
    }

    // ── National mosaic (IEM N0Q composite) ───────────────────

    function syncViewToggle() {
        if (dom.viewSite) dom.viewSite.classList.toggle("active", !state.mosaicMode);
        if (dom.viewMosaic) dom.viewMosaic.classList.toggle("active", state.mosaicMode);
        if (dom.mosaicHint) dom.mosaicHint.hidden = !state.mosaicMode;
        if (dom.sidebar) dom.sidebar.classList.toggle("mosaic-mode", state.mosaicMode);
        updateFramesLabel();
    }

    function updateFramesLabel() {
        if (!dom.framesValue) return;
        const only = satOnlyActive();
        // Satellite only allows longer loops: thirty Meso scans are half an hour.
        const max = only ? SAT_MAX_FRAMES : RADAR_MAX_FRAMES;
        if (dom.framesSlider.max !== String(max)) {
            dom.framesSlider.max = String(max);
            state.frameCount = Math.min(state.frameCount, max);
            dom.framesSlider.value = String(state.frameCount);
        }
        if (only) dom.framesValue.textContent = formatLoopLabel(state.frameCount, state.satClockStepMs);
        else if (state.mosaicMode) dom.framesValue.textContent = formatLoopLabel(state.frameCount, MOSAIC_STEP_MS);
        else dom.framesValue.textContent = String(state.frameCount);
    }

    function seedMosaicTime() {
        if (Number.isFinite(state.mosaicTimeMs)) return;
        const scan = state.selectedScanIndex >= 0 ? state.scans[state.selectedScanIndex] : null;
        const scanDate = scan && (scan.scanDate || state.date);
        const fromScan = scan?.time ? parseScanUtcDate(scanDate, scan.time) : null;
        if (fromScan) {
            state.mosaicTimeMs = snapMosaicMs(fromScan.getTime());
            return;
        }
        const fromUrl = state.initialUrlTime ? parseScanUtcDate(state.date, state.initialUrlTime) : null;
        if (fromUrl) {
            state.mosaicTimeMs = snapMosaicMs(fromUrl.getTime());
            return;
        }
        if (!state.date || state.date === getTodayUTCDateString()) {
            state.mosaicTimeMs = snapMosaicMs(Date.now() - MOSAIC_LIVE_LAG_MS);
            return;
        }
        const [y, m, d] = state.date.split("-").map(Number);
        state.mosaicTimeMs = snapMosaicMs(Date.UTC(y, m - 1, d, 18, 0, 0));
    }

    function syncMosaicDateControl() {
        const date = formatMosaicTimeParam(state.mosaicTimeMs).slice(0, 10);
        state.date = date;
        if (dom.datePicker && !state.liveMode) dom.datePicker.value = date;
    }

    function ensureMosaicLayer() {
        if (!mosaicLayer) {
            mosaicLayer = createMosaicLayer(L, dom.map, {
                opacity: state.radarOpacity,
                timeMs: state.mosaicTimeMs,
            });
        }
        return mosaicLayer;
    }

    function updateMosaicChrome(covered) {
        syncViewToggle();
        if (dom.mosaicHint) {
            dom.mosaicHint.textContent = covered
                ? "National base reflectivity every 5 minutes, from the Iowa Environmental Mesonet."
                : "The IEM national composite starts 16 February 2011. Pick a later date.";
        }
        renderMosaicInfoBar();
        updateLegend("reflectivity");
        setColorScaleVisible(covered);
        updateFrameProgressUI();
        dom.btnPrev.disabled = false;
        dom.btnPlay.disabled = false;
        dom.btnNext.disabled = false;
    }

    function renderMosaicInfoBar() {
        const covered = mosaicArchiveCovers(state.mosaicTimeMs);
        const clock = formatMosaicClock(state.mosaicTimeMs);
        const dateLabel = formatDate(formatMosaicTimeParam(state.mosaicTimeMs).slice(0, 10));
        dom.mapInfoBar.innerHTML = `
            <span class="info-item"><span class="info-label">View:</span> <span class="info-value">National mosaic</span></span>
            <span class="info-item"><span class="info-label">Time:</span> <span class="info-value">${clock}</span></span>
            <span class="info-item"><span class="info-label">Date:</span> <span class="info-value">${dateLabel}</span></span>
            <span class="info-item"><span class="info-label">Source:</span> <span class="info-value">${covered ? "IEM N0Q" : "before archive"}</span></span>
            ${satInfoHtml()}
        `;
        dom.mapInfoBar.classList.add("visible");
        syncMapDockOffsets();
    }

    function refreshMosaic() {
        if (!state.mosaicMode || !Number.isFinite(state.mosaicTimeMs)) return;
        syncMosaicDateControl();
        const covered = mosaicArchiveCovers(state.mosaicTimeMs);
        if (!covered || satOnlyActive()) {
            if (mosaicLayer && dom.map.hasLayer(mosaicLayer)) dom.map.removeLayer(mosaicLayer);
        } else {
            const layer = ensureMosaicLayer();
            if (!dom.map.hasLayer(layer)) layer.addTo(dom.map);
            layer.setOpacity(state.radarOpacity);
            layer.setMosaicTime(state.mosaicTimeMs);
        }
        if (satOnlyActive()) syncViewToggle();
        else updateMosaicChrome(covered);
        if (state.showSatellite) loadSatelliteForCurrentFrame();
        updateURL();
    }

    function pinMosaicTime() {
        if (!state.liveMode) return;
        state.liveMode = false;
        stopMosaicLiveTimer();
        dom.datePicker.hidden = false;
        dom.dateLiveHint.hidden = true;
        updateLiveButtonState();
    }

    function stopMosaicLiveTimer() {
        if (state.mosaicLiveTimer) {
            clearInterval(state.mosaicLiveTimer);
            state.mosaicLiveTimer = null;
        }
    }

    function jumpMosaicToLatest() {
        state.mosaicTimeMs = snapMosaicMs(Date.now() - MOSAIC_LIVE_LAG_MS);
        refreshMosaic();
    }

    function startMosaicLiveTimer() {
        stopMosaicLiveTimer();
        if (satOnlyActive()) {
            ensureSatLiveTimer();
            return;
        }
        state.mosaicLiveTimer = setInterval(jumpMosaicToLatest, 60 * 1000);
    }

    function setMosaicLive(enabled) {
        if (state.playing) stopAnim();
        state.liveMode = !!enabled;
        dom.datePicker.hidden = state.liveMode;
        dom.dateLiveHint.hidden = !state.liveMode;
        updateLiveButtonState();
        if (state.liveMode) {
            jumpMosaicToLatest();
            startMosaicLiveTimer();
        } else {
            stopMosaicLiveTimer();
            refreshMosaic();
        }
    }

    function setMosaicDate(dateStr) {
        if (!dateStr || !Number.isFinite(state.mosaicTimeMs)) return;
        const [y, m, d] = dateStr.split("-").map(Number);
        const prev = new Date(state.mosaicTimeMs);
        state.mosaicTimeMs = snapMosaicMs(Date.UTC(y, m - 1, d, prev.getUTCHours(), prev.getUTCMinutes()));
        refreshMosaic();
    }

    function setMosaicSlot(slot) {
        if (!Number.isFinite(state.mosaicTimeMs)) return;
        const date = formatMosaicTimeParam(state.mosaicTimeMs).slice(0, 10);
        state.mosaicTimeMs = mosaicMsFromSlot(date, slot);
        refreshMosaic();
    }

    function stepMosaic(delta) {
        const snapped = snapMosaicMs(state.mosaicTimeMs);
        const d = new Date(snapped);
        const dayStart = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
        const dayEnd = dayStart + MOSAIC_SLOTS * MOSAIC_STEP_MS;
        let next = snapped + delta * MOSAIC_STEP_MS;
        if (next < dayStart) next = dayEnd - MOSAIC_STEP_MS;
        else if (next >= dayEnd) next = dayStart;
        state.mosaicTimeMs = next;
        refreshMosaic();
    }

    function mosaicAnimTick() {
        if (!Number.isFinite(state.mosaicLoopAnchorMs)) {
            state.mosaicLoopAnchorMs = snapMosaicMs(state.mosaicTimeMs);
        }
        state.mosaicTimeMs = stepLoop(
            state.mosaicTimeMs,
            state.mosaicLoopAnchorMs,
            state.frameCount,
            MOSAIC_STEP_MS,
        );
        refreshMosaic();
    }

    function fitMosaicView(animate) {
        dom.map.fitBounds(MOSAIC_CONUS_BOUNDS, { padding: [16, 16], animate: !!animate });
    }

    function hideSiteRadar() {
        if (radarOverlay && dom.map.hasLayer(radarOverlay)) dom.map.removeLayer(radarOverlay);
        if (rangeRings && dom.map.hasLayer(rangeRings)) dom.map.removeLayer(rangeRings);
        if (stationDot && dom.map.hasLayer(stationDot)) dom.map.removeLayer(stationDot);
    }

    function restoreSiteRadar() {
        if (radarOverlay && !dom.map.hasLayer(radarOverlay) && !satOnlyActive()) radarOverlay.addTo(dom.map);
        if (state.lastRadarMeta) {
            drawRangeRings(state.lastRadarMeta.lat, state.lastRadarMeta.lon, state.lastRadarMeta.max_range_km);
            updateInfoBar(state.lastRadarMeta);
            updateLegend();
            setColorScaleVisible(true);
        } else {
            dom.mapInfoBar.classList.remove("visible");
            dom.mapInfoBar.innerHTML = "";
            setColorScaleVisible(false);
            updateLegend();
        }
        updateFrameProgressUI();
        if (!state.scans.length) {
            dom.btnPrev.disabled = true;
            dom.btnPlay.disabled = true;
            dom.btnNext.disabled = true;
        }
    }

    function setMosaicMode(on, options = {}) {
        if (on === state.mosaicMode) return;
        resetSatelliteRequests();
        // The other view crops a different area, so the frame on screen no longer fits.
        clearSatellite();
        state.satStatus = "none";
        if (on) {
            stopAnim();
            invalidatePreload();
            if (rainPanel?.active) rainPanel.exit();
            state.mosaicMode = true;
            seedMosaicTime();
            hideSiteRadar();
            syncViewToggle();
            // Fit first so national satellite crops start from the final viewport.
            if (options.fit) fitMosaicView(options.animateFit);
            if (state.liveMode) {
                jumpMosaicToLatest();
                startMosaicLiveTimer();
            } else {
                refreshMosaic();
            }
            return;
        }
        stopAnim();
        stopMosaicLiveTimer();
        state.mosaicMode = false;
        if (mosaicLayer && dom.map.hasLayer(mosaicLayer)) dom.map.removeLayer(mosaicLayer);
        syncViewToggle();
        if (options.restoreRadar !== false) {
            restoreSiteRadar();
            if (state.showSatellite) loadSatelliteForCurrentFrame();
        }
        if (state.liveMode && state.selectedStation) startLivePolling();
        updateURL();
    }

    function openSiteFromMosaic() {
        const center = dom.map.getCenter();
        const station = state.selectedStation
            || findClosestStation(state.stations, center.lat, center.lng);
        if (Number.isFinite(state.mosaicTimeMs) && !state.liveMode) {
            state.initialUrlTime = formatMosaicTimeCompact(state.mosaicTimeMs);
            syncMosaicDateControl();
        }
        if (station) {
            // A shared national view keeps its own map position. Site mode should
            // still fly to the radar instead of reusing that wide zoom.
            state.initialUrlMap = null;
            selectStation(station.id);
            return;
        }
        setMosaicMode(false);
    }

    // ── Date / Time ───────────────────────────────────────────

    function onDateChange() {
        if (state.mosaicMode) {
            if (state.liveMode) {
                dom.datePicker.value = state.date;
                return;
            }
            if (state.playing) stopAnim();
            pinMosaicTime();
            setMosaicDate(dom.datePicker.value);
            return;
        }
        if (state.liveMode) {
            // Keep live mode pinned to latest available scans.
            dom.datePicker.value = state.date;
            return;
        }
        stopAnim();
        resetSatelliteRequests();
        invalidatePreload();
        state.date = dom.datePicker.value;
        updateURL();
        if (state.selectedStation) {
            loadScans();
        }
    }

    async function loadScans() {
        if (!state.selectedStation || !state.date) return;

        const dateCandidates = state.liveMode
            ? getRecentDateCandidatesUTC(3)
            : getRecentDateCandidatesFrom(state.date, 3);
        dom.timeSelect.disabled = true;
        dom.timeSelect.innerHTML = '<option value="">Loading scans...</option>';
        dom.elevationSelect.disabled = true;
        if (!state.availableElevations.length) {
            dom.elevationSelect.innerHTML = '<option value="">Loading elevations...</option>';
        }
        dom.scanCount.textContent = "";
        state.selectedScanIndex = -1;
        updateFrameProgressUI();

        setPlayButtonState("loading");

        try {
            let resolvedDate = null;
            let resolvedScans = [];
            const dateResults = await fetchScansForDatesInBatches(dateCandidates);
            for (const dateResult of dateResults) {
                if (!dateResult) continue;
                const scans = dateResult.scans;
                if (!scans.length) continue;

                if (!resolvedDate) {
                    resolvedDate = dateResult.date;
                }

                const scansWithDate = scans.map((s) => ({ ...s, scanDate: dateResult.date }));

                // Build chronological history ending at latest scan.
                resolvedScans = scansWithDate.concat(resolvedScans);
                if (resolvedScans.length >= state.frameCount) break;
            }

            state.scans = resolvedScans;

            if (state.scans.length === 0) {
                dom.timeSelect.innerHTML = '<option value="">No scans found for this date</option>';
                dom.elevationSelect.disabled = true;
                dom.elevationSelect.innerHTML = '<option value="">No elevations available</option>';
                dom.scanCount.textContent = "";
                updateFrameProgressUI();
                setPlayButtonState("stopped");
                return;
            }

            if (resolvedDate) {
                state.date = resolvedDate;
                dom.datePicker.value = resolvedDate;
            }

            dom.timeSelect.innerHTML = state.scans
                .map((s, i) => {
                    const datePrefix = s.scanDate && s.scanDate !== state.date
                        ? `${s.scanDate} `
                        : "";
                    return `<option value="${i}">${datePrefix}${s.time} UTC</option>`;
                })
                .join("");

            dom.timeSelect.disabled = state.liveMode;
            dom.scanCount.textContent = `${state.scans.length} scans available`;

            // Enable animation controls
            dom.btnPrev.disabled = false;
            dom.btnPlay.disabled = false;
            dom.btnNext.disabled = false;

            // If we have a saved initial URL time, select it
            const urlTime = state.initialUrlTime;
            if (state.liveMode) {
                state.initialUrlTime = null;
            }
            if (urlTime && !state.liveMode) {
                state.initialUrlTime = null;
                const timeMatch = urlTime.replace(/:/g, "");
                const idx = state.scans.findIndex((s) =>
                    s.time.replace(/:/g, "").startsWith(timeMatch)
                );
                if (idx >= 0) {
                    dom.timeSelect.value = idx;
                    state.selectedScanIndex = idx;
                    updateFrameProgressUI();
                    await loadRadarData();
                    return;
                }

                // Fallback: snap to nearest available scan time.
                const targetDigits = timeMatch.padEnd(6, "0").slice(0, 6);
                const targetSeconds =
                    parseInt(targetDigits.slice(0, 2), 10) * 3600 +
                    parseInt(targetDigits.slice(2, 4), 10) * 60 +
                    parseInt(targetDigits.slice(4, 6), 10);
                if (Number.isFinite(targetSeconds)) {
                    const targetDate = state.date;
                    const sameDateOnly = state.scans
                        .map((scan, i) => ({ scan, i }))
                        .filter(({ scan }) => (scan.scanDate || state.date) === targetDate);
                    const candidates = sameDateOnly.length
                        ? sameDateOnly
                        : state.scans.map((scan, i) => ({ scan, i }));

                    let nearest = -1;
                    let minDiff = Number.POSITIVE_INFINITY;
                    candidates.forEach(({ scan, i }) => {
                        const scanDigits = scan.time.replace(/:/g, "").padEnd(6, "0").slice(0, 6);
                        const scanSeconds =
                            parseInt(scanDigits.slice(0, 2), 10) * 3600 +
                            parseInt(scanDigits.slice(2, 4), 10) * 60 +
                            parseInt(scanDigits.slice(4, 6), 10);
                        if (!Number.isFinite(scanSeconds)) return;
                        const diff = Math.abs(scanSeconds - targetSeconds);
                        if (diff < minDiff) {
                            minDiff = diff;
                            nearest = i;
                        }
                    });

                    if (nearest >= 0) {
                        dom.timeSelect.value = nearest;
                        state.selectedScanIndex = nearest;
                        updateFrameProgressUI();
                        await loadRadarData();
                        return;
                    }
                }
            }

            // Auto-select latest scan.
            const latestIdx = getLatestScanIndexAcrossDates(state.scans);
            dom.timeSelect.value = String(latestIdx);
            state.selectedScanIndex = latestIdx;
            updateFrameProgressUI();
            await loadRadarData();
        } catch (err) {
            console.error("Failed to load scans:", err);
            dom.timeSelect.innerHTML = '<option value="">Error loading scans</option>';
            dom.elevationSelect.disabled = true;
            dom.elevationSelect.innerHTML = '<option value="">No elevations available</option>';
            updateFrameProgressUI();
            setPlayButtonState("stopped");
        }
    }

    async function onTimeChange() {
        if (state.liveMode) return;
        stopAnim();
        invalidatePreload();
        state.selectedScanIndex = parseInt(dom.timeSelect.value);
        updateFrameProgressUI();
        await loadRadarData();
    }

    // ══════════════════════════════════════════════════════════
    // ── Single-Frame Polar Radar Loading ─────────────────────
    // ══════════════════════════════════════════════════════════

    async function loadRadarData(options = {}) {
        if (!state.selectedStation || state.selectedScanIndex < 0) return;
        const requestToken = ++state.radarLoadToken;

        const scan = state.scans[state.selectedScanIndex];
        if (!scan) return;

        const timeClean = scan.time.replace(/:/g, "");
        const dateForScan = scan.scanDate || state.date;
        const dateClean = dateForScan.replace(/-/g, "");

        const showPlaySpinner = options.showOverlay !== false && !satOnlyActive();
        const loadStartedAt = showPlaySpinner ? Date.now() : 0;
        if (showPlaySpinner) {
            state.radarDataLoadInProgress = true;
            setPlayButtonState("loading");
        }

        try {
            if (state.showLightning) prefetchLightning(scan);
            if (state.showSatellite) prefetchSatellite(scan);
            // Live: fetch the in-progress volume alongside the archived one.
            const isLatestScan = state.selectedScanIndex === getLatestScanIndexAcrossDates(state.scans);
            const livePromise = state.liveMode && isLatestScan && !isL3Product(state.product)
                ? withTimeout(fetchLiveChunkFrames(), LIVE_CHUNK_WAIT_MS)
                : null;
            let payload;
            if (isL3Product(state.product)) {
                payload = await loadL3Polar({
                    station: state.selectedStation,
                    dateClean,
                    timeClean,
                    productKey: state.product,
                });
            } else {
                payload = await loadL2Polar({
                    station: state.selectedStation,
                    dateClean,
                    timeClean,
                    product: state.product,
                    elevation: getSelectedElevation(),
                    includeSails: state.liveMode,
                    s3Key: scan.key,
                    s3Size: scan.size,
                });
            }
            if (!payload) {
                throw new Error("Radar response was empty");
            }
            if (requestToken !== state.radarLoadToken) {
                return;
            }
            updateElevationControlsFromPayload(payload);
            // In live mode we request lowest+SAILS and display the latest sweep:
            // from the in-progress real-time volume when it has one, else the archive.
            const live = livePromise ? await livePromise : null;
            if (requestToken !== state.radarLoadToken || state.mosaicMode) {
                return;
            }
            if (state.liveMode) state.liveChunkShownKey = liveChunkFramesKey(live);
            const liveFrames = live?.frames || [];
            const polarData = liveFrames.length
                ? liveFrames[liveFrames.length - 1]
                : (state.liveMode && payload && Array.isArray(payload.frames) && payload.frames.length
                    ? payload.frames[payload.frames.length - 1]
                    : payload);
            const decoded = decodePolarData(polarData);
            state.lastRadarMeta = polarData;

            if (radarOverlay && radarOverlay.setData) {
                radarOverlay.setData(polarData, decoded).setOpacity(effectiveRadarOpacity());
                if (!dom.map.hasLayer(radarOverlay) && !satOnlyActive()) radarOverlay.addTo(dom.map);
            } else {
                if (radarOverlay) {
                    dom.map.removeLayer(radarOverlay);
                    radarOverlay = null;
                }
                radarOverlay = new RadarGLLayer(
                    polarData,
                    decoded,
                    { opacity: effectiveRadarOpacity() }
                );
                if (!satOnlyActive()) radarOverlay.addTo(dom.map);
            }

            // Range rings & info
            drawRangeRings(polarData.lat, polarData.lon, polarData.max_range_km);
            updateInfoBar(polarData);
            setColorScaleVisible(true);
            updateFrameProgressUI();
            updateURL();

            if (state.showStormTracks) {
                loadStormTracksForCurrentFrame();
            }
            if (state.showLightning) {
                loadLightningForCurrentFrame();
            }
            if (state.showSatellite) {
                loadSatelliteForCurrentFrame();
            }
            if (state.showAlerts) {
                loadAlertsForCurrentFrame();
            }

            // Support sharing "animation on" state via URL.
            if (state.initialUrlAutoplay) {
                state.initialUrlAutoplay = false;
                if (!state.playing) {
                    setTimeout(() => togglePlay(), 0);
                }
            }
        } catch (err) {
            if (requestToken !== state.radarLoadToken) {
                return;
            }
            console.error("Failed to load radar data:", err);
            alert(`Error: ${err.message}`);
        } finally {
            if (requestToken === state.radarLoadToken) {
                state.radarDataLoadInProgress = false;
                if (showPlaySpinner) {
                    const minDisplayMs = 180;
                    const elapsed = Date.now() - loadStartedAt;
                    const delayMs = Math.max(0, minDisplayMs - elapsed);
                    if (delayMs > 0) {
                        setTimeout(() => {
                            if (requestToken === state.radarLoadToken && !state.mosaicMode) {
                                setPlayButtonState("stopped");
                            }
                        }, delayMs);
                    } else if (!state.mosaicMode) {
                        setPlayButtonState("stopped");
                    }
                }
                updateFrameProgressUI();
            }
        }
    }

    function drawRangeRings(lat, lon, maxRangeKm) {
        if (rangeRings) {
            dom.map.removeLayer(rangeRings);
        }
        if (stationDot) {
            dom.map.removeLayer(stationDot);
        }

        rangeRings = L.layerGroup();

        const intervals = [50, 100, 150, 200, 250];
        intervals.forEach((km) => {
            if (km <= maxRangeKm) {
                L.circle([lat, lon], {
                    radius: km * 1000,
                    color: "rgba(90, 169, 255, 0.12)",
                    fillColor: "transparent",
                    fillOpacity: 0,
                    weight: 1,
                    dashArray: "4 6",
                    interactive: false,
                }).addTo(rangeRings);
            }
        });

        stationDot = L.circleMarker([lat, lon], {
            radius: 5,
            color: "#5aa9ff",
            fillColor: "#5aa9ff",
            fillOpacity: 1,
            weight: 2,
            interactive: false,
        });

        if (!satOnlyActive()) rangeRings.addTo(dom.map);
        stationDot.addTo(dom.map);
    }

    function updateInfoBar(meta) {
        if (satOnlyActive()) {
            renderSatelliteInfoBar();
            return;
        }
        if (state.mosaicMode) {
            renderMosaicInfoBar();
            return;
        }
        if (!meta) return;
        const localTime = meta.time_local
            ? `${meta.time_local}${meta.timezone_abbr ? ` ${meta.timezone_abbr}` : ""}`
            : "N/A";
        const liveAge = state.liveMode ? formatLiveAge(meta) : null;
        var elevHtml = meta.elevation != null
            ? `<span class="info-item"><span class="info-label">Elev:</span> <span class="info-value">${meta.elevation}\u00b0</span></span>`
            : "";
        var levelTag = meta.level === 3
            ? `<span class="info-item"><span class="info-label">L3:</span> <span class="info-value">${meta.l3_product_code || ""}</span></span>`
            : "";
        const glmLabel = state.showLightning
            ? glmInfoLabel({ loading: state.glmLoading, count: state.lastGlmCount })
            : null;
        const glmHtml = glmLabel != null
            ? `<span class="info-item${state.glmLoading ? " glm-loading" : ""}"><span class="info-label">GLM:</span> <span class="info-value">${glmLabel}</span></span>`
            : "";
        const satHtml = satInfoHtml();
        dom.mapInfoBar.innerHTML = `
            <span class="info-item"><span class="info-label">Time:</span> <span class="info-value">${meta.time} UTC</span></span>
            ${liveAge ? `<span class="info-item"><span class="info-label">Live:</span> <span class="info-value">${liveAge}</span></span>` : ""}
            <span class="info-item"><span class="info-label">Local:</span> <span class="info-value">${localTime}</span></span>
            <span class="info-item"><span class="info-label">Date:</span> <span class="info-value">${formatDate(meta.date)}</span></span>
            ${elevHtml}
            ${levelTag}
            <span class="info-item"><span class="info-label">Product:</span> <span class="info-value">${formatProductLabel(meta.product)}</span></span>
            ${glmHtml}
            ${satHtml}
            ${state.showAlerts && state.lastAlertCount != null
                ? `<span class="info-item"><span class="info-label">Alerts:</span> <span class="info-value">${state.lastAlertCount}</span></span>`
                : ""}
        `;
        dom.mapInfoBar.classList.add("visible");
        syncMapDockOffsets();
    }

    function syncMapDockOffsets() {
        if (!dom.mapInfoBar) return;
        if (dockSyncRaf != null) {
            cancelAnimationFrame(dockSyncRaf);
        }
        dockSyncRaf = requestAnimationFrame(() => {
            const mobile = window.matchMedia("(max-width: 768px)").matches;
            const fallback = mobile ? 28 : 52;
            // Track the pill's real height. A fixed floor left a gap under the playback card.
            const measured = dom.mapInfoBar.classList.contains("visible")
                ? Math.ceil(dom.mapInfoBar.getBoundingClientRect().height || 0)
                : fallback;
            const infoHeight = measured > 0 ? measured : fallback;
            document.documentElement.style.setProperty("--map-info-height", `${infoHeight}px`);
            dockSyncRaf = null;
        });
    }

    // ══════════════════════════════════════════════════════════
    // ── Animation & Polar Frame Preloading ───────────────────
    // ══════════════════════════════════════════════════════════

    /**
     * Build the frame window: an array of scan indices starting
     * from the current selection, bounded by latest available scan.
     * This prevents near-real-time playback from wrapping to old scans.
     */
    function buildFrameWindow() {
        const anchor = state.selectedScanIndex;
        if (anchor < 0 || anchor >= state.scans.length) {
            state.frameWindow = [];
            return;
        }

        if (state.liveMode) {
            const end = anchor;
            const start = Math.max(0, end - state.frameCount + 1);
            const win = [];
            for (let i = start; i <= end; i++) {
                win.push(i);
            }
            state.frameWindow = win;
            return;
        }

        // Non-live mode:
        // Prefer frames starting at the selected scan, but if we're near the
        // end of available scans, backfill from earlier scans so we still load
        // up to state.frameCount total frames when possible.
        const end = Math.min(state.scans.length - 1, anchor + state.frameCount - 1);
        const start = Math.max(0, end - state.frameCount + 1);
        const count = (end - start) + 1;
        const win = [];
        for (let i = 0; i < count; i++) {
            win.push(start + i);
        }
        state.frameWindow = win;
    }

    /**
     * Preload all frames in the current window.
     * Loads newest volumes first and stops once enough frames are collected.
     */
    async function preloadFrames(options = {}) {
        const background = options.background === true;
        state.preloading = true;
        state.framesReady = false;
        state.preloadController = new AbortController();
        const signal = state.preloadController.signal;

        clearPolarFrames();
        buildFrameWindow();
        // The loop's satellite frames queue behind the radar, one per GOES file.
        if (state.showSatellite) queueSatelliteLoop(currentLoopTimes());

        const total = state.frameWindow.length;
        const targetFrameTotal = Math.max(1, state.frameCount);
        let loaded = 0;
        state.radarLoopProgress = { loaded: 0, total: targetFrameTotal };

        if (background) {
            setFrameLoadStatus(`Loading 0 / ${targetFrameTotal}`);
        } else {
            setFrameLoadStatus("");
            setPlayButtonState("loading");
        }

        const scanWindowNewestFirst = state.frameWindow.slice().reverse();
        const loadedSlotsNewestFirst = [];
        // Live: sweeps from the volume still being scanned go ahead of the archive.
        const liveAnchor = state.frameWindow[state.frameWindow.length - 1];
        let livePromise = state.liveMode && liveAnchor === getLatestScanIndexAcrossDates(state.scans)
            ? withTimeout(fetchLiveChunkFrames(signal), LIVE_CHUNK_WAIT_MS)
            : null;
        let loadedFrameCount = 0;
        let animStarted = false;
        let rangeRingsDrawn = false;

        async function fetchFrameSlot(scanIdx, slotNumber) {
            const scan = state.scans[scanIdx];
            const timeClean = scan.time.replace(/:/g, "");
            const scanDate = (scan.scanDate || state.date).replace(/-/g, "");
            for (let attempt = 0; attempt <= PRELOAD_MAX_RETRIES; attempt++) {
                if (signal.aborted) return null;
                try {
                    let data;
                    if (isL3Product(state.product)) {
                        data = await loadL3Polar({
                            station: state.selectedStation,
                            dateClean: scanDate,
                            timeClean,
                            productKey: state.product,
                            signal,
                        });
                    } else {
                        data = await loadL2Polar({
                            station: state.selectedStation,
                            dateClean: scanDate,
                            timeClean,
                            product: state.product,
                            elevation: getSelectedElevation(),
                            includeSails: true,
                            s3Key: scan.key,
                            s3Size: scan.size,
                            signal,
                        });
                    }
                    if (signal.aborted) return null;

                    const frameList = Array.isArray(data?.frames) && data.frames.length
                        ? data.frames
                        : (data && data.data ? [data] : []);
                    if (!frameList.length) {
                        throw new Error("Radar response was empty");
                    }
                    const decodedList = frameList.map(function (f) { return decodePolarData(f); });
                    return {
                        scanIdx,
                        slotFrames: frameList.map(function (f, j) {
                            return {
                                scanIdx,
                                polarData: f,
                                decoded: decodedList[j],
                                loaded: true,
                            };
                        }),
                        loadedFrameCount: frameList.length,
                    };
                } catch (err) {
                    if (signal.aborted) return null;
                    if (attempt < PRELOAD_MAX_RETRIES) {
                        const delay = PRELOAD_RETRY_BASE_MS * Math.pow(2, attempt);
                        console.warn(`Frame slot ${slotNumber} error, retrying in ${delay}ms (attempt ${attempt + 1}/${PRELOAD_MAX_RETRIES}):`, err.message);
                        await new Promise(function (r) { setTimeout(r, delay); });
                        continue;
                    }
                    console.error(`Frame slot ${slotNumber} failed after ${PRELOAD_MAX_RETRIES + 1} attempts:`, err);
                    return {
                        scanIdx,
                        slotFrames: [{
                            scanIdx,
                            polarData: null,
                            decoded: null,
                            loaded: false,
                        }],
                        loadedFrameCount: 0,
                    };
                }
            }
        }

        function rebuildFramesFromSlots() {
            const chronological = loadedSlotsNewestFirst.slice().reverse();
            const flat = [];
            chronological.forEach(function (slot) {
                if (Array.isArray(slot)) {
                    slot.forEach(function (f) { flat.push(f); });
                } else if (slot) {
                    flat.push(slot);
                }
            });
            const nextFrames = flat.length > state.frameCount
                ? flat.slice(flat.length - state.frameCount)
                : flat;

            if (!rangeRingsDrawn) {
                for (var i = 0; i < nextFrames.length; i++) {
                    var f = nextFrames[i];
                    if (f && f.loaded && f.polarData) {
                        drawRangeRings(f.polarData.lat, f.polarData.lon, f.polarData.max_range_km);
                        rangeRingsDrawn = true;
                        break;
                    }
                }
            }

            // Keep the in-flight loop on a stable list; apply new frames at wrap.
            if (state.playing && state.polarFrames.length > 0) {
                state.pendingPolarFrames = nextFrames;
                return;
            }
            commitPolarFrames(nextFrames);
        }

        for (let batchStart = 0; batchStart < scanWindowNewestFirst.length; batchStart += PRELOAD_FETCH_CONCURRENCY) {
            if (signal.aborted) break;

            const batchIndices = scanWindowNewestFirst.slice(
                batchStart,
                batchStart + PRELOAD_FETCH_CONCURRENCY
            );
            const batchResults = await Promise.all(
                batchIndices.map((scanIdx, offset) => fetchFrameSlot(scanIdx, batchStart + offset))
            );
            if (signal.aborted) break;

            if (livePromise) {
                const live = await livePromise;
                livePromise = null;
                if (signal.aborted) break;
                if (live?.frames?.length) {
                    loadedSlotsNewestFirst.push(live.frames.map(function (f) {
                        return { scanIdx: liveAnchor, polarData: f, decoded: decodePolarData(f), loaded: true };
                    }));
                    loadedFrameCount += live.frames.length;
                }
            }

            for (const result of batchResults) {
                if (!result) continue;
                loadedSlotsNewestFirst.push(result.slotFrames);
                loadedFrameCount += result.loadedFrameCount;
                loaded++;
                if (state.preloadController?.signal === signal) {
                    state.radarLoopProgress = { loaded: Math.min(loadedFrameCount, targetFrameTotal), total: targetFrameTotal };
                }

                if (background) {
                    const loadedFrames = Math.min(loadedFrameCount, targetFrameTotal);
                    setFrameLoadStatus(`Loading ${loadedFrames} / ${targetFrameTotal}`);
                }
            }

            rebuildFramesFromSlots();

            if (!animStarted && !signal.aborted && loadedFrameCount >= MIN_FRAMES_TO_START_ANIM) {
                state.framesReady = true;
                animStarted = true;
                if (!background) {
                    setFrameLoadStatus("");
                }
                if (options.startAnim !== false) startAnim();
            }

            if (loadedFrameCount >= state.frameCount) break;
        }

        state.preloading = false;
        if (state.preloadController?.signal === signal) state.radarLoopProgress = null;
        setFrameLoadStatus("");
        if (!animStarted && !background) {
            setPlayButtonState("stopped");
        }

        const stale = signal.aborted
            || (state.preloadController && state.preloadController.signal !== signal);
        if (stale) return false;

        rebuildFramesFromSlots();

        const framesForCount = state.pendingPolarFrames || state.polarFrames;
        const totalLoaded = framesForCount.filter(function (f) {
            return f && f.loaded;
        }).length;
        if (totalLoaded === 0) {
            if (!animStarted) {
                alert("Failed to load any frames. Please try again.");
            }
            return false;
        }

        state.framesReady = true;
        updateFrameProgressUI();
        return true;
    }

    /**
     * Remove preloaded frame data and reset state.
     */
    function clearPolarFrames() {
        state.polarFrames = [];
        state.pendingPolarFrames = null;
        state.frameWindow = [];
        state.framesReady = false;
        state.animFrameIndex = 0;
    }

    /**
     * Invalidate the preloaded frames (e.g. when station/date/product changes).
     */
    function invalidatePreload() {
        if (state.preloadController) {
            state.preloadController.abort();
            state.preloadController = null;
        }
        clearPolarFrames();
        state.preloading = false;
        state.radarLoopProgress = null;
        state.satLoop = null;
        setFrameLoadStatus("");
        updateFrameProgressUI();
        if (state.showLightning) {
            state.glmRenderedKey = "";
            loadLightningForCurrentFrame();
        }
        if (state.showSatellite) loadSatelliteForCurrentFrame();
    }

    /**
     * Show a specific frame by global index (works with SAILS: multiple frames per volume).
     */
    function showFrame(globalIdx) {
        if (state.mosaicMode || satOnlyActive()) return;
        const frame = getFrameAt(globalIdx);
        if (!frame || !frame.loaded) return;
        if (radarOverlay && radarOverlay.setData) {
            radarOverlay.setData(frame.polarData, frame.decoded).setOpacity(state.radarOpacity);
        }
        state.animFrameIndex = globalIdx;
        if (!state.playing) {
            state.selectedScanIndex = frame.scanIdx;
            dom.timeSelect.value = String(frame.scanIdx);
        }
        updateInfoBar(frame.polarData);
        updateFrameProgressUI();
        updateURL();
        if (state.showStormTracks) {
            loadStormTracksForCurrentFrame();
        }
        if (state.showLightning) {
            loadLightningForCurrentFrame();
        }
        if (state.showSatellite) {
            loadSatelliteForCurrentFrame();
        }
        if (state.showAlerts) {
            loadAlertsForCurrentFrame();
        }
    }

    /**
     * Toggle play/pause. On first play, preloads frames first.
     */
    async function togglePlay() {
        if (satOnlyActive()) {
            await toggleSatellitePlay();
            return;
        }
        if (state.mosaicMode) {
            if (state.playing) {
                stopAnim();
                if (state.liveMode) startMosaicLiveTimer();
                updateURL();
                return;
            }
            pinMosaicTime();
            state.mosaicLoopAnchorMs = snapMosaicMs(state.mosaicTimeMs);
            state.mosaicTimeMs = loopStartMs(state.mosaicLoopAnchorMs, state.frameCount, MOSAIC_STEP_MS);
            state.playing = true;
            setPlayButtonState("playing");
            if (state.showSatellite) queueSatelliteLoop(currentLoopTimes());
            refreshMosaic();
            state.animInterval = setInterval(mosaicAnimTick, state.animSpeed);
            return;
        }
        if (state.scans.length === 0) return;

        // Pause always takes priority so user can pause mid-preload
        if (state.playing) {
            stopAnim();
            return;
        }

        // If preloading but animation hasn't started yet, cancel everything
        if (state.preloading) {
            invalidatePreload();
            setFrameLoadStatus("");
            setPlayButtonState("stopped");
            loadRadarData();
            return;
        }

        // Already preloaded — resume
        if (state.framesReady) {
            startAnim();
            return;
        }

        // Start preloading; animation begins automatically once enough frames arrive
        setPlayButtonState("loading");
        preloadFrames({ background: state.liveMode }).then(function (ok) {
            if (!ok && !state.playing) {
                setPlayButtonState("stopped");
            }
        });
    }

    function startAnim() {
        const total = getTotalFrameCount();
        if (total <= 0) return;

        state.playing = true;
        setPlayButtonState("playing");

        state.animFrameIndex = Math.max(0, Math.min(state.animFrameIndex, total - 1));
        state.animEndPauseTicksRemaining = 0;
        showFrame(state.animFrameIndex);

        state.animInterval = setInterval(animTick, state.animSpeed);
    }

    function animTick() {
        const total = getTotalFrameCount();
        if (total === 0) return;

        // Hold on last frame before looping back to the first frame.
        if (state.animEndPauseTicksRemaining > 0) {
            state.animEndPauseTicksRemaining -= 1;
            if (state.animEndPauseTicksRemaining > 0) {
                return;
            }
        } else if (state.animFrameIndex === total - 1) {
            state.animEndPauseTicksRemaining = Math.max(END_PAUSE_MULTIPLIER - 1, 0);
            if (state.animEndPauseTicksRemaining > 0) {
                return;
            }
        }

        const wrapping = state.animFrameIndex >= total - 1;
        if (wrapping) {
            // Apply frames that finished loading during this loop so the next
            // cycle includes them without jumping mid-playback.
            if (state.pendingPolarFrames) {
                commitPolarFrames(state.pendingPolarFrames, { resetToStart: true });
            } else {
                state.animFrameIndex = 0;
            }
        } else {
            state.animFrameIndex += 1;
        }

        // Skip frames that failed to load
        const playTotal = getTotalFrameCount();
        let attempts = 0;
        while (attempts < playTotal) {
            const frame = getFrameAt(state.animFrameIndex);
            if (frame && frame.loaded) break;
            state.animFrameIndex = (state.animFrameIndex + 1) % playTotal;
            attempts++;
        }

        showFrame(state.animFrameIndex);
    }

    function stopAnim() {
        if (state.satPreloading) {
            state.satPreloadToken += 1;
            state.satPreloading = false;
        }
        state.playing = false;
        setPlayButtonState("stopped");
        state.animEndPauseTicksRemaining = 0;

        if (state.animInterval) {
            clearInterval(state.animInterval);
            state.animInterval = null;
        }

        // Paused: safe to reveal frames that loaded during the current loop.
        if (state.pendingPolarFrames) {
            commitPolarFrames(state.pendingPolarFrames);
        }

        // Keep the current frame visible on the GL layer.
    }

    function setPlayButtonState(mode) {
        const playIcon = dom.btnPlay.querySelector(".icon-play");
        const pauseIcon = dom.btnPlay.querySelector(".icon-pause");
        if (!state.preloading) {
            dom.btnPlay.classList.remove("loading");
        }

        switch (mode) {
            case "playing":
                dom.btnPlay.classList.add("playing");
                playIcon.style.display = "none";
                pauseIcon.style.display = "block";
                break;
            case "loading":
                dom.btnPlay.classList.remove("playing");
                dom.btnPlay.classList.add("loading");
                playIcon.style.display = "none";
                pauseIcon.style.display = "none";
                break;
            case "stopped":
            default:
                dom.btnPlay.classList.remove("playing");
                playIcon.style.display = "block";
                pauseIcon.style.display = "none";
                break;
        }
        updateFrameCycleVisibility();
    }

    /**
     * Step to the next scan.
     */
    function nextScan() {
        if (satOnlyActive()) {
            stepSatClock(1);
            return;
        }
        if (state.mosaicMode) {
            if (state.playing) stopAnim();
            pinMosaicTime();
            stepMosaic(1);
            return;
        }
        if (state.scans.length === 0) return;
        if (state.playing) {
            stopAnim();
        }

        const total = getTotalFrameCount();
        if (state.framesReady && total > 0) {
            const next = (state.animFrameIndex + 1) % total;
            showFrame(next);
        } else {
            state.selectedScanIndex = (state.selectedScanIndex + 1) % state.scans.length;
            dom.timeSelect.value = state.selectedScanIndex;
            updateFrameProgressUI();
            loadRadarData();
        }
    }

    function prevScan() {
        if (satOnlyActive()) {
            stepSatClock(-1);
            return;
        }
        if (state.mosaicMode) {
            if (state.playing) stopAnim();
            pinMosaicTime();
            stepMosaic(-1);
            return;
        }
        if (state.scans.length === 0) return;
        if (state.playing) {
            stopAnim();
        }

        const total = getTotalFrameCount();
        if (state.framesReady && total > 0) {
            const prev = (state.animFrameIndex - 1 + total) % total;
            showFrame(prev);
        } else {
            state.selectedScanIndex =
                (state.selectedScanIndex - 1 + state.scans.length) % state.scans.length;
            dom.timeSelect.value = state.selectedScanIndex;
            updateFrameProgressUI();
            loadRadarData();
        }
    }

    // ── URL State Management ──────────────────────────────────

    function updateURL() {
        const params = new URLSearchParams();

        if (state.selectedStation) {
            params.set("station", state.selectedStation.id);
        }
        const selectedScan = state.selectedScanIndex >= 0 ? state.scans[state.selectedScanIndex] : null;
        const selectedScanDate = selectedScan && selectedScan.scanDate ? selectedScan.scanDate : state.date;
        if (selectedScanDate) {
            params.set("date", selectedScanDate.replace(/-/g, ""));
        }
        if (selectedScan && selectedScan.time) {
            params.set("time", selectedScan.time.replace(/:/g, ""));
        }
        if (state.mosaicMode && Number.isFinite(state.mosaicTimeMs)) {
            params.set("mosaic", "1");
            params.set("date", formatMosaicDateCompact(state.mosaicTimeMs));
            params.set("time", formatMosaicTimeCompact(state.mosaicTimeMs));
        }
        if (state.product !== "reflectivity") {
            params.set("product", state.product);
        }
        const selectedElevation = getSelectedElevation();
        if (Number.isFinite(selectedElevation)) {
            params.set("elev", selectedElevation.toFixed(1));
        }
        params.set("frames", String(state.frameCount));
        params.set("speed", String(state.animSpeed));
        params.set("anim", state.playing ? "1" : "0");
        params.set("opacity", String(Math.round(state.radarOpacity * 100)));
        params.set("live", state.liveMode ? "1" : "0");
        if (state.showReferenceOverlay) {
            params.set("overlay", "1");
        }
        if (state.showStormTracks) {
            params.set("tracks", "1");
        }
        if (state.showLightning) {
            params.set("glm", "1");
        }
        writeSatParams(params, satShareState());
        if (state.showAlerts) {
            params.set("ww", "1");
        }
        writeRainParams(params, currentRainShare());
        if (dom.map) {
            const center = dom.map.getCenter();
            params.set("lat", center.lat.toFixed(4));
            params.set("lon", center.lng.toFixed(4));
            params.set("z", String(dom.map.getZoom()));
        }

        const url = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(null, "", url);
    }

    function buildShortToken() {
        const VERSION = 2;
        const bytes = [];
        const pushU8 = (v) => bytes.push(v & 0xff);
        const pushU16 = (v) => {
            bytes.push((v >> 8) & 0xff, v & 0xff);
        };
        const pushU24 = (v) => {
            bytes.push((v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff);
        };

        pushU8(VERSION);

        const station = (state.selectedStation && state.selectedStation.id
            ? state.selectedStation.id
            : "____").toUpperCase().padEnd(4, "_").slice(0, 4);
        for (let i = 0; i < 4; i++) pushU8(station.charCodeAt(i));

        const selectedScan = state.selectedScanIndex >= 0 ? state.scans[state.selectedScanIndex] : null;
        const selectedScanDate = selectedScan && selectedScan.scanDate ? selectedScan.scanDate : state.date;
        const dateDigits = selectedScanDate ? selectedScanDate.replace(/-/g, "") : "";
        let dayCount = 0xffff; // sentinel = absent
        if (/^\d{8}$/.test(dateDigits)) {
            const y = parseInt(dateDigits.slice(0, 4), 10);
            const m = parseInt(dateDigits.slice(4, 6), 10) - 1;
            const d = parseInt(dateDigits.slice(6, 8), 10);
            const epoch = Date.UTC(2000, 0, 1);
            const ts = Date.UTC(y, m, d);
            if (Number.isFinite(ts)) {
                const days = Math.floor((ts - epoch) / 86400000);
                if (days >= 0 && days < 0xffff) dayCount = days;
            }
        }
        pushU16(dayCount);

        let secOfDay = 0xffffff; // sentinel = absent
        if (selectedScan && selectedScan.time) {
            const t = selectedScan.time.replace(/:/g, "");
            if (/^\d{6}$/.test(t)) {
                const hh = parseInt(t.slice(0, 2), 10);
                const mm = parseInt(t.slice(2, 4), 10);
                const ss = parseInt(t.slice(4, 6), 10);
                secOfDay = Math.max(0, Math.min(86399, (hh * 3600) + (mm * 60) + ss));
            }
        }
        pushU24(secOfDay);

        const productCode = PRODUCT_TO_SHORT_TOKEN_CODE[state.product] ?? 0;
        pushU8(productCode);
        pushU8(Math.max(0, Math.min(255, state.frameCount | 0)));
        pushU16(Math.max(0, Math.min(65535, state.animSpeed | 0)));

        let flags = 0;
        if (state.playing) flags |= 1;
        if (state.liveMode) flags |= 2;
        if (state.showReferenceOverlay) flags |= 4;
        if (dom.map) flags |= 8;
        if (state.showStormTracks) flags |= 16;
        if (state.showLightning) flags |= 32;
        if (state.showAlerts) flags |= 64;
        const rain = currentRainShare();
        if (rain) flags |= RAIN_TOKEN_FLAG;
        pushU8(flags);

        pushU8(Math.max(0, Math.min(100, Math.round(state.radarOpacity * 100))));

        if (dom.map) {
            const c = dom.map.getCenter();
            const latQ = Math.max(0, Math.min(18000, Math.round((c.lat + 90) * 100)));
            const lonQ = Math.max(0, Math.min(36000, Math.round((c.lng + 180) * 100)));
            const zoomQ = Math.max(0, Math.min(255, Math.round(dom.map.getZoom() * 2)));
            pushU16(latQ);
            pushU16(lonQ);
            pushU8(zoomQ);
        }
        if (rain) appendRainToken(bytes, rain);

        let bin = "";
        for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
        return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
    }

    function decodeShortToken(token) {
        try {
            const padded = token.replace(/-/g, "+").replace(/_/g, "/");
            const b64 = padded + "=".repeat((4 - (padded.length % 4)) % 4);
            const raw = atob(b64);
            if (!raw) return null;

            // v2 compact binary token
            const v = raw.charCodeAt(0);
            if (v === 2) {
                const bytes = new Uint8Array(raw.length);
                for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);

                let p = 1;
                const readU8 = () => bytes[p++];
                const readU16 = () => ((bytes[p++] << 8) | bytes[p++]);
                const readU24 = () => ((bytes[p++] << 16) | (bytes[p++] << 8) | bytes[p++]);

                const station = String.fromCharCode(readU8(), readU8(), readU8(), readU8());
                const dayCount = readU16();
                const secOfDay = readU24();
                const productCode = readU8();
                const frames = readU8();
                const speed = readU16();
                const flags = readU8();
                const opacity = readU8();

                let d = null;
                if (dayCount !== 0xffff) {
                    const epoch = Date.UTC(2000, 0, 1);
                    const dt = new Date(epoch + dayCount * 86400000);
                    d = `${dt.getUTCFullYear()}${String(dt.getUTCMonth() + 1).padStart(2, "0")}${String(dt.getUTCDate()).padStart(2, "0")}`;
                }

                let t = null;
                if (secOfDay !== 0xffffff) {
                    const hh = Math.floor(secOfDay / 3600);
                    const mm = Math.floor((secOfDay % 3600) / 60);
                    const ss = secOfDay % 60;
                    t = `${String(hh).padStart(2, "0")}${String(mm).padStart(2, "0")}${String(ss).padStart(2, "0")}`;
                }

                let m = null;
                if (flags & 8) {
                    const latQ = readU16();
                    const lonQ = readU16();
                    const zoomQ = readU8();
                    m = [
                        Number(((latQ / 100) - 90).toFixed(2)),
                        Number(((lonQ / 100) - 180).toFixed(2)),
                        Number((zoomQ / 2).toFixed(1)),
                    ];
                }
                const rainRead = rainFromToken(flags, bytes, p);

                return {
                    s: station.replace(/_/g, "") || undefined,
                    d: d || undefined,
                    t: t || undefined,
                    p: SHORT_TOKEN_CODE_TO_PRODUCT[productCode] || "reflectivity",
                    f: frames,
                    v: speed,
                    a: (flags & 1) ? 1 : 0,
                    o: opacity,
                    l: (flags & 2) ? 1 : 0,
                    h: (flags & 4) ? 1 : undefined,
                    tr: (flags & 16) ? 1 : undefined,
                    glm: (flags & 32) ? 1 : undefined,
                    ww: (flags & 64) ? 1 : undefined,
                    m: m || undefined,
                    rain: rainRead?.rain,
                };
            }

            // Backward compatibility: JSON token
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }

    function loadFromURL() {
        const params = new URLSearchParams(window.location.search);
        const short = params.get("s");

        // Long-form params
        let stationId = params.get("station");
        let date = params.get("date");
        let product = params.get("product");
        let timeParam = params.get("time");
        let latParam = params.get("lat");
        let lonParam = params.get("lon");
        let zoomParam = params.get("z");
        let framesParam = params.get("frames");
        let speedParam = params.get("speed");
        let animParam = params.get("anim");
        let opacityParam = params.get("opacity");
        let liveParam = params.get("live");
        let overlayParam = params.get("overlay");
        let tracksParam = params.get("tracks");
        let glmParam = params.get("glm");
        let wwParam = params.get("ww");
        let elevParam = params.get("elev");
        state.initialRain = parseRainUrl(params.get("rain"), params.get("rpt"));

        // Compact short-form params (preferred when present)
        if (params.get("st")) stationId = params.get("st");
        if (params.get("d")) date = params.get("d");
        if (params.get("t")) timeParam = params.get("t");
        if (params.get("p")) {
            const p = params.get("p");
            product = SHORT_PARAM_TO_PRODUCT[p] || product;
        }
        if (params.get("m")) {
            const m = params.get("m").split(",");
            if (m.length === 3) {
                latParam = m[0];
                lonParam = m[1];
                zoomParam = m[2];
            }
        }
        if (params.get("fr")) framesParam = params.get("fr");
        if (params.get("spd")) speedParam = params.get("spd");
        if (params.get("a")) animParam = params.get("a");
        if (params.get("o")) opacityParam = params.get("o");
        if (params.get("lv")) liveParam = params.get("lv");
        if (params.get("ov")) overlayParam = params.get("ov");
        if (params.get("tr")) tracksParam = params.get("tr");
        if (params.get("gl")) glmParam = params.get("gl");

        // Backward compatibility for previous token-style short links.
        if (short) {
            const decoded = decodeShortToken(short);
            if (decoded) {
                stationId = decoded.s || stationId;
                date = decoded.d || date;
                product = decoded.p || product;
                timeParam = decoded.t || timeParam;
                if (Array.isArray(decoded.m) && decoded.m.length === 3) {
                    latParam = String(decoded.m[0]);
                    lonParam = String(decoded.m[1]);
                    zoomParam = String(decoded.m[2]);
                }
                if (decoded.f != null) framesParam = String(decoded.f);
                if (decoded.v != null) speedParam = String(decoded.v);
                if (decoded.a != null) animParam = String(decoded.a);
                if (decoded.o != null) opacityParam = String(decoded.o);
                if (decoded.l != null) liveParam = String(decoded.l);
                if (decoded.h != null) overlayParam = String(decoded.h);
                if (decoded.tr != null) tracksParam = String(decoded.tr);
                if (decoded.glm != null) glmParam = String(decoded.glm);
                if (decoded.ww != null) wwParam = String(decoded.ww);
                if (decoded.rain && !state.initialRain) state.initialRain = decoded.rain;
            }
        }

        // Save initial URL time before updateURL() can strip it
        state.initialUrlTime = timeParam || null;

        const lat = latParam != null ? parseFloat(latParam) : null;
        const lon = lonParam != null ? parseFloat(lonParam) : null;
        const zoom = zoomParam != null ? parseFloat(zoomParam) : null;
        if (
            Number.isFinite(lat) &&
            Number.isFinite(lon) &&
            Number.isFinite(zoom) &&
            zoom >= 2 &&
            zoom <= 19
        ) {
            state.initialUrlMap = { lat, lon, zoom };
            dom.map.setView([lat, lon], zoom, { animate: false });
        }

        if (SUPPORTED_PRODUCTS.includes(product)) {
            state.product = product;
            setActiveProductButton(product);
            updateLegend();
        }

        const requestedElevation = elevParam != null ? parseFloat(elevParam) : NaN;
        if (Number.isFinite(requestedElevation)) {
            state.selectedElevationByProduct[state.product] = requestedElevation;
        }

        if (date) {
            const formatted = date.length === 8
                ? `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
                : date;
            state.date = formatted;
            dom.datePicker.value = formatted;
        } else {
            // Default to "today" (UTC) so latest scans can load immediately.
            const today = getTodayUTCDateString();
            state.date = today;
            dom.datePicker.value = today;
        }

        const satShare = readSatParams(params);
        if (satShare.sat) {
            state.satProduct = satShare.sat;
            state.showSatellite = true;
            state.satMode = satShare.satMode;
            state.satSector = satShare.satSector;
            state.satGoes = satShare.satGoes;
            state.satHold = satShare.satHold;
        }

        const frames = framesParam != null ? parseInt(framesParam, 10) : NaN;
        if (Number.isFinite(frames)) {
            const maxFrames = satOnlyActive() ? SAT_MAX_FRAMES : RADAR_MAX_FRAMES;
            const clampedFrames = Math.max(4, Math.min(maxFrames, frames));
            dom.framesSlider.max = String(maxFrames);
            state.frameCount = clampedFrames;
            dom.framesSlider.value = String(clampedFrames);
        }
        updateFramesLabel();

        const speed = speedParam != null ? parseInt(speedParam, 10) : NaN;
        if (Number.isFinite(speed)) {
            const minMs = parseInt(dom.speedSlider.min || "100", 10);
            const maxMs = parseInt(dom.speedSlider.max || "600", 10);
            const clampedSpeed = Math.max(minMs, Math.min(maxMs, speed));
            state.animSpeed = clampedSpeed;
            dom.speedSlider.value = String(intervalMsToSliderValue(clampedSpeed));
        }

        const opacity = opacityParam != null ? parseInt(opacityParam, 10) : NaN;
        if (Number.isFinite(opacity)) {
            const clampedOpacity = Math.max(0, Math.min(100, opacity));
            state.radarOpacity = clampedOpacity / 100;
            dom.opacitySlider.value = String(clampedOpacity);
            dom.opacityValue.textContent = `${clampedOpacity}%`;
        }

        const wantMosaic = params.get("mosaic") === "1";
        const startInLiveMode = wantMosaic
            ? liveParam === "1"
            : (liveParam == null ? true : liveParam === "1");
        setLiveModeEnabled(startInLiveMode, { refreshNow: false });
        state.showReferenceOverlay = overlayParam === "1";
        setReferenceOverlayVisible(state.showReferenceOverlay);
        state.showStormTracks = tracksParam === "1";
        setStormTracksVisible(state.showStormTracks);
        state.showLightning = glmParam === "1";
        setLightningVisible(state.showLightning);
        syncSatelliteControls();
        if (satOnlyActive()) {
            updateLegend();
            setColorScaleVisible(true);
        }
        state.showAlerts = wwParam === "1";
        setAlertsVisible(state.showAlerts);

        state.initialUrlAutoplay = animParam === "1";

        state.pendingInitialStationId = stationId ? String(stationId).toUpperCase() : null;
        if (wantMosaic) {
            setMosaicMode(true, { fit: !state.initialUrlMap, animateFit: false });
        }
        trySelectInitialStation();
    }

    // ── Share ─────────────────────────────────────────────────

    function shareView() {
        updateURL();
        copyToClipboard(window.location.href, "Link copied to clipboard!");
    }

    function satShareState() {
        return {
            sat: state.showSatellite ? state.satProduct || "geo" : null,
            satMode: state.satMode,
            satSector: state.satSector,
            satGoes: state.satGoes,
            satHold: state.satHold,
        };
    }

    function shareShortView() {
        const token = buildShortToken();
        const params = new URLSearchParams();
        params.set("s", token);
        writeSatParams(params, satShareState());
        const shortUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
        copyToClipboard(shortUrl, "Short link copied to clipboard!");
    }

    function paletteBytesForProduct(product) {
        const colors = getPaletteForProduct(product);
        const palette = new Uint8Array(RADAR_PALETTE_SIZE * 4);
        for (let i = 0; i < RADAR_PALETTE_SIZE; i++) {
            const rgba = parseColorToRgba(colors[i] || null);
            const off = i * 4;
            palette[off] = rgba[0];
            palette[off + 1] = rgba[1];
            palette[off + 2] = rgba[2];
            palette[off + 3] = rgba[3];
        }
        return palette;
    }

    function collectLoadedExportFrames() {
        const source = state.pendingPolarFrames || state.polarFrames;
        const frames = [];
        const total = getTotalFrameCount(source);
        for (let i = 0; i < total; i++) {
            const frame = getFrameAt(i, source);
            if (!frame?.loaded || !frame.polarData) continue;
            frames.push({
                polar: frame.polarData,
                decoded: frame.decoded || decodePolarData(frame.polarData),
            });
        }
        return frames;
    }

    async function framesForKmzExport() {
        let frames = collectLoadedExportFrames();
        if (frames.length > 1) return frames;
        if (state.preloading) {
            showShareToast("Waiting for animation frames…");
            while (state.preloading) {
                await new Promise((resolve) => setTimeout(resolve, 200));
            }
            frames = collectLoadedExportFrames();
            if (frames.length) return frames;
        }
        if (state.selectedStation && state.scans.length > 1) {
            showShareToast("Loading animation frames…");
            await preloadFrames({ background: true, startAnim: false });
            frames = collectLoadedExportFrames();
            if (frames.length) return frames;
        }
        const polar = state.lastRadarMeta;
        if (polar?.data) {
            return [{ polar, decoded: decodePolarData(polar) }];
        }
        return [];
    }

    async function exportKmz() {
        if (dom.btnExportKmz) dom.btnExportKmz.disabled = true;
        try {
            const frames = await framesForKmzExport();
            if (!frames.length) {
                showShareToast("Load a radar scan first");
                return;
            }
            const polar = frames[0].polar;
            showShareToast(frames.length > 1
                ? `Exporting ${frames.length} frames…`
                : "Building Google Earth file…");
            let storms = [];
            let flashes = [];
            let alerts = [];
            const last = frames[frames.length - 1].polar;
            if (state.showStormTracks && state.selectedStation && last.date && last.time) {
                const tracks = await fetchStormTracks(state.selectedStation.id, last.date, last.time);
                storms = tracks?.storms || [];
            }
            if (state.showLightning && last.date && last.time) {
                const glm = await fetchLightning(last);
                flashes = glm?.flashes || [];
            }
            if (state.showAlerts && last.date && last.time) {
                const loaded = await fetchAlerts(last);
                alerts = (loaded?.features || []).map((feature) => ({
                    ...feature,
                    label: alertLabel(feature),
                    title: alertTitle(feature),
                    color: alertStyle(feature).stroke,
                }));
            }
            const { blob, filename } = await buildRadarKmz({
                frames,
                paletteRgba: paletteBytesForProduct(polar.product),
                station: state.selectedStation || { id: polar.station, lat: polar.lat, lon: polar.lon },
                storms,
                flashes,
                alerts,
                shareUrl: window.location.href,
                productLabel: formatProductLabel(polar.product),
                onProgress: (info) => showShareToast(info.text),
            });
            triggerDownload(blob, filename);
            showShareToast(frames.length > 1
                ? `Downloaded ${frames.length}-frame KMZ`
                : "Downloaded KMZ for Google Earth");
        } catch (err) {
            console.error("KMZ export failed:", err);
            showShareToast(err.message || "KMZ export failed");
        } finally {
            if (dom.btnExportKmz) dom.btnExportKmz.disabled = false;
        }
    }

    function showShareToast(toastMessage) {
        if (!dom.shareToast) return;
        dom.shareToast.textContent = toastMessage;
        dom.shareToast.classList.add("visible");
        setTimeout(() => dom.shareToast.classList.remove("visible"), 3000);
    }

    function effectiveRadarOpacity() {
        return rainPanel?.active ? 0 : state.radarOpacity;
    }

    // Live total once a run has finished; otherwise the window still being restored.
    function currentRainShare() {
        const live = rainPanel?.shareState() || null;
        if (live) {
            state.pendingRain = null;
            state.initialRain = null;
        }
        return live || state.pendingRain || state.initialRain || null;
    }

    function initRainTotal() {
        rainPanel = initRainPanel({
            L,
            map: dom.map,
            getContext: () => {
                const scan = state.selectedScanIndex >= 0 ? state.scans[state.selectedScanIndex] : null;
                const date = scan?.scanDate || state.date;
                const timeMs = scan && date ? Date.parse(`${date}T${scan.time}Z`) : NaN;
                return { station: state.selectedStation, timeMs: state.liveMode ? Date.now() : timeMs };
            },
            onChange: () => updateURL(),
            onStart: () => {
                stopAnim();
                if (state.preloading) invalidatePreload();
            },
            onEnter: () => {
                radarOverlay?.setOpacity?.(0);
                if (window.innerWidth <= 768 && !dom.sidebar.classList.contains("collapsed")) {
                    dom.sidebar.classList.add("collapsed");
                    setTimeout(invalidateMapSize, 350);
                }
                document.getElementById("mapContainer")?.classList.add("rain-mode");
            },
            onExit: () => {
                state.pendingRain = null;
                state.initialRain = null;
                radarOverlay?.setOpacity?.(state.radarOpacity);
                document.getElementById("mapContainer")?.classList.remove("rain-mode");
            },
        });
    }

    function openAboutModal() {
        dom.aboutModal.hidden = false;
    }

    function closeAboutModal() {
        dom.aboutModal.hidden = true;
    }

    function copyToClipboard(text, toastMessage) {
        const showToast = () => showShareToast(toastMessage);

        const fallbackCopy = () => {
            const input = document.createElement("textarea");
            input.value = text;
            input.setAttribute("readonly", "");
            input.style.position = "fixed";
            input.style.left = "-9999px";
            document.body.appendChild(input);
            input.focus();
            input.select();
            let copied = false;
            try {
                copied = document.execCommand("copy");
            } catch {
                copied = false;
            }
            document.body.removeChild(input);

            if (copied) {
                showToast();
                return;
            }

            // Final fallback for restricted clipboard environments.
            window.prompt("Copy this link:", text);
        };

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text).then(showToast).catch(fallbackCopy);
            return;
        }
        fallbackCopy();
    }

    // ── Helpers ───────────────────────────────────────────────

    function formatDate(dateStr) {
        if (!dateStr) return "";
        const d = dateStr.replace(/-/g, "");
        return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
    }

    function getTodayUTCDateString() {
        return new Date().toISOString().slice(0, 10);
    }

    function parseScanUtcDate(dateStr, timeStr) {
        if (!dateStr || !timeStr) return null;
        const d = String(dateStr).replace(/-/g, "");
        const t = String(timeStr).replace(/:/g, "");
        if (d.length !== 8 || t.length < 4) return null;
        const hh = t.slice(0, 2);
        const mm = t.slice(2, 4);
        const ss = t.length >= 6 ? t.slice(4, 6) : "00";
        const iso = `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}T${hh}:${mm}:${ss}Z`;
        const dt = new Date(iso);
        return Number.isNaN(dt.getTime()) ? null : dt;
    }

    function formatLiveAge(meta) {
        const scanDt = parseScanUtcDate(meta.date, meta.time);
        if (!scanDt) return "N/A";
        const ageSec = Math.max(0, Math.floor((Date.now() - scanDt.getTime()) / 1000));
        if (ageSec < 60) return `${ageSec}s ago`;
        if (ageSec < 3600) {
            const m = Math.floor(ageSec / 60);
            const s = ageSec % 60;
            return `${m}m ${s}s ago`;
        }
        const h = Math.floor(ageSec / 3600);
        const m = Math.floor((ageSec % 3600) / 60);
        const s = ageSec % 60;
        return `${h}h ${m}m ${s}s ago`;
    }

    function getRecentDateCandidatesUTC(maxDays = 2) {
        const now = new Date();
        const days = Math.max(1, Math.min(14, maxDays));
        const dates = [];
        for (let i = 0; i < days; i++) {
            const d = new Date(Date.UTC(
                now.getUTCFullYear(),
                now.getUTCMonth(),
                now.getUTCDate() - i
            ));
            dates.push(d.toISOString().slice(0, 10));
        }
        return dates;
    }

    function getRecentDateCandidatesFrom(baseDateStr, maxDays = 2) {
        const days = Math.max(1, Math.min(14, maxDays));
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(baseDateStr || "");
        if (!m) return getRecentDateCandidatesUTC(maxDays);
        const base = new Date(Date.UTC(
            parseInt(m[1], 10),
            parseInt(m[2], 10) - 1,
            parseInt(m[3], 10)
        ));
        const dates = [];
        for (let i = 0; i < days; i++) {
            const d = new Date(base.getTime() - i * 86400000);
            dates.push(d.toISOString().slice(0, 10));
        }
        return dates;
    }

    function getLatestScanIndex(scans) {
        if (!Array.isArray(scans) || scans.length === 0) return 0;
        let latestIdx = 0;
        let latestTime = "";
        scans.forEach((scan, idx) => {
            const timeKey = (scan && scan.time ? scan.time : "").replace(/:/g, "");
            if (timeKey >= latestTime) {
                latestTime = timeKey;
                latestIdx = idx;
            }
        });
        return latestIdx;
    }

    function getLatestScanIndexAcrossDates(scans) {
        if (!Array.isArray(scans) || scans.length === 0) return 0;
        let latestIdx = 0;
        let latestKey = "";
        scans.forEach((scan, idx) => {
            const dateKey = (scan && scan.scanDate ? scan.scanDate : "").replace(/-/g, "");
            const timeKey = (scan && scan.time ? scan.time : "").replace(/:/g, "");
            const key = `${dateKey}${timeKey}`;
            if (key >= latestKey) {
                latestKey = key;
                latestIdx = idx;
            }
        });
        return latestIdx;
    }

    // ── Boot ──────────────────────────────────────────────────
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init);
    } else {
        init();
    }

